import { z } from 'zod'

// ─── Enums / union types ────────────────────────────────────────────────────
// String-literal unions match the SQL Connect enum values (UPPER_SNAKE_CASE)
// so values round-trip through the generated SDK without mapping.
// See docs/api/trpc-routes.md and docs/data-model/student-state.md.

export type Intent            = 'tutor' | 'assess' | 'recap'   // graph-internal, not a DB enum
export type CurriculumScope   = 'STRICT' | 'ADJACENT' | 'OPEN'
export type MasteryTrend      = 'IMPROVING' | 'STABLE' | 'DECLINING' | 'INSUFFICIENT_DATA'
export type SchoolStaffAction = 'DISMISSED' | 'REDIRECTED' | 'TOOK_OVER'
export type MemoryType        = 'WORKING' | 'EPISODIC' | 'SEMANTIC'
export type ExplanationStyle  = 'VISUAL' | 'EXAMPLE_FIRST' | 'DEFINITION_FIRST' | 'SOCRATIC'
export type LearningStandardStatus = 'PENDING' | 'DRAFT' | 'PUBLISHED'
export type LessonPlanStatus       = 'PLANNED' | 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'
export type TutoringWindowStatus   = 'OPEN' | 'CLOSED'
export type LessonResourceType     = 'PDF' | 'VIDEO' | 'LINK' | 'IMAGE' | 'DOCUMENT' | 'SLIDES' | 'INTERACTIVE'
// Unit Assessment (epic #160). `LessonPlanKind` is the render/behaviour discriminator
// on a LessonPlan (NULL legacy rows ⇒ STANDARD); `AssessmentPhase` tags an
// AssessmentEvent as the pre- or post-half of a Unit Assessment (null ⇒ ordinary event).
export type LessonPlanKind         = 'STANDARD' | 'UNIT_ASSESSMENT_PRE' | 'UNIT_ASSESSMENT_POST'
export type AssessmentPhase        = 'PRE' | 'POST'
// Practice Block DOK + section-aware mastery (epic #176). `DokLevel` is a Depth-of-
// Knowledge tier authored per question (Low recall/procedure → Moderate application →
// High analysis/transfer, reusing the practice-seed taxonomy). `BlockSection` marks
// which half of an AI Practice Block an AssessmentEvent came from: PRACTICE (the
// low-stakes practice problems, recorded but NOT counted toward mastery) vs EXIT_TICKET
// (the graded checks that still move StandardsMastery). Both null on free-tutor/legacy events.
// AI-taught full lessons (epic #318) add CHECK (a check-for-understanding answer) and
// GUIDED (a we-do problem answer) — both recorded but mastery-neutral like PRACTICE.
export type DokLevel               = 'LOW' | 'MODERATE' | 'HIGH'
export type BlockSection           = 'PRACTICE' | 'EXIT_TICKET' | 'CHECK' | 'GUIDED' | 'FREE_TUTOR'
// FREE_TUTOR (epic #440 Phase 5): a free-tutoring answer (no lesson). Whether it moves
// mastery is the class policy's `freeTutoringMastery`: COUNT (fully), RECORD_ONLY
// (recorded, mastery-neutral like PRACTICE) or REDUCED (half weight).
// Make-up lesson assignment lifecycle (epic #318): the teacher assigns, the agent moves
// it to IN_PROGRESS on the first start and COMPLETED when the lesson finishes.
export type LessonAssignmentStatus = 'ASSIGNED' | 'IN_PROGRESS' | 'COMPLETED'

// RBAC permission catalog — a `resource:action` capability string, NOT a SQL enum.
// The source of truth is the `Permission` table (seeded via SeedPermissions in
// dataconnect/seed_data.gql); this union lets the teacher app + agent share literals for
// usePermissions()/enforcement. Keep in sync with the catalog when keys are added.
// functions/ mirrors these structurally (isolated build) where it enforces — same pattern
// as its local UserRole. See docs/architecture/permissions.md.
export type PermissionKey =
  | 'course:manage'
  | 'staff:manage'
  | 'session:view_all'
  | 'curriculum:upload'
  | 'class:manage'
  | 'class:view'
  | 'student:view'
  | 'curriculum:generate_seeds'
  | 'lesson:manage'
  | 'lesson:generate_ai'
  | 'pacing:manage_class'
  | 'class:configure_mastery'   // epic #440: edit a class's mastery scoring (responsiveness, prior weight, free-tutoring credit)
  // Student records (epic #595). The manage keys are class-scoped (the class teacher, own
  // students only); `student:view_demographics` is a view key that ships with its guard.
  | 'student:manage_profile'       // profile + photo + supports
  | 'student:view_demographics'    // audited: every view writes a StudentRecordChange
  | 'student:manage_demographics'
  | 'guardian:manage'
export type ChunkType         =
  | 'CONCEPT_DEFINITION'
  | 'WORKED_EXAMPLE'
  | 'PRACTICE_PROBLEM'
  | 'VISUAL_DESCRIPTION'
  | 'SUMMARY'

export type SessionStatus =
  | 'ACTIVE'
  | 'PAUSED'
  | 'STALLED'
  | 'INTERVENTION_ACTIVE'
  | 'LONG_SESSION'
  | 'RAPID_PROGRESS'
  | 'ENDED'

export type AnomalyType =
  | 'STALLED'
  | 'RAPID_PROGRESSION'
  | 'LOW_MASTERY_DROP'
  | 'SENSITIVE_TOPIC'
  | 'LOW_CONFIDENCE_SCORE'
  | 'LONG_IDLE'
  // Full lesson (epic #318): the student still missed a check for understanding after
  // every re-teach attempt; the lesson moved on. Review-only (never pauses).
  | 'RETEACH_EXHAUSTED'

// ─── Core data types ─────────────────────────────────────────────────────────

// Per-student tutoring settings from the ClassEnrollment row
export interface ClassEnrollmentSettings {
  curriculumScope:           CurriculumScope
  preferredExplanationStyle: ExplanationStyle
  readingLevel:              number          // grade level
  escalationOverrides?:      Partial<EscalationPolicy>
}

// How responsive the mastery score is to recent answers. Sets the floor on the
// weight a fresh answer carries once enough evidence has accumulated (below that
// crossover the score is still a true running average). See apps/agent/src/graph/mastery.ts.
export type MasteryResponsiveness = 'BALANCED' | 'RESPONSIVE' | 'FORGIVING'

/** Select options for the responsiveness profile — shared by the admin class form and the teacher dialog. */
export const MASTERY_RESPONSIVENESS_OPTIONS: ReadonlyArray<{ value: MasteryResponsiveness; label: string }> = [
  { value: 'BALANCED',   label: 'Balanced — steady (recommended)' },
  { value: 'RESPONSIVE', label: 'Responsive — reacts faster to recent answers' },
  { value: 'FORGIVING',  label: 'Forgiving — gains count faster than slips' },
]

export interface EscalationPolicy {
  consecutiveWrongAnswersThreshold: number   // default: 4
  masteryDropThreshold:             number   // default: 0.15
  lowConfidenceScoreThreshold:      number   // default: 0.5
  sessionTimeLimit:                 number   // minutes, default: 45
  idleTimeThreshold:                number   // minutes, default: 10
  rapidProgressThreshold:           number   // default: 0.9
  masteryResponsiveness?:           MasteryResponsiveness   // default: 'BALANCED'
  masteryBadgeThreshold?:           number   // standing mastery that earns the "mastered" badge; default: 0.9
  // Prior weight (epic #440): how many phantom WRONG answers the running average starts
  // from, so the first real answers can't claim the whole score. With k=1 one correct
  // answer reads 50%, two 67%, three 75%. 0 = legacy (first answer counts in full).
  // Integer 0–5. Default: 1.
  masteryPriorWeight?:              number
  // Free-tutoring credit (epic #440 Phase 5, closes #337): how a FREE_TUTOR answer counts.
  // COUNT = like an exit-ticket answer (default); RECORD_ONLY = recorded for the teacher,
  // mastery-neutral; REDUCED = FREE_TUTOR_REDUCED_WEIGHT × the normal weight.
  freeTutoringMastery?:             FreeTutoringMastery
  maxAttemptsPerPracticeItem?:      number   // AI Practice: attempts on one item before the tutor shows the solution and moves on; default: 3
}

export type FreeTutoringMastery = 'COUNT' | 'RECORD_ONLY' | 'REDUCED'

/** Weight (w) a FREE_TUTOR answer carries under REDUCED — half an exit-ticket answer, floor included. */
export const FREE_TUTOR_REDUCED_WEIGHT = 0.5

/** Select options for the free-tutoring credit mode — shared by the admin class form and the teacher dialog. */
export const FREE_TUTORING_MASTERY_OPTIONS: ReadonlyArray<{ value: FreeTutoringMastery; label: string }> = [
  { value: 'COUNT',       label: 'Counts — like an exit-ticket answer (default)' },
  { value: 'REDUCED',     label: 'Reduced — half the weight of an exit-ticket answer' },
  { value: 'RECORD_ONLY', label: 'Record only — visible to you, does not move mastery' },
]

/**
 * The agent's defaults for every EscalationPolicy field. A class's stored policy and a
 * student's `escalationOverrides` are spread over this (`{...DEFAULT, ...class, ...student}`),
 * so a field missing from the stored JSON always means "the default". The ONE copy — the
 * agent, the admin class form, the teacher dialog and the eval fixtures all import it.
 */
export const DEFAULT_ESCALATION_POLICY: Readonly<Required<EscalationPolicy>> = {
  consecutiveWrongAnswersThreshold: 4,
  masteryDropThreshold:             0.15,
  lowConfidenceScoreThreshold:      0.5,
  sessionTimeLimit:                 45,
  idleTimeThreshold:                10,
  rapidProgressThreshold:           0.9,
  masteryResponsiveness:            'BALANCED',
  masteryBadgeThreshold:            0.9,
  masteryPriorWeight:               1,
  freeTutoringMastery:              'COUNT',
  maxAttemptsPerPracticeItem:       3,
}

/**
 * Counted answers a standard needs before the system will claim anything about standing
 * mastery: the trend label, the "Standard Mastered!" badge, the RAPID_PROGRESSION /
 * LOW_MASTERY_DROP alerts, and (epic #440) the display band — below it every surface
 * shows a neutral "early" state. Lives here so the agent and the apps agree on it.
 */
export const MIN_EVIDENCE_FOR_TREND = 3

// ─── Mastery display bands (epic #440 Phase 3) ────────────────────────────────
// The ONE set of cut-offs every surface uses — student skill map and session rail,
// teacher heatmap / sessions views, My Classes cards, Ask AI tools. Before this each
// surface kept its own (0.8/0.4, 0.8/0.55, 0.7/0.4, 0.8/0.5…).

/** Score cut-offs (0–1): `mastered` at or above 0.8, `developing` at or above 0.55. */
export const MASTERY_BANDS = { mastered: 0.8, developing: 0.55 } as const

/**
 * - `none`       — never assessed (no evidence and a zero score)
 * - `early`      — fewer than MIN_EVIDENCE_FOR_TREND counted answers: the percent is
 *                  shown but no colour band is claimed. With the default prior (k=1) a
 *                  first correct answer is 50%, which must not read "Needs practice"
 *                  in red; and under a legacy k=0 class one answer must not read
 *                  "Mastered". Callers that have no evidence count (per-standard class
 *                  averages) skip this state.
 * - `beginning` / `developing` / `mastered` — by MASTERY_BANDS.
 */
export type MasteryBand = 'none' | 'early' | 'beginning' | 'developing' | 'mastered'

export function isEarlyEvidence(evidenceCount: number | null | undefined): boolean {
  return typeof evidenceCount === 'number' && evidenceCount > 0 && evidenceCount < MIN_EVIDENCE_FOR_TREND
}

/** "1 of 3 checks" — how far a skill is from a settled band. */
export function earlyEvidenceLabel(evidenceCount: number): string {
  const n = Math.max(0, Math.min(MIN_EVIDENCE_FOR_TREND, Math.round(evidenceCount)))
  return `${n} of ${MIN_EVIDENCE_FOR_TREND} checks`
}

export function masteryBand(score: number | null | undefined, evidenceCount?: number | null): MasteryBand {
  if (score == null || Number.isNaN(score)) return 'none'
  if (evidenceCount != null) {
    if (evidenceCount <= 0 && score <= 0) return 'none'
    if (isEarlyEvidence(evidenceCount)) return 'early'
  }
  if (score >= MASTERY_BANDS.mastered) return 'mastered'
  if (score >= MASTERY_BANDS.developing) return 'developing'
  return 'beginning'
}

export interface LearningStandard {
  standardId:       string
  schoolId:         string   // School-scoped catalog owner; courses select via CourseStandard
  standardCode:     string   // e.g. "7.NS.A.1"
  standardName:     string
  description?:     string
  gradeLevel?:      string   // matches Course.gradeLevel (String)
  subject?:         string
  strand?:          string   // catalog grouping, e.g. "Number Sense & Operations"
  status:           LearningStandardStatus   // PENDING → DRAFT → PUBLISHED
  sourceFileId?:    string   // provenance of the upload that produced this row
  prereqStandardIds: string[]
}

// M:N join — which school standards a course has selected.
export interface CourseStandard {
  courseId:   string
  standardId: string
}

// A pacing-guide unit, ingested from the curriculum map (or created manually).
// Re-upload idempotency is keyed on (courseId, title).
export interface CurriculumUnit {
  unitId:              string
  courseId:            string
  academicPeriodId?:   string   // the quarter; absent if the map omits it
  title:               string   // "Unit 1: Rational Number Operations"
  sequence:            number   // order within the course
  coreConcepts:        string[] // the bulleted concepts from the map
  mtrCodes:            string[] // MA.K12.MTR.* — process guidance, not mastery-tracked
  approxDurationWeeks?: number  // ≈4.5
  sourceFileId?:       string   // provenance of the map upload
  createdAt?:          string
}

// M:N join — unit ↔ content benchmark (existing LearningStandard).
export interface CurriculumUnitStandard {
  unitId:     string
  standardId: string
}

// The contract the tutor executes for a lesson's independent-practice (you-do)
// phase. Authored prompt-shaped so session-time prompt assembly is a slot-fill,
// never a parse of rich text. Stored as its own `LessonPlan.tutorBlock` JSON
// column (never inside `body`). Validate with `lessonTutorBlockSchema` — the DB
// column is `Any`, so shape is enforced here, not at the DB layer.
// See GitHub epic #30 (Lesson-driven tutoring).
// A single practice problem or exit-ticket item. Accepts a bare string (legacy
// stored blocks + the AI generator emit strings) OR an object, and normalizes
// both to the object form — so no data migration is needed. `requireReasoning`
// makes the tutor require shown work for THIS item and caps an answer-only
// response below mastery (see assessment-agent scoring).
export const practiceItemSchema = z
  .union([
    z.string().min(1),
    // `showWork` / `showWorkHint`: the teacher's drawing-pad opt-in for this typed item and
    // what the tutor should look for in the work (epic #312). Feedback only — never scored.
    z.object({ text: z.string().min(1), requireReasoning: z.boolean().optional(), dokLevel: z.enum(['LOW', 'MODERATE', 'HIGH']).optional(), standardId: z.string().optional(), showWork: z.boolean().optional(), showWorkHint: z.string().max(300).optional() }),
    // A reference to a LessonResource (video / interactive widget) sequenced among
    // the block's items (epic #87). The referenced resource's type decides whether
    // the agent emits a `media` or `interactive` SSE event when the cursor reaches it.
    z.object({ resourceId: z.string().min(1) }),
  ])
  .transform((v) => (typeof v === 'string' ? { text: v } : v))

export type PracticeItem = z.infer<typeof practiceItemSchema>

/**
 * A block item that is authored text (a practice problem or exit-ticket question).
 * `standardId` is the one learning standard this item targets (epic per-item standard
 * tagging). Optional — legacy/unauthored items lack it and render as "—"; for exit-ticket
 * items it deterministically routes the mastery event to that standard (Phase B).
 */
export type TextPracticeItem     = { text: string; requireReasoning?: boolean; dokLevel?: DokLevel; standardId?: string; showWork?: boolean; showWorkHint?: string }
/** A block item that references a LessonResource (video / interactive widget). */
export type ResourcePracticeItem = { resourceId: string }

export function isTextPracticeItem(item: PracticeItem | LessonPoint): item is TextPracticeItem {
  return 'text' in item
}
export function isResourcePracticeItem(item: PracticeItem | LessonPoint): item is ResourcePracticeItem {
  return 'resourceId' in item
}

export const lessonTutorBlockSchema = z.object({
  // tight, measurable — narrower than the lesson objective
  objective:       z.string().min(1),
  // subset of LessonPlanStandard this block practices/assesses
  standardIds:     z.array(z.string()).min(1),
  // how the AI opens, given the teacher just taught it
  openingMove:     z.string().min(1),
  practice: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('PROBLEM_SET'), problems: z.array(practiceItemSchema).min(1) }),
    // requireReasoning applies to the single concept task
    z.object({ kind: z.literal('CONCEPT'),     prompt:   z.string().min(1), requireReasoning: z.boolean().optional(), dokLevel: z.enum(['LOW', 'MODERATE', 'HIGH']).optional(), standardId: z.string().optional() }),
  ]),
  // what "got it" looks like — drives advancement + exit
  successCriteria: z.string().min(1),
  // optional formative exit-ticket questions
  checkItems:      z.array(practiceItemSchema).optional(),
  // optional override of the per-student enrollment explanation style
  scaffolding:     z.enum(['SOCRATIC', 'GUIDED', 'LIGHT']).optional(),
  // bounds the block; helps pacing + auto-close
  estimatedMinutes: z.number().int().positive(),
  // Administration behaviour, orthogonal to item shape (epic #160). undefined ⇒
  // 'PRACTICE' (all legacy blocks): the tutor helps, re-poses on wrong, hints.
  // 'ASSESSMENT' flips the agent into no-help administration — single attempt per
  // item, neutral ack, advance regardless of correctness; events are snapshot-only.
  mode:            z.enum(['PRACTICE', 'ASSESSMENT']).optional(),
})

export type LessonTutorBlock = z.infer<typeof lessonTutorBlockSchema>

// ─── AI-taught full lesson (epic #318) ───────────────────────────────────────
// A lesson script is the contract the tutor walks to TEACH a whole lesson (hook → I-do →
// check → we-do → you-do → exit ticket → recap), generalising the you-do
// `LessonTutorBlock`. A fixed cursor walks it — the LLM never decides when to move on
// (same rule as the block cursor). Stored as its own `LessonPlan.lessonScript` JSON
// column (the DB column is `Any`); read it with `parseLessonScript`. This section is the
// original v1 shape (ordered `segments`), kept for reading pre-#363 rows and for
// `migrateLessonScriptV1toV2`; the current shape is v2 (`lessonScriptSchema`, below).
//
// - `practice` / `exit_ticket` carry no content: they run the lesson's existing
//   `tutorBlock` (its practice problems, then its checkItems), so a scripted lesson and
//   a teacher-led practice block share one authored you-do.
// - A failed `check` re-teaches via `reteachSegmentId` → an `instruct` segment marked
//   `reteachOnly` (skipped on the main path), ideally in a DIFFERENT representation, then
//   re-checks. Bounded by `maxReteach`; past it the lesson walks through the answer and
//   moves on (review-only RETEACH_EXHAUSTED anomaly for the teacher).
// - Only exit-ticket answers move mastery; check (CHECK) and guided (GUIDED) answers
//   are recorded but mastery-neutral.

export const LESSON_SEGMENT_TYPES = ['hook', 'instruct', 'check', 'guided', 'practice', 'exit_ticket', 'recap'] as const
export type LessonSegmentType = typeof LESSON_SEGMENT_TYPES[number]

// ─── Math visuals from a structured spec (epic #318 Phase 3) ──────────────────
// The model never draws: it returns a small JSON spec, checked against this schema, and
// the apps render it as SVG (packages/ui MathVisual) — exact, cheap, and identical on
// Qwen and Anthropic (no tool calling). Strict + versioned + bounds-checked so a bad
// spec is rejected rather than drawn wrong. `describeVisual` gives the alt text.

const num = z.number().finite()
const label = z.string().trim().min(1).max(24)
const pt = z.object({ x: num.min(-100).max(100), y: num.min(-100).max(100) })

const numberLineSchema = z.object({
  type:      z.literal('number_line'),
  version:   z.literal(1),
  min:       num.min(-1000).max(1000),
  max:       num.min(-1000).max(1000),
  step:      num.positive(),
  points:    z.array(z.object({ value: num, label: label.optional(), open: z.boolean().optional() })).max(10).default([]),
  jumps:     z.array(z.object({ from: num, to: num, label: label.optional() })).max(8).default([]),
  intervals: z.array(z.object({ from: num, to: num, includeFrom: z.boolean().default(true), includeTo: z.boolean().default(true) })).max(3).default([]),
  caption:   z.string().max(140).optional(),
}).superRefine((v, ctx) => {
  if (v.max <= v.min) ctx.addIssue({ code: 'custom', message: 'number line: max must be greater than min' })
  if ((v.max - v.min) / v.step > 40) ctx.addIssue({ code: 'custom', message: 'number line: at most 40 ticks' })
  const inRange = (x: number) => x >= v.min && x <= v.max
  if (!v.points.every((p) => inRange(p.value))) ctx.addIssue({ code: 'custom', message: 'number line: a point is outside min..max' })
  if (!v.jumps.every((j) => inRange(j.from) && inRange(j.to))) ctx.addIssue({ code: 'custom', message: 'number line: a jump is outside min..max' })
  if (!v.intervals.every((i) => inRange(i.from) && inRange(i.to) && i.from < i.to)) ctx.addIssue({ code: 'custom', message: 'number line: a bad interval' })
})

const curveSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('linear'), m: num.min(-50).max(50), b: num.min(-100).max(100), label: label.optional() }),
  z.object({ kind: z.literal('quadratic'), a: num.min(-20).max(20), b: num.min(-50).max(50), c: num.min(-100).max(100), label: label.optional() }),
])

const coordinatePlaneSchema = z.object({
  type:     z.literal('coordinate_plane'),
  version:  z.literal(1),
  xMin: num.min(-100).max(100), xMax: num.min(-100).max(100),
  yMin: num.min(-100).max(100), yMax: num.min(-100).max(100),
  gridStep: num.positive().default(1),
  points:   z.array(pt.extend({ label: label.optional() })).max(12).default([]),
  segments: z.array(z.object({ from: pt, to: pt, label: label.optional() })).max(8).default([]),
  curves:   z.array(curveSchema).max(3).default([]),
  // Shade the region above / below one linear boundary (an inequality).
  shading:  z.array(z.object({ m: num.min(-50).max(50), b: num.min(-100).max(100), side: z.enum(['above', 'below']), dashed: z.boolean().optional() })).max(2).default([]),
  polygons: z.array(z.object({ points: z.array(pt).min(3).max(8), label: label.optional() })).max(3).default([]),
  caption:  z.string().max(140).optional(),
}).superRefine((v, ctx) => {
  if (v.xMax <= v.xMin || v.yMax <= v.yMin) ctx.addIssue({ code: 'custom', message: 'coordinate plane: max must exceed min' })
  if ((v.xMax - v.xMin) / v.gridStep > 40 || (v.yMax - v.yMin) / v.gridStep > 40) ctx.addIssue({ code: 'custom', message: 'coordinate plane: at most 40 grid lines per axis' })
  const inside = (p: { x: number; y: number }) => p.x >= v.xMin && p.x <= v.xMax && p.y >= v.yMin && p.y <= v.yMax
  if (!v.points.every(inside)) ctx.addIssue({ code: 'custom', message: 'coordinate plane: a point is outside the window' })
  if (!v.segments.every((s) => inside(s.from) && inside(s.to))) ctx.addIssue({ code: 'custom', message: 'coordinate plane: a segment is outside the window' })
  if (!v.polygons.every((p) => p.points.every(inside))) ctx.addIssue({ code: 'custom', message: 'coordinate plane: a polygon is outside the window' })
})

const tapeDiagramSchema = z.object({
  type:    z.literal('tape_diagram'),
  version: z.literal(1),
  tapes:   z.array(z.object({
    label: label.optional(),
    parts: z.array(z.object({ size: num.positive().max(1000), label: label.optional(), shaded: z.boolean().optional() })).min(1).max(12),
  })).min(1).max(4),
  total:   z.object({ label }).optional(),
  caption: z.string().max(140).optional(),
})

const fractionBarsSchema = z.object({
  type:    z.literal('fraction_bars'),
  version: z.literal(1),
  // A bar split into `parts` equal pieces, `shaded` of them filled; or, with `rows`, an
  // area model (a rows × parts grid with `shaded` cells filled).
  bars:    z.array(z.object({
    parts:  z.number().int().min(1).max(24),
    shaded: z.number().int().min(0).max(576),
    rows:   z.number().int().min(1).max(24).optional(),
    label:  label.optional(),
  })).min(1).max(4),
  caption: z.string().max(140).optional(),
}).superRefine((v, ctx) => {
  if (!v.bars.every((b) => b.shaded <= b.parts * (b.rows ?? 1))) ctx.addIssue({ code: 'custom', message: 'fraction bars: shaded exceeds the pieces' })
})

const geometrySchema = z.object({
  type:     z.literal('geometry'),
  version:  z.literal(1),
  polygons: z.array(z.object({
    vertices:   z.array(pt.extend({ label: label.optional() })).min(3).max(8),
    sideLabels: z.array(z.string().max(16)).max(8).optional(),   // side i joins vertex i → i+1
  })).min(1).max(3),
  angles:   z.array(z.object({ vertex: pt, from: pt, to: pt, label: label.optional(), right: z.boolean().optional() })).max(6).default([]),
  caption:  z.string().max(140).optional(),
})

export const visualSpecSchema = z.discriminatedUnion('type', [
  numberLineSchema, coordinatePlaneSchema, tapeDiagramSchema, fractionBarsSchema, geometrySchema,
])

export type NumberLineSpec      = z.infer<typeof numberLineSchema>
export type CoordinatePlaneSpec = z.infer<typeof coordinatePlaneSchema>
export type TapeDiagramSpec     = z.infer<typeof tapeDiagramSchema>
export type FractionBarsSpec    = z.infer<typeof fractionBarsSchema>
export type GeometrySpec        = z.infer<typeof geometrySchema>
export type VisualSpec = NumberLineSpec | CoordinatePlaneSpec | TapeDiagramSpec | FractionBarsSpec | GeometrySpec
export const VISUAL_TYPES = ['number_line', 'coordinate_plane', 'tape_diagram', 'fraction_bars', 'geometry'] as const

const fmt = (n: number) => (Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100)).replace('-', 'negative ')

/** Screen-reader alt text for a visual, generated from the spec (never from the model). */
export function describeVisual(v: VisualSpec): string {
  const cap = v.caption ? ` ${v.caption}` : ''
  switch (v.type) {
    case 'number_line': {
      const pts = v.points.map((p) => `${p.label ? `${p.label} at ` : ''}${fmt(p.value)}${p.open ? ' (open)' : ''}`)
      const jumps = v.jumps.map((j) => `a jump from ${fmt(j.from)} to ${fmt(j.to)}${j.label ? ` labeled ${j.label}` : ''}`)
      const ints = v.intervals.map((i) => `shaded from ${fmt(i.from)} to ${fmt(i.to)}`)
      return `Number line from ${fmt(v.min)} to ${fmt(v.max)}${pts.length ? `, points: ${pts.join(', ')}` : ''}${jumps.length ? `; ${jumps.join('; ')}` : ''}${ints.length ? `; ${ints.join('; ')}` : ''}.${cap}`
    }
    case 'coordinate_plane': {
      const parts = [
        ...v.points.map((p) => `point ${p.label ? `${p.label} ` : ''}at (${fmt(p.x)}, ${fmt(p.y)})`),
        ...v.curves.map((c) => (c.kind === 'linear' ? `the line y = ${fmt(c.m)}x + ${fmt(c.b)}` : `the parabola y = ${fmt(c.a)}x² + ${fmt(c.b)}x + ${fmt(c.c)}`)),
        ...v.segments.map((s) => `a segment from (${fmt(s.from.x)}, ${fmt(s.from.y)}) to (${fmt(s.to.x)}, ${fmt(s.to.y)})`),
        ...v.shading.map((s) => `the region ${s.side} y = ${fmt(s.m)}x + ${fmt(s.b)} shaded`),
        ...v.polygons.map((p) => `a ${p.points.length}-sided shape${p.label ? ` ${p.label}` : ''}`),
      ]
      return `Coordinate plane, x from ${fmt(v.xMin)} to ${fmt(v.xMax)}, y from ${fmt(v.yMin)} to ${fmt(v.yMax)}${parts.length ? `, showing ${parts.join(', ')}` : ''}.${cap}`
    }
    case 'tape_diagram':
      return `Tape diagram: ${v.tapes.map((t) => `${t.label ? `${t.label}: ` : ''}${t.parts.length} part${t.parts.length > 1 ? 's' : ''}${t.parts.some((p) => p.label) ? ` (${t.parts.map((p) => p.label ?? '?').join(', ')})` : ''}`).join('; ')}${v.total ? `; total ${v.total.label}` : ''}.${cap}`
    case 'fraction_bars':
      return `Fraction model: ${v.bars.map((b) => `${b.label ? `${b.label}: ` : ''}${b.shaded} of ${b.parts * (b.rows ?? 1)} ${b.rows ? 'cells' : 'parts'} shaded`).join('; ')}.${cap}`
    case 'geometry':
      return `Geometry: ${v.polygons.map((p) => `a ${p.vertices.length}-sided shape${p.vertices.some((x) => x.label) ? ` ${p.vertices.map((x) => x.label ?? '').join('')}` : ''}${p.sideLabels?.length ? ` with sides ${p.sideLabels.filter(Boolean).join(', ')}` : ''}`).join('; ')}${v.angles.length ? `; angles ${v.angles.map((a) => a.label ?? (a.right ? 'right angle' : 'marked')).join(', ')}` : ''}.${cap}`
  }
}


// Interactive math manipulatives (epic #318 Phase 5) — used on a we-do (guided) problem:
// the student builds the answer on the tool and submits; it is graded deterministically
// against `target`, which never leaves the server (toClientManipulative strips it).
const MAX_NUMBER_LINE_TICKS = 40
export const MANIPULATIVE_TYPES = ['number_line', 'fraction_builder', 'algebra_tiles'] as const
export type ManipulativeType = (typeof MANIPULATIVE_TYPES)[number]

const tileCount = z.number().int().min(-9).max(9)
export const manipulativeSpecSchema = z.discriminatedUnion('type', [
  // Drag a point to a value on a number line.
  z.object({
    type:   z.literal('number_line'),
    min:    z.number(),
    max:    z.number(),
    step:   z.number().positive(),
    target: z.number(),
  }).superRefine((v, ctx) => {
    if (v.max <= v.min) ctx.addIssue({ code: 'custom', path: ['max'], message: 'max must be greater than min' })
    else if ((v.max - v.min) / v.step > MAX_NUMBER_LINE_TICKS) ctx.addIssue({ code: 'custom', path: ['step'], message: `at most ${MAX_NUMBER_LINE_TICKS} steps` })
    if (v.target < v.min || v.target > v.max) ctx.addIssue({ code: 'custom', path: ['target'], message: 'target must be on the line' })
    else if (Math.abs(Math.round((v.target - v.min) / v.step) * v.step + v.min - v.target) > 1e-9) ctx.addIssue({ code: 'custom', path: ['target'], message: 'target must land on a step' })
  }),
  // Split a bar into equal parts and shade some — builds a fraction.
  z.object({
    type:       z.literal('fraction_builder'),
    target:     z.object({ numerator: z.number().int().min(0).max(12), denominator: z.number().int().min(1).max(12) }),
    // Accept an equivalent fraction (2/4 for 1/2). Default true.
    equivalent: z.boolean().optional(),
  }).superRefine((v, ctx) => {
    if (v.target.numerator > v.target.denominator) ctx.addIssue({ code: 'custom', path: ['target'], message: 'one bar: numerator ≤ denominator' })
  }),
  // Model a polynomial ax² + bx + c with positive / negative tiles (zero pairs allowed).
  z.object({
    type:   z.literal('algebra_tiles'),
    target: z.object({ x2: tileCount, x: tileCount, unit: tileCount }),
  }).superRefine((v, ctx) => {
    if (!v.target.x2 && !v.target.x && !v.target.unit) ctx.addIssue({ code: 'custom', path: ['target'], message: 'empty expression' })
  }),
])
export type ManipulativeSpec = z.infer<typeof manipulativeSpecSchema>

/** What the student's browser gets — never the target. */
export type ClientManipulative =
  | { type: 'number_line'; min: number; max: number; step: number }
  | { type: 'fraction_builder' }
  | { type: 'algebra_tiles'; useX2: boolean }

export function toClientManipulative(spec: ManipulativeSpec): ClientManipulative {
  switch (spec.type) {
    case 'number_line':      return { type: 'number_line', min: spec.min, max: spec.max, step: spec.step }
    case 'fraction_builder': return { type: 'fraction_builder' }
    case 'algebra_tiles':    return { type: 'algebra_tiles', useX2: spec.target.x2 !== 0 }
  }
}

/** A tile pile: [positive, negative] counts per tile kind. */
export type TilePile = [number, number]
export type ManipulativeState =
  | { type: 'number_line'; value: number | null }
  | { type: 'fraction_builder'; numerator: number; denominator: number }
  | { type: 'algebra_tiles'; x2: TilePile; x: TilePile; unit: TilePile }

export const manipulativeStateSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('number_line'), value: z.number().nullable() }),
  z.object({ type: z.literal('fraction_builder'), numerator: z.number().int().min(0).max(12), denominator: z.number().int().min(1).max(12) }),
  z.object({
    type: z.literal('algebra_tiles'),
    x2:   z.tuple([z.number().int().min(0).max(20), z.number().int().min(0).max(20)]),
    x:    z.tuple([z.number().int().min(0).max(20), z.number().int().min(0).max(20)]),
    unit: z.tuple([z.number().int().min(0).max(20), z.number().int().min(0).max(20)]),
  }),
])

export const MANIPULATIVE_LABELS: Record<ManipulativeType, string> = {
  number_line:      'number line',
  fraction_builder: 'fraction builder',
  algebra_tiles:    'algebra tiles',
}

function fmtNum(n: number): string {
  return Number.isInteger(n) ? String(n) : String(+n.toFixed(4))
}

/** "2x² − 3x + 1" from net tile counts. */
export function polynomialText(x2: number, x: number, unit: number): string {
  const terms: string[] = []
  const push = (coef: number, sym: string) => {
    if (!coef) return
    const abs = Math.abs(coef)
    const body = sym ? `${abs === 1 ? '' : abs}${sym}` : String(abs)
    terms.push(terms.length === 0 ? (coef < 0 ? `−${body}` : body) : `${coef < 0 ? '−' : '+'} ${body}`)
  }
  push(x2, 'x²')
  push(x, 'x')
  push(unit, '')
  return terms.length ? terms.join(' ') : '0'
}

/** What the student built, in words (transcript line + the tutor's grading note). */
export function describeManipulativeState(state: ManipulativeState | null): string {
  if (!state) return 'skipped it'
  switch (state.type) {
    case 'number_line':
      return state.value === null ? 'did not place a point' : `placed the point at ${fmtNum(state.value)}`
    case 'fraction_builder':
      return `shaded ${state.numerator} of ${state.denominator} equal parts (${state.numerator}/${state.denominator})`
    case 'algebra_tiles': {
      const net = (p: TilePile) => p[0] - p[1]
      return `built ${polynomialText(net(state.x2), net(state.x), net(state.unit))} with tiles`
    }
  }
}

/** What the student can do with the tool, in words (the tutor's directive). */
export function describeManipulativeTool(spec: ClientManipulative): string {
  switch (spec.type) {
    case 'number_line':
      return `a number line from ${fmtNum(spec.min)} to ${fmtNum(spec.max)} (steps of ${fmtNum(spec.step)}) where the student places ONE point — their final answer. It does not show jumps or intermediate steps`
    case 'fraction_builder':
      return 'a fraction bar the student splits into equal parts (up to 12) and shades — the shaded part is their final answer'
    case 'algebra_tiles':
      return `algebra tiles (${spec.useX2 ? 'x², ' : ''}x and 1 tiles, positive and negative; a positive and a negative tile of the same kind cancel) the student uses to build their final expression`
  }
}

/** The answer the manipulative expects, in words (teacher view / walkthrough). */
export function describeManipulativeTarget(spec: ManipulativeSpec): string {
  switch (spec.type) {
    case 'number_line':      return `the point at ${fmtNum(spec.target)}`
    case 'fraction_builder': return `${spec.target.numerator}/${spec.target.denominator}${spec.equivalent === false ? ' exactly' : ' (or an equivalent fraction)'}`
    case 'algebra_tiles':    return polynomialText(spec.target.x2, spec.target.x, spec.target.unit)
  }
}

/** The tool set to its answer — the teacher's review shows it this way. */
export function manipulativeTargetState(spec: ManipulativeSpec): ManipulativeState {
  switch (spec.type) {
    case 'number_line':      return { type: 'number_line', value: spec.target }
    case 'fraction_builder': return { type: 'fraction_builder', ...spec.target }
    case 'algebra_tiles': {
      const pile = (n: number): TilePile => (n >= 0 ? [n, 0] : [0, -n])
      return { type: 'algebra_tiles', x2: pile(spec.target.x2), x: pile(spec.target.x), unit: pile(spec.target.unit) }
    }
  }
}

/** Deterministic grading. A null state (skipped) or a different tool type is wrong. */
export function gradeManipulative(spec: ManipulativeSpec, state: ManipulativeState | null): boolean {
  if (!state || state.type !== spec.type) return false
  switch (spec.type) {
    case 'number_line': {
      const s = state as Extract<ManipulativeState, { type: 'number_line' }>
      return s.value !== null && Math.abs(s.value - spec.target) < spec.step / 2
    }
    case 'fraction_builder': {
      const s = state as Extract<ManipulativeState, { type: 'fraction_builder' }>
      if (spec.equivalent === false) return s.numerator === spec.target.numerator && s.denominator === spec.target.denominator
      return s.numerator * spec.target.denominator === spec.target.numerator * s.denominator
    }
    case 'algebra_tiles': {
      const s = state as Extract<ManipulativeState, { type: 'algebra_tiles' }>
      const net = (p: TilePile) => p[0] - p[1]
      return net(s.x2) === spec.target.x2 && net(s.x) === spec.target.x && net(s.unit) === spec.target.unit
    }
  }
}

// A posed question / we-do problem: the text forms of practiceItemSchema only. A check is
// answered in text; a we-do problem may instead be solved on a `manipulative` (Phase 5).
// `answer` is an optional reference answer for a question with one short exact answer;
// AI-authored answers are re-solved 3× and majority-voted (epic #283's rule) —
// `answerStatus` records the outcome ('needs_review' = the solves disagreed, so a
// teacher should check it). The grader treats a verified answer as the key.
export const lessonQuestionSchema = z
  .union([
    z.string().min(1),
    z.object({
      text: z.string().min(1),
      requireReasoning: z.boolean().optional(),
      dokLevel: z.enum(['LOW', 'MODERATE', 'HIGH']).optional(),
      standardId: z.string().optional(),
      answer: z.string().min(1).optional(),
      answerStatus: z.enum(['verified', 'rekeyed', 'needs_review', 'teacher']).optional(),
      // A diagram shown with the question (Phase 3), e.g. "which point is at -3?".
      visual: visualSpecSchema.optional(),
      // A we-do problem the student solves on an interactive tool (Phase 5).
      manipulative: manipulativeSpecSchema.optional(),
    }),
  ])
  .transform((v) => (typeof v === 'string' ? { text: v } : v))

// A step-by-step worked example — the "virtual whiteboard" (epic #318 Phase 2). The
// student app reveals the steps one at a time (Next step); a step marked `studentTries`
// is the student's turn: they type it, and it's graded as a mastery-neutral we-do answer
// before the example continues. `answer` is the final result, majority-verified when
// AI-written (same `answerStatus` values as lesson questions).
export const workedExampleStepSchema = z.object({
  math:         z.string().min(1),          // the step's expression / equation, LaTeX in $…$
  explanation:  z.string().min(1),          // one short sentence: what this step does
  studentTries: z.boolean().optional(),
})
export const workedExampleSchema = z.object({
  problem:      z.string().min(1),
  steps:        z.array(workedExampleStepSchema).min(2).max(8),
  answer:       z.string().min(1).optional(),
  answerStatus: z.enum(['verified', 'rekeyed', 'needs_review', 'teacher']).optional(),
})
export type WorkedExample = z.infer<typeof workedExampleSchema>
export type WorkedExampleStep = z.infer<typeof workedExampleStepSchema>

// An AI-generated illustration (epic #318 Phase 6) for a non-math teaching point or the
// warm-up: made at authoring time, stored in Storage, and shown to students ONLY once a
// teacher approves it (`status: 'approved'`). Precise math uses spec visuals instead.
export const lessonImageSchema = z.object({
  url:         z.string().min(1),
  storagePath: z.string().min(1),
  // What the picture shows — the tutor is told this, and it is the image's alt text.
  alt:         z.string().min(1).max(400),
  // The prompt it was generated from (for the teacher; regenerate reuses the point text).
  prompt:      z.string().max(2000).optional(),
  status:      z.enum(['pending', 'approved']),
  model:       z.string().optional(),
})
export type LessonImage = z.infer<typeof lessonImageSchema>

/** Lesson images per lesson — generation stops at this cap (cost guard). */
export const MAX_LESSON_IMAGES = 12

// An I-do teaching point: text, a resource (video), or a worked example.
// A teaching point with a diagram (Phase 3) is listed FIRST: zod objects strip unknown
// keys, so the plain text variant would otherwise match and silently drop `visual`.
export const lessonPointSchema = z.union([
  // A diagram (Phase 3) and/or an illustration (Phase 6). Refined so a plain text point
  // falls through to the practice-item form.
  z.object({ text: z.string().min(1), visual: visualSpecSchema.optional(), image: lessonImageSchema.optional() })
    .refine((p) => !!p.visual || !!p.image, 'a diagram or an illustration'),
  practiceItemSchema,
  z.object({ workedExample: workedExampleSchema }),
])
export type LessonPoint = z.infer<typeof lessonPointSchema>
export function isImagePoint(p: LessonPoint | undefined): p is { text: string; visual?: VisualSpec; image: LessonImage } {
  return !!p && typeof p === 'object' && 'image' in p && !!(p as { image?: unknown }).image
}
export function isVisualPoint(p: LessonPoint | undefined): p is { text: string; visual: VisualSpec } {
  return !!p && typeof p === 'object' && 'visual' in p && !!(p as { visual?: unknown }).visual
}
export function isWorkedExamplePoint(p: LessonPoint | undefined): p is { workedExample: WorkedExample } {
  return !!p && typeof p === 'object' && 'workedExample' in p
}

// Pre-recorded narration of a segment's authored text (epic #318 Phase 4). Keyed by the
// text it reads ('hook', 'recap', or 'p<index>' for an I-do point) plus a hash of the
// spoken text, so editing the text makes the clip stale and it is simply not played
// until it is regenerated.
export const narrationClipSchema = z.object({
  key:         z.string().min(1),
  hash:        z.string().min(1),
  url:         z.string().min(1),
  storagePath: z.string().min(1),
  voice:       z.string().optional(),
  chars:       z.number().int().nonnegative().optional(),
})
export type NarrationClip = z.infer<typeof narrationClipSchema>

const segmentBase = {
  // Stable id within the script — `reteachSegmentId` references it.
  id:          z.string().min(1),
  // Short label for the teacher chip / student progress ("Adding opposite signs").
  title:       z.string().min(1).optional(),
  // The concept this segment teaches or checks — grounds RAG + the re-teach.
  concept:     z.string().min(1),
  // Subset of the lesson standards this segment covers (grading scope); empty ⇒ the
  // script's standards.
  standardIds: z.array(z.string()).default([]),
  narration:   z.array(narrationClipSchema).optional(),
}

export const lessonSegmentSchema = z.discriminatedUnion('type', [
  z.object({ ...segmentBase, type: z.literal('hook'), prompt: z.string().min(1), image: lessonImageSchema.optional() }),
  // Teaching points presented one at a time. A resource point (video) is shown, not posed.
  z.object({ ...segmentBase, type: z.literal('instruct'), points: z.array(lessonPointSchema).min(1), reteachOnly: z.boolean().optional() }),
  z.object({
    ...segmentBase,
    type:             z.literal('check'),
    questions:        z.array(lessonQuestionSchema).min(1),
    passCriteria:     z.string().min(1),
    reteachSegmentId: z.string().optional(),
    // Re-teach rounds before walking through the answer and moving on. Default 1.
    maxReteach:       z.number().int().min(0).max(3).optional(),
  }),
  z.object({ ...segmentBase, type: z.literal('guided'), problems: z.array(lessonQuestionSchema).min(1) }),
  z.object({ ...segmentBase, type: z.literal('practice') }),
  z.object({ ...segmentBase, type: z.literal('exit_ticket') }),
  z.object({ ...segmentBase, type: z.literal('recap'), summary: z.string().min(1) }),
])
export type LessonSegment = z.infer<typeof lessonSegmentSchema>

export const lessonScriptV1Schema = z
  .object({
    version:          z.literal(1),
    objective:        z.string().min(1),
    standardIds:      z.array(z.string()).min(1),
    estimatedMinutes: z.number().int().positive().optional(),
    segments:         z.array(lessonSegmentSchema).min(1),
  })
  .superRefine((script, ctx) => {
    const ids = new Set<string>()
    script.segments.forEach((seg, i) => {
      if (ids.has(seg.id)) ctx.addIssue({ code: 'custom', path: ['segments', i, 'id'], message: `duplicate segment id "${seg.id}"` })
      ids.add(seg.id)
    })
    script.segments.forEach((seg, i) => {
      if (seg.type !== 'check' || !seg.reteachSegmentId) return
      const target = script.segments.find((t) => t.id === seg.reteachSegmentId)
      if (!target || target.type !== 'instruct') {
        ctx.addIssue({ code: 'custom', path: ['segments', i, 'reteachSegmentId'], message: `reteachSegmentId "${seg.reteachSegmentId}" must name an instruct segment` })
      }
    })
    for (const type of ['practice', 'exit_ticket', 'recap'] as const) {
      if (script.segments.filter((t) => t.type === type).length > 1) {
        ctx.addIssue({ code: 'custom', path: ['segments'], message: `at most one ${type} segment` })
      }
    }
    // The exit ticket continues the practice block's cursor, so when both are present
    // it must come straight after practice.
    const p = script.segments.findIndex((t) => t.type === 'practice')
    const e = script.segments.findIndex((t) => t.type === 'exit_ticket')
    if (p >= 0 && e >= 0 && e !== p + 1) {
      ctx.addIssue({ code: 'custom', path: ['segments'], message: 'exit_ticket must immediately follow practice' })
    }
    if (script.segments.every((t) => t.type === 'instruct' && t.reteachOnly)) {
      ctx.addIssue({ code: 'custom', path: ['segments'], message: 'the script has no main-path segment' })
    }
  })

export type LessonScriptV1 = z.infer<typeof lessonScriptV1Schema>

export type LessonQuestion = z.infer<typeof lessonQuestionSchema>

// ─── Lesson script v2: a node graph with conditional edges (epic #363) ────────
// Same content per part as v1, but routing lives on explicit `edges` instead of array
// order + `reteachSegmentId`. The runtime picks the next node by evaluating the edges
// leaving the current one (`pickNextNode` in the agent); positions are never stored —
// the teacher canvas auto-lays the graph out. "Re-teach-only" is derived: a node not on
// `mainPath(script)`. v1 rows migrate lazily on read (`parseLessonScript`).

const nodeBase = {
  ...segmentBase,
  // The learning target this node serves ("I can …"), student-facing. Absent ⇒ the
  // headline falls back to `concept`.
  objective:   z.string().min(1).optional(),
  // Groups the nodes serving one target (= the id of the concept's teach node). Absent ⇒
  // ungrouped (hook, practice, exit ticket, recap).
  objectiveId: z.string().min(1).optional(),
}

export const lessonNodeSchema = z.discriminatedUnion('type', [
  z.object({ ...nodeBase, type: z.literal('hook'), prompt: z.string().min(1), image: lessonImageSchema.optional() }),
  z.object({ ...nodeBase, type: z.literal('instruct'), points: z.array(lessonPointSchema).min(1) }),
  z.object({ ...nodeBase, type: z.literal('check'), questions: z.array(lessonQuestionSchema).min(1), passCriteria: z.string().min(1) }),
  z.object({ ...nodeBase, type: z.literal('guided'), problems: z.array(lessonQuestionSchema).min(1) }),
  z.object({ ...nodeBase, type: z.literal('practice') }),
  z.object({ ...nodeBase, type: z.literal('exit_ticket') }),
  z.object({ ...nodeBase, type: z.literal('recap'), summary: z.string().min(1) }),
])
export type LessonNode = z.infer<typeof lessonNodeSchema>

// When an edge is taken. Precedence (see `validateLessonGraph` / the agent's
// `pickNextNode`): node finished → `return` (while re-teaching) → gated edges in array
// order (`score_below` / `mastery_below`, signal below threshold, budget left) → `default`; check
// passed → `pass` → `default`; check failed → first `fail` with budget left →
// `exhausted` → `default`.
export const lessonEdgeConditionSchema = z.discriminatedUnion('kind', [
  // The node finished → go here.
  z.object({ kind: z.literal('default') }),
  // Check only: graded correct (overrides `default`).
  z.object({ kind: z.literal('pass') }),
  // Check only: graded wrong, at most `maxTimes` (default 1) times. `to === from` ⇒ a live
  // re-teach on the spot instead of an authored re-teach node.
  z.object({ kind: z.literal('fail'), maxTimes: z.number().int().min(1).max(3).optional() }),
  // Check only: every fail budget is spent (after the walkthrough).
  z.object({ kind: z.literal('exhausted') }),
  // Re-teach node → back to the check that sent the student.
  z.object({ kind: z.literal('return') }),
  // Gated (epic #363 Phase F) — taken when the node finishes and its signal is below
  // `threshold` (0–1), at most `maxTimes` (default 1) times; may point upstream to re-run
  // part of the lesson. Exit ticket only: this session's exit-ticket score.
  z.object({ kind: z.literal('score_below'), threshold: z.number().min(0).max(1), maxTimes: z.number().int().min(1).max(3).optional() }),
  // Any node: standing mastery of `standardId`, else the lowest over the node's
  // standardIds (else the script's).
  z.object({ kind: z.literal('mastery_below'), threshold: z.number().min(0).max(1), standardId: z.string().min(1).optional(), maxTimes: z.number().int().min(1).max(3).optional() }),
])
export type LessonEdgeCondition = z.infer<typeof lessonEdgeConditionSchema>
export type LessonEdgeKind = LessonEdgeCondition['kind']

export const lessonEdgeSchema = z.object({
  id:        z.string().min(1),
  from:      z.string().min(1),
  to:        z.string().min(1),
  condition: lessonEdgeConditionSchema,
})
export type LessonEdge = z.infer<typeof lessonEdgeSchema>

export const lessonScriptV2Schema = z
  .object({
    version:          z.literal(2),
    objective:        z.string().min(1),
    standardIds:      z.array(z.string()).min(1),
    estimatedMinutes: z.number().int().positive().optional(),
    entryNodeId:      z.string().min(1),
    nodes:            z.array(lessonNodeSchema).min(1),
    edges:            z.array(lessonEdgeSchema),
  })
  .superRefine((script, ctx) => {
    for (const issue of validateLessonGraph(script)) ctx.addIssue({ code: 'custom', path: issue.path, message: issue.message })
  })
export type LessonScriptV2 = z.infer<typeof lessonScriptV2Schema>

// The current lesson script shape (epic #363 Phase E flipped these from v1). Rows are
// read through `parseLessonScript` (a stray v1 row is migrated on read).
export const lessonScriptSchema = lessonScriptV2Schema
export type LessonScript = LessonScriptV2

/** Accepts either version (v2 first — zod strips unknown keys); normalise with `parseLessonScript`. */
export const lessonScriptInputSchema = z.union([lessonScriptV2Schema, lessonScriptV1Schema])
export type LessonScriptInput = z.infer<typeof lessonScriptInputSchema>

// The student's position in a v1 LessonScriptV1 — only read to migrate a checkpoint
// written before epic #363 (see the agent's migrate-state). `segment` indexes
// script.segments; `item` the point/question/problem within it. `returnTo` is set while
// inside a re-teach segment (where to re-check afterwards). `reteachUsed` counts re-teach
// rounds per check segment id. `attempts` tracks misses on the current check/guided
// item. `segmentStartMsg` is messages.length when the segment began (history window).
export interface LessonCursor {
  segment:         number
  item:            number
  returnTo:        { segment: number; item: number } | null
  reteachUsed:     Record<string, number>
  attempts:        { misses: number; turns: number }
  segmentStartMsg: number
}

// The student's position in a LessonScriptV2 (epic #363). `nodeId` names the current
// node; `returnTo` is set while inside a re-teach node (the check to re-ask). `edgeTaken`
// counts how often each budgeted edge (`fail`, later the gated kinds) was taken.
export interface LessonCursorV2 {
  v:               2
  nodeId:          string
  item:            number
  returnTo:        { nodeId: string; item: number } | null
  edgeTaken:       Record<string, number>
  attempts:        { misses: number; turns: number }
  segmentStartMsg: number
}

// Teacher live-view mirror of the student's lesson position (liveSessions doc field).
export interface LessonProgress {
  segmentType:  LessonSegmentType
  segmentIndex: number   // 1-based position on the main path
  segmentCount: number   // main-path segments (re-teach segments excluded)
  title?:       string
  objective?:   string   // the learning target of the current part (falls back to its concept)
  reteaching?:  boolean
  pass?:        number   // 2+ once a gated edge has re-run part of the lesson (absent on the first pass)
}

/** What the tutor is doing in a part (action chip, student progress). Off-path instructs read "Re-teach". */
export const LESSON_SEGMENT_LABELS: Record<LessonSegmentType, string> = {
  hook:        'Hook',
  instruct:    'Teach',
  check:       'Check',
  guided:      'Work together',
  practice:    'Practice',
  exit_ticket: 'Exit ticket',
  recap:       'Wrap-up',
}

/** " · second pass" once a gate has re-run part of the lesson (Phase F); "" on the first pass. */
export function lessonPassLabel(pass: number | undefined): string {
  if (!pass || pass < 2) return ''
  const ordinal = ['second', 'third', 'fourth'][pass - 2]
  return ordinal ? ` · ${ordinal} pass` : ` · pass ${pass}`
}

export interface LessonPlan {
  lessonPlanId:     string
  unitId:           string
  authorUid:        string
  title:            string
  objective?:       string   // measurable learning objective
  gradeLevel?:      string   // snapshot from course, editable
  body?:            string   // rich-text editor JSON; absent while PLANNED stub
  tutorBlock?:      LessonTutorBlock   // AI you-do practice contract; absent when the lesson has no AI phase
  lessonScript?:    LessonScript       // AI-taught full lesson (epic #318); absent until authored
  estimatedMinutes?: number  // 45–120 (1–2 class periods)
  sequence:         number   // order within the unit
  status:           LessonPlanStatus
  kind?:            LessonPlanKind   // render/behaviour discriminator; absent (legacy) ⇒ STANDARD. Epic #160.
  assessmentGroupId?: string         // shared by the Pre/Post pair; absent for STANDARD. Epic #160.
  createdAt?:       string
  updatedAt?:       string
}

// M:N join — per-lesson standards alignment (subset of the unit's standards).
export interface LessonPlanStandard {
  lessonPlanId: string
  standardId:   string
}

export interface LessonResource {
  resourceId:   string
  lessonPlanId: string
  type:         LessonResourceType
  title:        string
  url?:         string   // LINK / VIDEO / external SLIDES
  storagePath?: string   // uploads: lesson-plans/{courseId}/{planId}/{fileId}/{name}
  mimeType?:    string
  sizeBytes?:   number
  sequence?:    number
  // INTERACTIVE only (epic #87): authored widget spec + server-only answer keys +
  // per-question standards. Stored as the DB `content` Any/JSON column; shape
  // enforced by `interactiveResourceContentSchema`, not the DB. Answer keys are
  // stripped before the widget is sent to the student (see the `interactive` SSE event).
  content?:     InteractiveResourceContent
}

export interface StandardsMastery {
  studentId:        string   // Firebase Auth uid — (studentId, standardId) is the record key
  standardId:       string
  standardCode:     string
  standardName:     string
  // Student-facing 2–5 word label (LearningStandard.shortName, #409). Display with
  // standardLabel() — falls back to the code, then the long name.
  standardShortName?: string
  masteryScore:     number   // 0.0–1.0
  confidence:       number
  evidenceCount:    number
  trend:            MasteryTrend
  lastAssessedAt?:  string
  flaggedForReview: boolean
}

/** The label a student sees for a standard: short name → code → long name (#409). */
export function standardLabel(s: { standardShortName?: string | null; standardCode?: string | null; standardName?: string | null }): string {
  return s.standardShortName?.trim() || s.standardCode?.trim() || s.standardName?.trim() || ''
}

/** A course's standards grouped by curriculum unit, in unit order (#409). Standards in
 *  no unit land in a trailing group with `unitTitle: null`. */
export interface StandardGroup {
  unitTitle:   string | null
  sequence:    number
  standardIds: string[]
}

export interface AssessmentEvent {
  eventId:              string
  studentId:            string
  sessionId:            string
  classId:              string
  standardId:           string
  standardCode:         string
  prompt:               string
  studentResponse:      string
  score:                number
  agentConfidence:      number
  schoolStaffOverrideScore?: number
  flaggedForReview:     boolean
  // Set when the score was capped below the pass line ONLY because a show-your-work
  // item got a correct answer with no visible reasoning. It gates block advancement
  // (re-pose to ask for work) but is mastery-neutral: update_progress records it
  // durably yet applies no mastery change, and anomaly_check excludes it from the
  // stall / mastery-drop / progression rules — so a correct-but-terse answer never
  // trips a false escalation. Absent (undefined) on every other event.
  reasoningHoldback?:   boolean
  // Set when a lesson-block answer is correct so far but doesn't finish the item (e.g.
  // one part of a multi-part question). The item stays open and the next attempt is
  // scored against the whole item, so — like reasoningHoldback — update_progress records
  // it but applies no mastery change, and anomaly_check excludes it. Absent otherwise.
  inProgress?:          boolean
  // Unit Assessment (epic #160). Set on events produced during a Pre/Post Unit
  // Assessment session: `assessmentPhase` tags which half, `assessmentGroupId`
  // links the pair. A non-null `assessmentPhase` is the snapshot-only signal —
  // update_progress records the event durably but skips the mastery upsert, so a
  // pre-test on unlearned material never tanks the running average. Absent
  // (undefined) on every ordinary tutoring event.
  assessmentPhase?:     AssessmentPhase
  assessmentGroupId?:   string
  // AI Practice Block section-aware mastery + DOK (epic #176). `blockSection` marks
  // which half of the block produced this event: 'PRACTICE' events are recorded
  // durably but skip the mastery upsert (like assessmentPhase), 'EXIT_TICKET' events
  // still count. `dokLevel` is the authored Depth-of-Knowledge tier of the answered
  // question. `lessonPlanId` is denormalized so the results read is a single indexed
  // scan. All absent (undefined) on free-tutor / legacy events.
  dokLevel?:            DokLevel
  blockSection?:        BlockSection
  lessonPlanId?:        string
  // Which item of its section this answers (epic #500): the 0-based position among the
  // block's practice problems or its exit-ticket checkItems, and — for a quiz resource,
  // which records one event per question — the question's id. Drives the per-problem
  // results. Absent on free-tutor / lesson-script / legacy events.
  blockItemIndex?:      number
  blockQuestionId?:     string
  /** Scratch pad (epic #312): the drawing sent with this answer. Feedback only — never scored. */
  workDrawingId?:       string
  createdAt:            string
}

export interface StudentMemory {
  memoryId:           string
  studentId:          string
  sessionId?:         string
  memoryType:         MemoryType
  content:            string
  relatedStandardIds: string[]
  salienceScore:      number
  createdAt:          string
  lastAccessedAt:     string
}

export interface AnomalyEvent {
  anomalyId:           string
  sessionId:           string
  studentId:           string   // Firebase Auth uid
  type:                AnomalyType
  triggeredAt:         string
  thresholdValue:      number
  observedValue:       number
  contextSnapshot:     Message[]
  /**
   * Raw `messages` indices of the student message(s) that triggered this anomaly, so the
   * teacher's transcript can highlight them. STALLED carries the whole wrong-answer
   * streak; the other types carry the turn's student message. Absent on pre-existing flags.
   */
  messageIndices?:     number[]
  schoolStaffAcknowledged: boolean
  schoolStaffAction?:      SchoolStaffAction
}

export interface ActiveSession {
  sessionId:        string
  studentId:        string      // Firebase Auth uid
  studentName:      string
  threadId:         string
  durationMinutes:  number
  currentTopic:     string
  masteryScore:     number
  status:           SessionStatus
  escalated:        boolean     // anomaly detail comes from AlertsQuery (AnomalyEvent rows)
  interactionCount: number
}

export interface Message {
  role:      'student' | 'agent' | 'school_staff' | 'system'
  content:   string
  timestamp: string
  metadata?: {
    intent?:     Intent
    correct?:    boolean
    standardId?: string
  }
}

export interface Badge {
  badgeId:     string
  name:        string
  description: string
  earnedAt:    string
}

export interface ScopeOverride {
  curriculumScope:            CurriculumScope
  assessmentDifficultyFloor?: number
  prereqAccessEnabled:        boolean
  unlockCondition?: {
    minimumMasteryScore:  number
    minimumEvidenceCount: number
    minimumConfidence:    number
  }
}

export interface DashboardMetrics {
  activeSessionCount:   number
  avgMasteryToday:      number
  masteryDeltaVsLast:   number
  openAlertCount:       number
  assessmentsToday:     number
  pendingReviewCount:   number
}

export interface StudentMasteryRow {
  studentId:      string
  studentName:    string
  masteryScore:   number
  confidence:     number
  evidenceCount:  number
  trend:          MasteryTrend
  lastAssessedAt?: string
  badges:         string[]
}

export interface SessionStats {
  questionsAnswered: number
  correctCount:      number
  durationMinutes:   number
  standardsTouched:  number
}

// ─── SSE stream events — student app ─────────────────────────────────────────

/** A teacher-assigned focus practice's progress (epic #575; shown in the session, epic #639). */
export interface FocusPracticeProgress {
  standardId:    string
  label:         string
  answersDone:   number
  targetAnswers: number
  completed:     boolean
}

export type AgentStreamEvent =
  | { type: 'token';         data: { token: string } }
  | { type: 'turn_end';      data: { intent: Intent; assessmentEvent?: AssessmentEvent } }
  | { type: 'progress';      data: { standardId: string; newScore: number; delta: number } }
  // Free tutoring (#410): the standard the tutor's newest problem targets changed.
  | { type: 'focus';         data: { standardId: string } }
  // Focus practice (epic #639): a counted answer advanced a teacher-assigned skill
  // practice; `completed` on the answer that finished it.
  | { type: 'practice_progress'; data: FocusPracticeProgress }
  // A counted answer that moved mastery by CHIP_MIN_DELTA or less (epic #639): the quiet
  // "✓ Counted" note, never a chip (chip ordinals feed the score explainer).
  | { type: 'counted';       data: { standardId: string; newScore: number; delta: number } }
  | { type: 'badge';         data: { badge: Badge } }
  | { type: 'suggest_break'; data: { turnCount: number } }
  // The agent ended the session: a lesson-driven practice block's exit ticket is done
  // (epic #30, Phase 6), or a free-tutoring session reached its time limit. The
  // student app ends the session; masteryDelta is the change on the block's
  // standards this session (empty for a time-limit close).
  | { type: 'session_complete'; data: { masteryDelta: Record<string, number> } }
  | { type: 'paused';        data: { message: string; types: AnomalyType[] } }
  | { type: 'redact_last_tutor_message'; data: Record<string, never> }
  | { type: 'redact_last_student_message'; data: Record<string, never> }
  // Rich interactive tutor content (epic #87). Emitted when the practice-block
  // cursor reaches a RESOURCE item. `interactive.data` carries NO answer keys.
  | { type: 'media';         data: MediaPayload }
  | { type: 'interactive';   data: InteractiveWidget & { correlationId: string } }
  // A worked example on the virtual whiteboard (epic #318 Phase 2): the whole example,
  // with how many steps are revealed so far; `worked_step` syncs the reveal count after
  // the student's "your turn" step was graded.
  | { type: 'worked_example'; data: WorkedExampleBlock }
  // A math visual drawn from a structured spec (epic #318 Phase 3); `alt` is generated
  // from the spec (describeVisual), never from the model.
  | { type: 'visual';        data: VisualBlock }
  | { type: 'worked_step';   data: { id: string; revealed: number } }
  // End the current tutor message and start a new one (epic #318): a full-lesson turn
  // whose feedback on the student's reply goes out BEFORE the next item's content.
  | { type: 'message_break'; data: Record<string, never> }
  // Replace the text of the tutor message being streamed (#355): the agent tidied it
  // after streaming (e.g. dropped sentences that restated the narration above).
  | { type: 'replace_message'; data: { content: string } }
  // Scratch pad (epic #312): sent at the end of every turn — whether the item the
  // student is on now takes drawn work.
  | { type: 'show_work';     data: ShowWorkState }
  | { type: 'error';         data: { message: string } }

// ─── Rich interactive tutor content (epic #87) ───────────────────────────────
// Videos and interactive widgets (quiz / ordering / matching) are authored as
// LessonResources and sequenced into a practice block by resourceId; the agent
// emits them as `media` / `interactive` SSE events. The client-facing payloads
// below carry NO answer keys — keys + per-question standards live only on the
// server-side InteractiveResourceContent (LessonResource.content) and are stripped
// before the `interactive` event is sent.

export type MediaKind = 'video' | 'lottie' | 'gif' | 'audio' | 'image'

export interface MediaPayload {
  kind:     MediaKind
  url:      string
  caption?: string
  poster?:  string   // still frame for a video / gif
  /** Narration (epic #318 Phase 4): the exact text the audio reads, shown as its transcript. */
  transcript?: string
  /** An illustration's alt text (epic #318 Phase 6). */
  alt?: string
}

// Client-facing widget payloads (no keys) ─────────────────────────────────────
export interface QuizOption   { id: string; text: string }
export interface QuizQuestion {
  id:           string
  prompt:       string
  options:      QuizOption[]
  multiSelect?: boolean
}
export interface QuizWidgetPayload     { questions: QuizQuestion[] }   // multi-question
export interface OrderingWidgetPayload { prompt: string; items: { id: string; text: string }[] }
export interface MatchingWidgetPayload {
  prompt: string
  left:   { id: string; text: string }[]
  right:  { id: string; text: string }[]
}
export type InteractiveWidget =
  | { widget: 'quiz';     payload: QuizWidgetPayload }
  | { widget: 'ordering'; payload: OrderingWidgetPayload }
  | { widget: 'matching'; payload: MatchingWidgetPayload }
  // A math manipulative on a lesson we-do problem (epic #318 Phase 5).
  | { widget: 'manipulative'; payload: ClientManipulative & { prompt: string } }

// Student answers — submitWidgetAnswer input / graph resume value ──────────────
export type WidgetAnswer =
  | { widget: 'quiz';     answers: { questionId: string; selectedOptionIds: string[] }[] }
  | { widget: 'ordering'; orderedItemIds: string[] }
  | { widget: 'matching'; pairs: { leftId: string; rightId: string }[] }
  // null = skipped
  | { widget: 'manipulative'; state: ManipulativeState | null }

// Server-only authored content (LessonResource.content) — keys + per-question
// standards. Validated with this schema on read (agent) and write (teacher / functions).
const idText = z.object({ id: z.string().min(1), text: z.string().min(1) })

// Answer-key verification (epic #283): after generation the model solves each question
// independently several times, without the key; a majority vote confirms, corrects, or
// flags it. Teacher-facing only — toClientWidget never sends it to students.
export const keyVerificationSchema = z.object({
  status:           z.enum(['verified', 'rekeyed', 'needs_review']),
  votes:            z.array(z.string()).optional(),     // each solve's chosen option id, or 'NONE'
  solverValue:      z.string().optional(),               // the solvers' computed answer, for review
  originalOptionId: z.string().optional(),               // the generated key, when it was corrected
})
export type KeyVerification = z.infer<typeof keyVerificationSchema>

export const quizAuthoredQuestionSchema = z.object({
  id:               z.string().min(1),
  prompt:           z.string().min(1),
  options:          z.array(idText).min(2),
  multiSelect:      z.boolean().optional(),
  correctOptionIds: z.array(z.string().min(1)).min(1),   // server-only answer key
  standardId:       z.string().min(1),                    // per-question standard mapping
  dokLevel:         z.enum(['LOW', 'MODERATE', 'HIGH']).optional(),  // authored DOK (epic #261); teacher-only
  verification:     keyVerificationSchema.optional(),    // answer-key check (epic #283); teacher-only
})

export const interactiveResourceContentSchema = z.discriminatedUnion('widget', [
  z.object({
    widget:    z.literal('quiz'),
    questions: z.array(quizAuthoredQuestionSchema).min(1),
  }),
  z.object({
    widget:       z.literal('ordering'),
    prompt:       z.string().min(1),
    items:        z.array(idText).min(2),
    correctOrder: z.array(z.string().min(1)).min(2),      // itemIds in the correct order (key)
    standardId:   z.string().min(1),
  }),
  z.object({
    widget:       z.literal('matching'),
    prompt:       z.string().min(1),
    left:         z.array(idText).min(1),
    right:        z.array(idText).min(1),
    correctPairs: z.array(z.object({ leftId: z.string().min(1), rightId: z.string().min(1) })).min(1),  // key
    standardId:   z.string().min(1),
  }),
])

export type InteractiveResourceContent = z.infer<typeof interactiveResourceContentSchema>

/** Strip answer keys + per-question standards from authored content → the client-facing
 *  widget payload. The agent uses this so the student never receives the key. */
export function toClientWidget(content: InteractiveResourceContent): InteractiveWidget {
  switch (content.widget) {
    case 'quiz':
      return {
        widget: 'quiz',
        payload: {
          questions: content.questions.map((q) => ({
            id: q.id,
            prompt: q.prompt,
            options: q.options,
            ...(q.multiSelect ? { multiSelect: true } : {}),
          })),
        },
      }
    case 'ordering':
      return { widget: 'ordering', payload: { prompt: content.prompt, items: content.items } }
    case 'matching':
      return { widget: 'matching', payload: { prompt: content.prompt, left: content.left, right: content.right } }
  }
}

function setEquals(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  const bs = new Set(b)
  return a.every((x) => bs.has(x))
}

/** Deterministically grade a widget answer against its authored content. Returns one
 *  score entry per graded standard — quiz: one per question (0/1 exact match, carrying the
 *  question's prompt + authored dokLevel); ordering: fraction of items in the correct
 *  position; matching: fraction of correct pairs. */
export function gradeWidget(
  content: InteractiveResourceContent,
  answer: WidgetAnswer,
): { standardId: string; score: number; prompt?: string; dokLevel?: DokLevel; questionId?: string }[] {
  if (content.widget === 'quiz' && answer.widget === 'quiz') {
    const byId = new Map(answer.answers.map((a) => [a.questionId, a.selectedOptionIds]))
    return content.questions.map((q) => ({
      standardId: q.standardId,
      score: setEquals(byId.get(q.id) ?? [], q.correctOptionIds) ? 1 : 0,
      prompt: q.prompt,
      questionId: q.id,
      ...(q.dokLevel ? { dokLevel: q.dokLevel } : {}),
    }))
  }
  if (content.widget === 'ordering' && answer.widget === 'ordering') {
    const n = content.correctOrder.length
    const correct = content.correctOrder.filter((id, i) => answer.orderedItemIds[i] === id).length
    return [{ standardId: content.standardId, score: n ? correct / n : 0 }]
  }
  if (content.widget === 'matching' && answer.widget === 'matching') {
    const key = new Map(content.correctPairs.map((p) => [p.leftId, p.rightId]))
    const n = content.correctPairs.length
    const got = answer.pairs.filter((p) => key.get(p.leftId) === p.rightId).length
    return [{ standardId: content.standardId, score: n ? got / n : 0 }]
  }
  return []
}

// ─── tRPC input/output types ──────────────────────────────────────────────────

// The teacher-gated tutoring window for a class (epic #30, Phase 4). Lives in
// Firestore at tutoringWindows/{classId}, agent-owned (Admin SDK writes, clients
// read via onSnapshot) — same mirror pattern as liveSessions. One window per class.
export interface TutoringWindow {
  classId:      string
  status:       TutoringWindowStatus
  lessonPlanId: string   // the lesson (with a tutorBlock) this window runs
  lessonTitle:  string
  openedByUid:  string
  // Firestore also carries openedAt / updatedAt server timestamps.
}

// Per-student progress through a tutoring window (epic #30, Phase 6). Firestore
// mirror at tutoringProgress/{classId}_{lessonPlanId}_{studentUid}, agent-owned — drives the
// teacher's live completion dashboard. PRACTICING on a lesson-driven start →
// COMPLETED (with the mastery delta on the block's standards) when the exit
// ticket is done, or ENDED if the student ends the session before finishing it
// (so the teacher dashboard doesn't show them stuck "Practicing" forever).
export interface TutoringProgress {
  classId:      string
  lessonPlanId: string
  studentUid:   string
  studentName:  string
  status:       'PRACTICING' | 'COMPLETED' | 'ENDED'
  // standardCode → mastery delta (0..1) on the block's standards; set on completion.
  masteryDelta?: Record<string, number>
  // standardCode → standing mastery (0..1) on the block's standards; updated live
  // each assessment turn during practice so the teacher sees the absolute score climb.
  masteryScore?: Record<string, number>
  // Where the student is (epic #500), kept current each graded turn: the section, the
  // 0-based item within it and the section's item count, plus the non-passing tries on
  // that item so far. Absent on docs written before it shipped.
  section?:      'PRACTICE' | 'EXIT_TICKET'
  itemIndex?:    number
  itemCount?:    number
  triesOnItem?:  number
  // ISO times: first opened this lesson's practice / finished the exit ticket.
  startedAt?:    string
  completedAt?:  string
}

// lessonPlanId present ⇒ a lesson-driven (gated) start: refused unless the class's
// tutoring window is OPEN for that lesson. Absent ⇒ ad-hoc tutoring (ungated).
// A student has at most one RUNNING session per class. `lessonPlanId` joins that
// lesson's teacher-opened practice; `freeTutoring` starts (or resumes) free tutoring;
// `makeupLessonPlanId` starts (or resumes) an assigned AI-taught make-up lesson (epic
// #318). An explicit request ENDS an open practice/free session of another kind, but a
// make-up lesson is only PARKED (paused, resumable later) — never ended by a switch.
// With no flag, start resumes the most recent open non-make-up session.
export interface SessionStartInput  {
  studentId: string
  classId: string
  lessonPlanId?: string
  freeTutoring?: boolean
  makeupLessonPlanId?: string
  // Free tutoring on one skill the student picked on the Overview skill map (#425):
  // seeds the session's current standard on a NEW free session.
  focusStandardId?: string
  // Free tutoring as prep for this unit test: the session covers every standard aligned
  // to the test lesson, weakest first (a resumed free session switches to it).
  testPrepLessonPlanId?: string
}
export interface SessionStartOutput {
  sessionId:       string
  threadId:        string
  status:          'ACTIVE' | 'PAUSED'
  // Non-empty only when status is 'PAUSED' because of an unresolved anomaly escalation
  // (not a plain self/teacher pause) — lets the client show the right banner/no-retry
  // state on a fresh mount instead of guessing from `status` alone.
  escalationTypes?: AnomalyType[]
  enrollment:      ClassEnrollmentSettings
  masterySnapshot: StandardsMastery[]
  // Lesson-driven (gated) starts only: the standard ids the lesson's tutor block
  // targets. The student view narrows its progress sidebar to these so a focused
  // practice shows just the standard(s) it's about, not the whole course. Absent/
  // empty for ad-hoc tutoring, where the full course mastery is shown.
  focusStandardIds?: string[]
  // The course's standards grouped by unit, for the progress rail's "All skills" (#409).
  standardGroups?: StandardGroup[]
  // Free tutoring (epic #639): the student's open focus practice in this class — the
  // rail's "Practice: N of M answers" for the skill being practised.
  focusPractice?: FocusPracticeProgress[]
  // Free tutoring (#410): the standard currently being practiced, on resume.
  currentStandardId?: string
  // On resume: this session's net mastery change so far, by standardId (the agent's
  // session_meta.progress_delta). The rail's "This session" is derived from this plus
  // the live score — never by summing SSE progress events, which a reconnect can miss.
  sessionProgress?: Record<string, number>
  // The lesson this session practices; absent for free tutoring.
  lessonPlanId?: string
  // An AI-taught full (make-up) lesson session (epic #318).
  fullLesson?: boolean
  // A NEW session whose agent-opening turn is streaming now: connect the SSE stream
  // instead of loading history.
  opening?: boolean
  // Scratch pad (epic #312): the current item takes drawn work; `pending` = a drawing
  // whose reading the student still has to check.
  showWork?: ShowWorkState
}

export interface SessionResumeInput  { sessionId: string; threadId: string }
export interface SessionResumeOutput { resumedAt: string }

export interface SessionCheckResumableInput  { studentId: string; classId: string }
// When resumable, `lessonPlanId`/`lessonTitle` identify an unfinished lesson practice
// (absent ⇒ the open session is free tutoring).
export interface SessionCheckResumableOutput {
  resumable: boolean
  lessonPlanId?: string
  lessonTitle?: string
  // Unfinished lesson practice only: how far through the block (practice problems then
  // exit-ticket checks) the student is — the Overview's "3 of 6 done" (#422).
  itemsDone?: number
  itemsTotal?: number
  // When the unfinished lesson practice stops being resumable (ISO, #420).
  expiresAt?: string
}

// Student Overview data (epic #421): the student's mastery across the class's course
// (every course standard; unassessed ones at evidenceCount 0) grouped by unit — the
// same snapshot + groups session.start returns, without starting a session.
export interface StudentOverviewInput  { classId: string }
export interface StudentOverviewOutput {
  mastery:        StandardsMastery[]
  standardGroups: StandardGroup[]
  /** Open focus practice in this class (epic #575, #583); absent from older agents. */
  practice?:      StudentFocusPractice[]
}

/** Focus practice a teacher assigned on one skill (epic #575, #583): free tutoring on it. */
export interface StudentFocusPractice {
  standardId:    string
  label:         string
  // Epic #639: the code tells look-alike skill names apart; assignedAt marks a New one.
  standardCode?: string
  assignedAt?:   string
  dueDate:       string | null
  answersDone:   number
  targetAnswers: number
  inProgress:    boolean
}

// The student's week in one class (#423): Mon–Fri from the server-side class pacing
// (the dates teachers see). `weekStart` (a Monday) and `today` are the student's local
// dates (YYYY-MM-DD) — the server has no school timezone.
export interface StudentWeekInput { classId: string; weekStart: string; today: string }
// How far the student got in a lesson's AI Practice block: answered every exit-ticket
// check → COMPLETED; any exit-ticket answer → STARTED. Practice problems alone don't count.
export type StudentPracticeStatus = 'NONE' | 'STARTED' | 'COMPLETED'
export interface StudentWeekLesson {
  lessonPlanId:   string
  title:          string
  kind:           LessonPlanKind
  unitTitle:      string
  hasPractice:    boolean
  practiceStatus: StudentPracticeStatus
}
// CLASS: the class meets (lessons may be empty); NO_SCHOOL: a non-instructional special
// day (`label` = its name); NO_LESSONS: meets on a no-lessons schedule (e.g. a half day);
// NO_CLASS: an instructional day this class doesn't meet.
export type StudentWeekDayKind = 'CLASS' | 'NO_SCHOOL' | 'NO_LESSONS' | 'NO_CLASS'
export interface StudentWeekDay {
  date:    string
  kind:    StudentWeekDayKind
  label?:  string
  lessons: StudentWeekLesson[]
}
export interface StudentUpcomingTest {
  lessonPlanId: string
  title:        string
  date:         string
  unitTitle:    string
  // School days (class meetings) from `today` to the test: 0 = today, 1 = next meeting.
  meetingsAway: number
}
export interface StudentWeekOutput {
  weekStart: string
  days:      StudentWeekDay[]
  // The next unit post-assessment on or after `today`, anywhere in the year (null if none).
  nextUnitTest: StudentUpcomingTest | null
  // Make-up lessons the student finished this week (#424 — the To do list's checked-off rows).
  completedMakeups: { lessonPlanId: string; title: string; completedAt: string }[]
  // Focus practice the student finished this week (epic #639 — checked off like make-ups).
  completedPractice?: { standardId: string; label: string; completedAt: string }[]
}

// My Classes (epic #433): one status card per enrolled class (ACTIVE school year). The
// route returns each class's raw to-do inputs; the student app ranks them with the same
// buildOverviewPlan as the Overview and adds the live tutoringWindows signal itself.
// Course / period / teacher come from the client's GetMyClassEnrollments.
export interface StudentClassesInput { today: string }
export interface StudentClassMakeup {
  lessonPlanId: string
  title:        string
  dueDate:      string | null
  inProgress:   boolean
}
export interface StudentClassCard {
  classId: string
  // false when this class's reads failed — the card shows a retry state, the rest load.
  ok: boolean
  // Today in this class (kind NO_CLASS when there's no pacing for it).
  today: StudentWeekDay
  nextUnitTest: StudentUpcomingTest | null
  // Average mastery over the course standards the student has evidence on (null: none
  // yet), and how many of the course's standards that is.
  mastery: { average: number | null; practiced: number; total: number }
  // Lowest practiced skill below Mastered (80%) — in the next unit test's unit when
  // there is one, else across the course. Prep / Practice focus on it.
  weakest: { standardId: string; label: string; pct: number } | null
  makeups: StudentClassMakeup[]
  // Open focus practice on one skill (epic #575, #583).
  practice: StudentFocusPractice[]
  // An open, unexpired lesson AI Practice (#420).
  unfinished: { lessonPlanId: string; title: string; expiresAt: string } | null
  freeInProgress: boolean
}
export interface StudentClassesOutput { classes: StudentClassCard[] }

export interface SendMessageInput  {
  sessionId: string; threadId: string; message: string
  // Scratch pad (epic #312): the drawing sent with this answer, already checked (work.confirm).
  drawingId?: string
}
export interface SendMessageOutput { eventId: string }

// Submit an interactive-widget answer (epic #87). Resumes the parked turn and routes
// to grade_widget, which grades deterministically and advances the block.
export interface SubmitWidgetAnswerInput  { sessionId: string; threadId: string; correlationId: string; answer: WidgetAnswer }
export interface SubmitWidgetAnswerOutput { eventId: string }

export interface SessionHistoryInput  { threadId: string; /** A past-session replay (epic #379). */ readOnly?: boolean }
export interface SessionHistoryMessage {
  id:        string
  role:      'student' | 'assistant' | 'school_staff'
  content:   string
  redacted?: boolean
  // A rich block presented mid-transcript (epic #87), resurrected in position so it
  // survives reload / resume — these aren't part of the durable message list. Media is
  // watch-only; an interactive widget is the current unanswered one (student can answer it).
  block?:
    | { kind: 'media'; media: MediaPayload }
    // `answered` / `answer`: a lesson manipulative (epic #318 Phase 5) shown with the
    // student's submitted answer. `score`: a graded practice widget (quiz / ordering /
    // matching) in a read-only transcript (epic #379).
    | { kind: 'interactive'; widget: InteractiveWidget & { correlationId: string }; answered?: boolean; answer?: WidgetAnswer; score?: { correct: number; total: number } }
    | { kind: 'worked_example'; example: WorkedExampleBlock }
    | { kind: 'visual'; visual: VisualBlock }
    // A mastery change the answer just above caused (#408): the "↑ +12% <skill>" chip,
    // restored from the checkpoint's progress markers so it survives reload / resume.
    | { kind: 'progress'; standardId: string; label: string; delta: number; newScore: number }
    // Focus practice finished on this answer (epic #639): "Practice complete ✓".
    | { kind: 'practice_complete'; standardId: string; label: string; targetAnswers: number }
    // A counted answer whose change was too small for a chip (epic #639): "✓ Counted · +0.6%".
    // Not a chip — it never counts toward chip ordinals.
    | { kind: 'counted'; standardId: string; label: string; delta: number; newScore: number }
  /**
   * Staff transcripts only: this message is part of an alert. `answer` = the student
   * message that triggered it; `question` = the tutor message / block it answered.
   */
  alert?: { part: 'question' | 'answer'; anomalyIds: string[]; types: AnomalyType[] }
  /** Scratch pad (epic #312): this student answer was sent with a drawing of the work.
   *  `lines` = the reading the student confirmed; absent when they skipped the check. */
  work?: { drawingId: string; lines?: string[]; fixedLines?: number }
}

export interface VisualBlock { id: string; spec: VisualSpec; alt: string }

/** A worked example as the student app renders it (no answer key / status). */
export interface WorkedExampleBlock {
  id:       string
  problem:  string
  steps:    { math: string; explanation: string; studentTries?: boolean }[]
  revealed: number   // steps shown so far (≥ 1)
}

export interface RevealWorkedStepInput { threadId: string; id: string; revealed: number }
export interface SessionHistoryOutput { messages: SessionHistoryMessage[] }

export interface SessionEndInput  { sessionId: string; threadId: string }
export interface SessionEndOutput {
  summary:      string
  masteryDelta: Record<string, number>
  badgesEarned: Badge[]
}

// Teacher-triggered anomaly pause (existing, unchanged)
export interface PauseSessionInput  { threadId: string; reason: AnomalyType }
export interface PauseSessionOutput { checkpointId: string; pausedAt: string }

// Student-triggered pause (new)
export interface StudentPauseInput  { sessionId: string; threadId: string }
export interface StudentPauseOutput { pausedAt: string }

export interface SessionSummary {
  sessionId:        string
  langgraphThreadId: string
  lessonPlanId?:    string   // the lesson/tutor-block this session ran, when lesson-driven (epic #30)
  startedAt:        string
  endedAt:          string | null
  durationMinutes:  number | null
  interactionCount: number
  standardsTouched: string[]
  progressDelta:    Record<string, number>
}

// ─── Staff session history (epic #379) ───────────────────────────────────────
/** What a session was, derived from its row: a make-up lesson, a unit assessment, lesson practice, or free tutoring. */
export type SessionKind = 'lesson' | 'assessment' | 'practice' | 'free'

export interface StaffSessionSummary extends SessionSummary {
  status:     SessionStatus
  kind:       SessionKind
  escalated:  boolean
  lessonPlan: { id: string; title: string } | null
  /**
   * Replayed with the class's scoring policy (epic #547) — use this, not the stored
   * `progressDelta` (stale since the #440 backfill). Absent when the replay failed.
   */
  skills?:    SessionSkillChange[]
}

export interface ListStudentSessionsInput {
  studentUid:    string
  classId:       string
  lessonPlanId?: string
  limit?:        number
  /** Cursor: the `startedAt` of the last row of the previous page. */
  before?:       string
}
export interface ListStudentSessionsOutput {
  student:    { uid: string; displayName: string | null }
  sessions:   StaffSessionSummary[]
  /** Pass as `before` to load the next page; null when there are no more. */
  nextCursor: string | null
}

// ─── Student Past Sessions (epic #463) ───────────────────────────────────────
/**
 * Where a session stands for the student: finished, still resumable, or lesson practice
 * that ended (by the student, a session switch, or its end-of-day expiry, #420) before
 * its exit ticket was done.
 */
export type StudentSessionState = 'done' | 'open' | 'unfinished'

/** One skill's mastery across a session: the score just before its first answer and after its last. */
export interface SessionSkillChange {
  standardId:    string
  label:         string   // standardLabel() of the standard
  before:        number
  after:         number
  /** Counted answers on the skill after this session — feeds masteryBand's `early` state. */
  evidenceCount: number
}

export interface StudentSessionSummary {
  sessionId:       string
  threadId:        string
  kind:            SessionKind
  /** The lesson's title; null for free tutoring (use `focusStandardId`). */
  title:           string | null
  lessonPlanId:    string | null
  /** Free tutoring: the skill most of the answers were on; null when nothing was answered. */
  focusStandardId: string | null
  state:           StudentSessionState
  startedAt:       string
  endedAt:         string | null
  /** Recorded duration, or first-to-last activity for a session that never ended. */
  minutes:         number | null
  /** Scored answers (multi-part steps and held-back answers are not scored). */
  answered:        number
  /** Scored answers at or above 0.5. */
  correct:         number
  exitTicket:      { answered: number; correct: number } | null
  /** Replayed with the class's scoring policy — not the stored `progressDelta`. */
  skills:          SessionSkillChange[]
}

/** The summary strip: the current school month. */
export interface StudentPastSessionsSummary {
  month:          string   // 'YYYY-MM' in school time
  sessions:       number
  minutes:        number
  answered:       number
  correct:        number
  skillsImproved: number
}

export interface StudentSessionsInput {
  classId: string
  limit?:  number
  /** Cursor: `nextCursor` from the previous page. */
  before?: string
}
export interface StudentSessionsOutput {
  sessions:   StudentSessionSummary[]
  nextCursor: string | null
  /** First page only (no `before`). */
  summary:    StudentPastSessionsSummary | null
}

/** A scored answer below the pass line, for the recap's "Look at again" list. */
export interface StudentMissedItem {
  eventId:         string
  prompt:          string
  studentResponse: string
  standardId:      string
  label:           string | null
  /** The student answered the same prompt correctly later in the session. */
  fixedLater:      boolean
  /**
   * The answer, only where an authored key exists — a quiz / ordering / matching widget
   * (then `studentResponse` is the student's choice in words, not the raw widget JSON).
   */
  answer:          string | null
  /** Answered in a widget (the transcript shows it as a widget block, not a text bubble). */
  inWidget:        boolean
}

export interface StudentSessionRecapInput  { sessionId: string }
/**
 * The recap (epic #463). Typed answers carry no key — AssessmentEvent stores none and
 * free-tutoring problems have none, so the tutor's explanation in the transcript is the
 * answer. Widget answers are resolved against the lesson's authored widget, key included.
 */
export interface StudentSessionRecapOutput {
  classId: string
  session: StudentSessionSummary
  /** In the order they happened; one per prompt (the first miss). */
  missed:  StudentMissedItem[]
}

export interface StudentSessionNoteInput { sessionId: string }
/** "Your tutor's note" on the recap (#468); null when the session is too short for one. */
export interface StudentSessionNoteOutput { note: string | null }

/** One session's effect on one skill, for the By skill tab's trail. */
export interface SkillTrailStop {
  sessionId: string
  kind:      SessionKind
  title:     string | null
  startedAt: string
  before:    number
  after:     number
}

export interface StudentSkillHistory {
  standardId:    string
  label:         string
  /** The standing score (StandardsMastery — the same number as the skill map). */
  score:         number
  evidenceCount: number
  /** Sessions in this class that touched the skill, oldest first. */
  trail:         SkillTrailStop[]
}

export interface StudentSkillHistoryInput  { classId: string }
/** Skills touched in the class's sessions, weakest first (epic #463, By skill tab). */
export interface StudentSkillHistoryOutput { skills: StudentSkillHistory[] }

// ─── Score explainer (epic #547) ─────────────────────────────────────────────
// "How was this calculated?": every answer that touched a skill, with the score just
// before and after it and the parts of the formula that explain the move. Built by the
// agent's pure score ledger (the same fold as the mastery replay); `explainLedgerStep`
// is the one wording, shared by the teacher drawer, the student recap and Ask AI.

/** Why an answer was recorded but didn't move the score — mirrors update_progress's skips. */
export type ScoreLedgerUncounted = 'practice' | 'check' | 'guided' | 'in_progress' | 'held_back' | 'assessment' | 'record_only'

export interface ScoreLedgerStep {
  eventId:         string
  sessionId:       string
  /** The session's lesson title; null for free tutoring or a session in another class. */
  lessonTitle:     string | null
  /** null when the session belongs to another class (mastery is per student × standard). */
  sessionKind:     SessionKind | null
  sessionStartedAt: string | null
  /** EXIT_TICKET, PRACTICE, FREE_TUTOR … (legacy free-tutor rows read FREE_TUTOR). */
  section:         string | null
  /** 0-based position within its section, when recorded. */
  itemIndex:       number | null
  prompt:          string
  /** The answer in words (widget answers are resolved against the lesson's widget). */
  response:        string
  /** Only where an authored key exists (quiz / ordering / matching widgets). */
  answerKey:       string | null
  /** The score that counted (a teacher override replaces the grader's). */
  score:           number
  correct:         boolean
  overridden:      boolean
  counted:         boolean
  uncountedReason: ScoreLedgerUncounted | null
  before:          number
  after:           number
  /** Counted answers on the skill after this one. */
  evidenceAfter:   number
  /** Share of the gap moved; null when not counted. */
  alpha:           number | null
  regime:          'warmup' | 'floor' | null
  /** Per-answer weight (0.5 = free tutoring under REDUCED). */
  weight:          number
  createdAt:       string
}

export interface ScoreLedgerSkill {
  standardId:    string
  label:         string
  startScore:    number
  startEvidence: number
  endScore:      number
  endEvidence:   number
  /** The score after one more right, full-weight answer — for "one more right answer → X%". */
  nextIfRight:   number
  steps:         ScoreLedgerStep[]
}

/** The class's scoring settings the ledger was replayed with. */
export interface ScoreLedgerPolicy {
  responsiveness: MasteryResponsiveness
  priorWeight:    number
  floors:         { up: number; down: number }
  freeTutoring:   FreeTutoringMastery
}

export interface ScoreLedger {
  /** `session`: the skills one session touched. `skill`: one skill's whole history. */
  scope:  'session' | 'skill'
  policy: ScoreLedgerPolicy
  skills: ScoreLedgerSkill[]
}

export interface StaffScoreLedgerInput   { classId: string; studentUid: string; sessionId?: string; standardId?: string }
export interface StudentScoreLedgerInput { classId: string; sessionId?: string; standardId?: string }
export type ScoreLedgerOutput = ScoreLedger

const LEDGER_FRACTIONS: Record<number, string> = { 2: 'half', 3: 'a third', 4: 'a quarter', 5: 'a fifth', 6: 'a sixth', 7: 'a seventh', 8: 'an eighth', 10: 'a tenth' }

/** α in words: "a quarter", "a third" … or a percent when it isn't a simple fraction. */
export function ledgerFractionWords(alpha: number): string {
  for (const [m, name] of Object.entries(LEDGER_FRACTIONS)) {
    if (Math.abs(alpha - 1 / Number(m)) < 0.004) return name
  }
  return `${Math.round(alpha * 100)}%`
}

const ledgerPct = (x: number) => Math.round(x * 100)

const LEDGER_UNCOUNTED: Record<ScoreLedgerUncounted, [staff: string, student: string]> = {
  practice:    ['Practice problem — for learning only; it doesn’t change the score.', 'Practice — it’s for learning, so it doesn’t change your score.'],
  check:       ['Check for understanding — it doesn’t change the score.', 'A quick check — it doesn’t change your score.'],
  guided:      ['Worked-together problem — it doesn’t change the score.', 'A problem you worked on with the tutor — it doesn’t change your score.'],
  in_progress: ['One step of a longer problem — the score changes when the whole problem is answered.', 'One step of a longer problem — your score changes when you finish the whole problem.'],
  held_back:   ['Held back until the work was shown — not scored.', 'Held back until you showed your work — not scored.'],
  assessment:  ['Unit test answer — reported with the unit test; it doesn’t change the skill score.', 'A unit test answer — it’s part of your test result, not this score.'],
  record_only: ['Free tutoring is record-only in this class — not scored.', 'Free tutoring practice — it doesn’t change your score in this class.'],
}

/** "Right" / "Wrong" / "Partly right" for a counted step. */
export function ledgerVerdict(step: Pick<ScoreLedgerStep, 'score'>): 'Right' | 'Wrong' | 'Partly right' {
  return step.score >= 1 ? 'Right' : step.score <= 0 ? 'Wrong' : 'Partly right'
}

/** A point count with at most one decimal ("2.2", "12"). */
const onePlace = (x: number) => String(Math.round(x * 10) / 10)

/**
 * A score change in points (`fraction` = after − before, 0–1 scale). From 1 point up it
 * is whole, rounded like the in-chat chips ("+3", "−23"); below 1 point it keeps one
 * decimal ("+0.6", "+<0.1"), so a small gain never reads as "98% → 98% +1" (epic #639).
 * Shared by the score explainer and the in-chat "Counted" note.
 */
export function formatPointChange(fraction: number): string {
  const d = fraction * 100
  const a = Math.abs(d)
  const sign = d > 0 ? '+' : d < 0 ? '−' : '±'
  if (a >= 0.95) return `${sign}${Math.round(a)}`
  if (a === 0) return '±0'
  return Math.round(a * 10) === 0 ? `${sign}<0.1` : `${sign}${onePlace(a)}`
}

/** The signed change of a step: whole points from 1 up (like the chips), one decimal below. */
export function ledgerDelta(step: Pick<ScoreLedgerStep, 'before' | 'after'>): string {
  return formatPointChange(step.after - step.before)
}

/**
 * The smallest mastery change that gets an in-chat chip (#408) — `update_progress` pushes
 * a chip only above it. Smaller counted changes get a quiet "Counted" note instead
 * (epic #639), which is never a chip, so it can't shift chip ordinals.
 */
export const CHIP_MIN_DELTA = 0.01

/**
 * The answer an in-chat chip stands for (epic #547): chips exist only for counted answers
 * that moved the score by more than CHIP_MIN_DELTA, so a chip's ordinal on its skill
 * indexes that subset of the ledger's steps. Shared by both score explainers.
 */
export function chipStepEventId(
  steps: Pick<ScoreLedgerStep, 'eventId' | 'counted' | 'before' | 'after'>[],
  ordinal: number,
): string | null {
  return steps.filter((s) => s.counted && Math.abs(s.after - s.before) > CHIP_MIN_DELTA)[ordinal]?.eventId ?? null
}

/** How each answer moves the score, in words, for a class's policy. */
export function ledgerRuleWords(policy: Pick<ScoreLedgerPolicy, 'floors'>): string {
  const up = ledgerFractionWords(policy.floors.up)
  const down = ledgerFractionWords(policy.floors.down)
  return up === down
    ? `each answer moves the score ${up} of the way toward 100% when it’s right and toward 0% when it’s wrong`
    : `each right answer moves the score ${up} of the way toward 100% and each wrong answer ${down} of the way toward 0%`
}

const PARENT_STEP_LIMIT = 12

/**
 * The plain-text summary a teacher copies for a parent (epic #547): the change, the rule
 * in one sentence, the answers that counted and what it takes to reach Mastered. No
 * formula symbols, no answer keys.
 */
export function ledgerParentSummary(input: {
  studentName: string
  skill:       ScoreLedgerSkill
  policy:      Pick<ScoreLedgerPolicy, 'floors'>
  /** Where it happened, e.g. "Tuesday’s exit ticket"; omitted for a whole-history summary. */
  when?:       string
}): string {
  const { studentName: name, skill, policy, when } = input
  const counted = skill.steps.filter((s) => s.counted)
  const right = counted.filter((s) => s.score >= 1).length
  const notCounted = skill.steps.length - counted.length
  const start = ledgerPct(skill.startScore)
  const end = ledgerPct(skill.endScore)
  const lines: string[] = []

  lines.push(`${name}’s “${skill.label}” score went from ${start}% to ${end}%${when ? ` in ${when}` : ''}. ${name} answered ${right} of ${counted.length} scored question${counted.length === 1 ? '' : 's'} correctly.`)
  lines.push('')
  lines.push(`How the score works: ${ledgerRuleWords(policy)}. When a student is already doing well, there is little room left to go up and a long way to come down, so one miss counts more than a right answer. The first few answers on a skill are averaged, so one lucky answer can’t read 100%.`)
  lines.push('')
  lines.push('Answer by answer:')
  const shown = counted.slice(-PARENT_STEP_LIMIT)
  if (shown.length < counted.length) lines.push(`(${counted.length - shown.length} earlier answers not shown)`)
  shown.forEach((s, i) => {
    lines.push(`${counted.length - shown.length + i + 1}. ${ledgerVerdict(s)} — ${ledgerPct(s.before)}% → ${ledgerPct(s.after)}% (${ledgerDelta(s)})`)
  })
  if (notCounted > 0) lines.push(`${notCounted} practice answer${notCounted === 1 ? ' was' : 's were'} for learning and didn’t change the score.`)
  lines.push('')
  const next = ledgerPct(skill.nextIfRight)
  if (skill.endScore >= MASTERY_BANDS.mastered) lines.push(`${name} is in the Mastered range (80% and up).`)
  else if (skill.nextIfRight >= MASTERY_BANDS.mastered) lines.push(`One more correct answer will bring ${name} to ${next}% — Mastered (80% and up).`)
  else lines.push(`The next correct answer will bring ${name} to ${next}%; Mastered starts at 80%.`)
  return lines.join('\n')
}

/**
 * One ledger step in a sentence. `staff` (teacher, and the parent copy) or `student`
 * ("you"). Numbers are whole percents; the change is rounded on its own, like the
 * in-chat progress chips, so the two never disagree (a row can then be a point off
 * the difference of its rounded before/after).
 */
export function explainLedgerStep(step: ScoreLedgerStep, audience: 'staff' | 'student' = 'staff'): string {
  const you = audience === 'student'
  if (!step.counted || step.alpha === null) {
    return LEDGER_UNCOUNTED[step.uncountedReason ?? 'practice'][you ? 1 : 0]
  }
  const before = ledgerPct(step.before)
  const after = ledgerPct(step.after)
  const f = ledgerFractionWords(step.alpha)
  const signed = ledgerDelta(step)
  const score = you ? 'your score' : 'the score'
  const prefix = (step.overridden ? (you ? 'Your teacher changed this answer’s grade. ' : 'Grade changed by a teacher. ') : '')
    + (step.weight < 1 ? (you ? 'Free tutoring counts half in your class. ' : 'Free tutoring counts half in this class. ') : '')

  if (step.regime === 'warmup') {
    const verdict = step.score >= 1 ? (you ? 'Right!' : 'Right.') : step.score <= 0 ? (you ? 'Not quite.' : 'Wrong.') : `Partly right (${ledgerPct(step.score)}% credit).`
    return `${prefix}${verdict} ${you ? 'One of your first answers' : 'One of the first answers'} on this skill, so it moves ${score} ${f} of the way → ${after}% (${signed}).`
  }
  if (step.score >= 1) {
    // From the unrounded score, so the sum adds up: 97.8% leaves 2.2 points, a quarter
    // of which is +0.6 (epic #639). Whole points when the change is 1 point or more.
    const exact = (1 - step.before) * 100
    const room = Math.abs(step.after - step.before) * 100 < 0.95 ? onePlace(exact) : String(Math.round(exact))
    return `${prefix}${you ? 'Right!' : 'Right.'} ${you ? 'You had' : 'There were'} ${room} points of room; ${f} of that is ${signed} → ${after}%.`
  }
  if (step.score <= 0) {
    return `${prefix}${you ? 'Not quite.' : 'Wrong.'} At ${before}%, a miss moves ${score} ${f} of the way toward 0 → ${after}% (${signed}). The higher the score, the bigger one miss.`
  }
  return `${prefix}Partly right (${ledgerPct(step.score)}% credit): ${score} moves ${f} of the way toward ${ledgerPct(step.score)}% → ${after}% (${signed}).`
}

export interface ClassSessionSummaryInput  { classId: string }
export interface ClassSessionSummaryOutput {
  students: { studentUid: string; sessionCount: number; lastStartedAt: string | null; escalatedCount: number }[]
}

export interface GetSessionTranscriptInput  { threadId: string }
export interface GetSessionTranscriptOutput {
  session: StaffSessionSummary & { classId: string; student: { uid: string; displayName: string | null } }
  /**
   * Read-only, as the student saw it, except that redacted messages keep their original
   * text (with `redacted: true`) so staff can see what was hidden. Empty when the
   * checkpoint is gone (`transcriptAvailable: false`).
   */
  messages:            SessionHistoryMessage[]
  transcriptAvailable: boolean
  /** Every alert raised in this session (open and dismissed), oldest first. */
  alerts:              TranscriptAlert[]
}

export interface TranscriptAlert {
  anomalyId:    string
  type:         AnomalyType
  triggeredAt:  string
  acknowledged: boolean
  /** Whether the triggering message was found in the transcript (and is highlighted). */
  located:      boolean
}

export interface InjectPromptInput  {
  threadId: string
  prompt:   string
  type:     'school_staff_message' | 'agent_instruction'
}
export interface InjectPromptOutput { injected: boolean }

export interface ResumeSessionInput  {
  threadId:      string
  checkpointId:  string
  scopeOverride?: ScopeOverride
}
export interface ResumeSessionOutput { resumed: boolean }

export interface SubmitReviewInput  { eventId: string; score: number; note?: string }
export interface SubmitReviewOutput { newMasteryScore: number; masteryTrend: MasteryTrend }

// ─── District rollup (epic #76 Phase 3) ──────────────────────────────────────
// Response shape of the getDistrictRollup callable (functions/src/district-rollup.ts).
// The callable is the ONLY place these metrics are computed — Data Connect has no
// aggregation, so a Cloud Function pages the class-denormalised tables and rolls them
// up (never client-aggregated). Consumed by the teacher app's district dashboard.

// The four per-scope metrics, shared by the school- and class-level rollups.
export interface RollupMetrics {
  enrollmentCount:        number        // distinct student UIDs across ClassEnrollment
  activeSessionCount:     number        // Session.status != ENDED
  masteryAverage:         number | null // mean StandardsMastery.masteryScore; null if no rows
  unacknowledgedAlertCount: number      // AnomalyEvent.schoolStaffAcknowledged == false
}

export interface SchoolRollup extends RollupMetrics {
  schoolId:             string
  schoolName:           string
  activeSchoolYearName: string | null   // null if the school has no ACTIVE school year
}

export interface PerClassRollup extends RollupMetrics {
  classId:     string
  courseName:  string
  teacherName: string | null
}

export interface DistrictRollup {
  districtId:   string
  districtName: string
  generatedAt:  string                  // ISO timestamp the rollup was computed
  schools:      SchoolRollup[]          // district mode (always present)
  classes?:     PerClassRollup[]        // drill-down mode (present when schoolId was given)
}

export interface GetDistrictRollupInput  { districtId: string; schoolId?: string }
export type      GetDistrictRollupOutput = DistrictRollup

// ─── Cost rollup (epic #223 Phase 2 / #224) ─────────────────────────────────
// Response shapes of the getSchoolCostRollup / getClassCostRollup callables
// (functions/src/cost-rollup.ts). Measured LLM (variable) cost, attributed from the
// per-turn SessionCost ledger. INTERNAL — ED360_STAFF only; the callables are the ONLY
// place cost is aggregated (Data Connect has no SUM). Cost is attributed to a school
// year by the CLASS's owning year (via classId — a Class belongs to exactly one year),
// NOT by the turn's wall-clock createdAt; `priced` is false if any row undercounted
// (an unpriced model billed $0).

export interface ClassCostRollup {
  classId:      string
  courseName:   string
  teacherName:  string | null
  totalUsd:     number
  turnCount:    number
  sessionCount: number
  studentCount: number
}

export interface TeacherCostRollup {
  teacherUid:  string | null   // null → the "Unassigned" bucket (Class.teacher is nullable)
  teacherName: string | null
  classCount:  number
  totalUsd:    number
  turnCount:   number
}

export interface MonthCost { month: string; totalUsd: number }   // "YYYY-MM"

/**
 * Spend for one (provider, model) pair. The agent can serve a turn on more than one
 * model — `LLM_PROVIDER` selects the primary, and a provider error falls back to
 * Anthropic (see apps/agent/src/llm/anthropic.ts). Prices differ by more than an order
 * of magnitude between tiers, so a rollup that reports only dollars can move sharply
 * without the workload changing at all. Breaking spend out per model makes that
 * visible in the product instead of inferable only from Cloud Run logs.
 */
export interface ProviderCost {
  provider:     string   // 'anthropic' | 'openai_compat'
  modelId:      string
  totalUsd:     number
  turnCount:    number   // turns whose PRIMARY (highest-cost) model was this one
  inputTokens:  number
  outputTokens: number
}

// Authoring-time cost (epic #342): teacher-side AI spend with no student behind it —
// lesson drafting, visuals, answer checks, narration (TTS), images, practice seeds,
// curriculum / standards / map ingestion — from the AuthoringCost ledger. Each row's
// school year is STAMPED at write (the course's year, or the school's ACTIVE year for
// school-scoped work), and the rollup filters on it.
export interface AuthoringKindCost   { kind: string; totalUsd: number; count: number }   // kind = route / pipeline, e.g. 'draftScript', 'ingest_curriculum'
export interface AuthoringCourseCost { courseId: string | null; courseName: string | null; totalUsd: number; count: number }   // null → school-wide (standards)
export interface AuthoringActorCost  { actorUid: string | null; actorName: string | null; totalUsd: number; count: number }    // null → unknown (no uploader recorded)
export interface AuthoringModelCost {
  medium:       string   // 'text' | 'tts' | 'image'
  provider:     string
  modelId:      string
  totalUsd:     number
  count:        number
  inputTokens:  number
  outputTokens: number
  ttsChars:     number
  imageCount:   number
}
export interface AuthoringCostRollup {
  totalUsd: number
  rowCount: number
  priced:   boolean
  byKind:   AuthoringKindCost[]
  byCourse: AuthoringCourseCost[]
  byActor:  AuthoringActorCost[]
  byModel:  AuthoringModelCost[]
  byMonth:  MonthCost[]
}

export interface SchoolCostRollup {
  schoolId:       string
  schoolName:     string
  schoolYearId:   string
  schoolYearName: string | null   // null if the requested year no longer exists
  generatedAt:    string          // ISO timestamp the rollup was computed
  month:          string | null   // the "YYYY-MM" filter applied, or null for the whole year
  totalUsd:       number
  turnCount:      number
  priced:         boolean
  byClass:        ClassCostRollup[]
  byTeacher:      TeacherCostRollup[]
  byMonth:        MonthCost[]
  byProvider:     ProviderCost[]   // desc by cost; >1 entry ⇒ turns ran on mixed models
  authoring:      AuthoringCostRollup   // totalUsd / turnCount / byMonth above are TUTORING (SessionCost) only
}

export interface StudentCost { studentUid: string; totalUsd: number; turnCount: number }
export interface DayCost     { day: string; totalUsd: number }   // "YYYY-MM-DD"

export interface ClassCostDetail {
  classId:      string
  courseName:   string
  teacherName:  string | null
  generatedAt:  string
  totalUsd:     number
  turnCount:    number
  sessionCount: number
  priced:       boolean
  byStudent:    StudentCost[]
  byDay:        DayCost[]
  byProvider:   ProviderCost[]
}

// Cost is attributed by the class's owning school year. The school rollup takes that
// year explicitly (schoolYearId) so viewing a non-ACTIVE year is correct; the class
// detail is keyed by classId alone (year-unique). No createdAt window — the class's
// year owns every one of its turns regardless of when the turn ran.
// `month` ("YYYY-MM", optional) narrows totals and tables to rows created that month;
// SchoolCostRollup.byMonth / authoring.byMonth always cover the whole year.
export interface GetSchoolCostRollupInput  { schoolId: string; schoolYearId: string; month?: string }
export type      GetSchoolCostRollupOutput = SchoolCostRollup
export interface GetClassCostRollupInput   { classId: string; month?: string }
export type      GetClassCostRollupOutput  = ClassCostDetail

// ─── Unit Assessment growth report (epic #160) ──────────────────────────────
// Response shape of the getUnitAssessmentGrowth callable
// (functions/src/unit-assessment-growth.ts). Like getDistrictRollup, the callable
// is the ONLY place these deltas are computed — it pages the phase-tagged
// AssessmentEvent rows for a (classId, assessmentGroupId) pair and diffs pre→post
// (never client-aggregated). `pre`/`post` are mean scores in [0,1]; null when that
// phase has no events yet. `delta = post − pre` (null if either side is missing).

// One standard's class-level pre→post movement.
export interface StandardGrowth {
  standardId:   string
  standardCode: string
  standardName: string
  pre:          number | null   // class mean pre-assessment score
  post:         number | null   // class mean post-assessment score
  delta:        number | null   // post − pre; null if either phase is missing
}

// One student's per-standard results + overall pre→post movement.
export interface StudentGrowth {
  studentUid:  string
  studentName: string | null
  pre:         number | null    // mean pre score across the unit's standards
  post:        number | null    // mean post score across the unit's standards
  delta:       number | null    // post − pre
  standards:   StandardGrowth[] // this student's per-standard breakdown
}

export interface UnitAssessmentGrowth {
  classId:           string
  assessmentGroupId: string
  generatedAt:       string          // ISO timestamp the report was computed
  standards:         StandardGrowth[] // class-level, one per unit standard
  students:          StudentGrowth[]  // per-student breakdown
}

export interface GetUnitAssessmentGrowthInput { classId: string; assessmentGroupId: string }
export type      GetUnitAssessmentGrowthOutput = UnitAssessmentGrowth

// ─── Practice Block results (epic #176) ─────────────────────────────────────
// Response shape of the getPracticeBlockResults callable
// (functions/src/practice-block-results.ts). Like the growth report, the callable
// is the ONLY place these are computed — it pages the AssessmentEvent rows for a
// (classId, lessonPlanId) pair (via the ["classId","lessonPlanId"] index) plus the
// class's StandardsMastery, and aggregates in memory. It splits results by the two
// block sections: PRACTICE (recorded but NOT counted toward mastery) and EXIT_TICKET
// (the graded checks that move StandardsMastery). `avgScore` is a mean in [0,1] using
// the schoolStaffOverrideScore when a teacher hand-corrected the AI score; null when
// the bucket has no answered items.

// A DOK bucket key. UNSPECIFIED holds legacy/unauthored items (dokLevel null) so they
// still surface in the breakdown rather than vanishing.
export type DokBucketKey = DokLevel | 'UNSPECIFIED'
export interface DokCell { count: number; avgScore: number | null }
export type DokBreakdown = Record<DokBucketKey, DokCell>

// One student's aggregate for a single section (PRACTICE or EXIT_TICKET).
export interface PracticeSectionResult {
  answered: number
  avgScore: number | null
  byDok:    DokBreakdown   // per-DOK count + avg score
}

// A student's mastery on one exit-ticket standard (the graded section only).
export interface StandardMasteryCell {
  standardId:    string
  standardCode:  string
  standardName:  string
  masteryScore:  number | null   // 0–1; null when no mastery row exists yet
  evidenceCount: number
}

// Why an answered question was recorded but not scored: a correct-but-unfinished step on a
// multi-part item, or a correct answer held back until the student shows work. Both are
// mastery-neutral and excluded from the section counts/averages.
export type PracticeUnscoredReason = 'IN_PROGRESS' | 'WORK_NOT_SHOWN'

// A single answered question, for the per-student drill-down.
export interface PracticeQuestionResult {
  standardId:      string
  standardCode:    string
  section:         BlockSection
  dokLevel:        DokLevel | null   // null on legacy/unauthored items → UI shows "—"
  prompt:          string
  studentResponse: string
  score:           number            // effective score (override ?? raw)
  unscored:        PracticeUnscoredReason | null  // non-null ⇒ listed but not counted
  createdAt:       string
}

export interface StudentPracticeResult {
  studentUid:  string
  studentName: string | null
  practice:    PracticeSectionResult   // NOT counted toward mastery
  exitTicket:  PracticeSectionResult   // counts toward mastery
  mastery:     StandardMasteryCell[]   // per-standard mastery for the exit-ticket standards
  questions:   PracticeQuestionResult[] // raw rows (both sections) for the drill-down
}

export interface PracticeBlockResults {
  classId:      string
  lessonPlanId: string
  generatedAt:  string   // ISO timestamp the report was computed
  // Standards assessed anywhere in the block (either section), for stable column labels.
  standards:    { standardId: string; standardCode: string; standardName: string }[]
  practice:     PracticeSectionResult   // class-wide roll-up, practice section
  exitTicket:   PracticeSectionResult   // class-wide roll-up, exit-ticket section
  students:     StudentPracticeResult[]
  // One row per practice problem / exit-ticket question (epic #500). Older deploys omit it.
  items?:       PracticeItemResult[]
  // Answers with no item index — recorded before it existed and not matched by the
  // backfill (scripts/backfill-block-item-index.ts). They count in the totals only.
  untaggedAnswers?: number
}

/**
 * Class results for one Practice Block item (epic #500), from events tagged with
 * `blockItemIndex` (+ `blockQuestionId` for a quiz question). "Passed" is a score ≥ 0.5,
 * the agent's own bar for clearing an item. Computed by `getPracticeBlockResults`.
 */
export interface PracticeItemResult {
  section:         'PRACTICE' | 'EXIT_TICKET'
  index:           number          // 0-based within the section (problems, or checkItems)
  questionId:      string | null   // the quiz question, for a quiz resource item
  prompt:          string
  dokLevel:        DokLevel | null
  reached:         number          // students with any recorded answer
  answered:        number          // students with a scored answer
  firstTryCorrect: number | null   // share of `answered` who passed on their first scored answer
  avgTries:        number | null   // mean scored answers until passing (all, if never)
  avgFinalScore:   number | null   // mean of each student's last scored answer
  unscored:        number          // in-progress / work-not-shown attempts
}

export interface GetPracticeBlockResultsInput { classId: string; lessonPlanId: string }
export type      GetPracticeBlockResultsOutput = PracticeBlockResults

// ─── Copy School Staff & Courses to a new School Year (epic #233) ────────────
// Input/output of the copyToSchoolYear callable (functions/src/copy-school-year.ts).
// An ED360_STAFF admin copies selected School Staff and Courses from a source school
// year into a target year in the SAME school, cascading each course's classes (shells,
// no rosters), curriculum (units + lesson plans + pacing) and re-embedded RAG chunks.
// Both years must belong to the same school and target !== source. The callable pages
// FK-dependent reads, remaps source→new ids, and re-embeds chunk content via Vertex —
// so it runs server-side (the generated client hooks can't re-embed or page cleanly).
// Progress is mirrored to ingestion_jobs/copy-school-year-{targetSchoolYearId}.
export interface CopyToSchoolYearInput {
  sourceSchoolYearId: string
  targetSchoolYearId: string
  staffUids:          string[]   // SchoolYearUser (SCHOOL_STAFF) members to roll over
  courseIds:          string[]   // source-year courses to cascade-copy
}

export interface CopyToSchoolYearOutput {
  coursesCopied:     number
  classesCopied:     number
  unitsCopied:       number
  lessonPlansCopied: number
  chunksCopied:      number
  staffCopied:       number
  skipped:           string[]   // course names skipped (already exist in target)
  warnings:          string[]   // non-fatal notes (e.g. unmatched academic periods)
}

// ─── Math delimiter protection ──────────────────────────────────────────────

const MATH_DOLLAR_SENTINEL = String.fromCharCode(0xe000) // Private-Use sentinel for a protected literal `$`

// A `$` glued to a decimal or thousands-grouped money amount ($2.50, $1,000,
// $1,000.00), terminated by a boundary. Deliberately does NOT match a bare integer
// ($4): a bare `$N` is either currency (left unpaired → survives as a literal `$`)
// OR the OPENING delimiter of an integer-leading math span ($7 - 3 = 4$). Letting
// span-pairing decide is the whole fix — an earlier bare-integer currency rule stole
// the opener and orphaned the closing `$`, so "$7 - 3 = 4$" rendered as literal text.
const MATH_MONEY_RE = /\$(?:\d{1,3}(?:,\d{3})+(?:\.\d{2})?|\d+\.\d{2})(?=[\s)\]"',;:!?]|$)/g

// ─── Math tokenizer (KaTeX rendering, epic #290) ────────────────────────────
// The notation contract everywhere (lesson bodies, practice blocks, quizzes, tutor
// chat) is LaTeX inside `$…$`, with `$$…$$` as display math. `splitMath` is the ONE
// place that decides which `$` is a delimiter, so every renderer (MathText,
// MathMarkdown, the TipTap body migration) agrees. Currency is protected the same way
// as `normalizeMathText`: `\$` and grouped/decimal money ($2.50, $1,000) never pair,
// and a closing `$` immediately followed by a digit is not a closer ("$4 and $5").

export type MathSegment =
  | { kind: 'text'; value: string }
  | { kind: 'math'; value: string; display: boolean }

/** Length-preserving currency mask: the `$` of `\$` and of money amounts becomes the
 *  sentinel, so scan indices line up with the original string. */
function maskCurrency(input: string): string {
  return input
    .replace(/\\\$/g, '\\' + MATH_DOLLAR_SENTINEL)
    .replace(MATH_MONEY_RE, (m, offset: number, whole: string) => {
      // "$10,000 \times 0.10 = 1,000$" is math, not money: a LaTeX command before the
      // next `$` on the same line means this dollar opens a span.
      const rest = whole.slice(offset + m.length)
      const next = rest.search(/[$\n]/)
      if (next >= 0 && rest[next] === '$' && /\\[a-zA-Z]/.test(rest.slice(0, next))) return m
      // "$0.45 > 0.40$" is math too: a relation or operator before a `$` that closes
      // (not another price — "$2.50 + $3.00" — so not followed by a digit), no prose.
      const between = rest.slice(0, next)
      if (next >= 0 && rest[next] === '$' && !/\d/.test(rest[next + 1] ?? '')
        && /[=<>≤≥≈+×÷−]/.test(between) && !/[a-zA-Z]{2,}/.test(between)) return m
      return MATH_DOLLAR_SENTINEL + m.slice(1)
    })
}

/** Tidy one LaTeX expression for KaTeX: trim, and brace thousands separators —
 *  in math mode a bare comma is punctuation and renders "10,000" as "10, 000". */
export function normalizeLatex(tex: string): string {
  return tex.trim().replace(/(\d),(?=\d{3}(?!\d))/g, '$1{,}')
}

/** Two plain words in a row (outside \text{…}-style commands) mean a span swallowed
 *  prose — e.g. "cost $12. Identify the variables: pounds ($x$)" pairing the price's
 *  `$` with the next span's opener. LaTeX math never runs words together like that. */
function looksLikeProse(body: string): boolean {
  const bare = body.replace(/\\[a-zA-Z]+\s*\{[^{}]*\}/g, ' ').replace(/\\[a-zA-Z]+/g, ' ')
  // Lowercase only: vertex labels like "ABC \sim DEF" are math, "the variables" is prose.
  return /\b[a-z]{3,}\s+[a-z]{3,}\b/.test(bare)
}

type MathScan = { segments: MathSegment[]; openAt: number }

function scanMath(input: string): MathScan {
  const s = maskCurrency(input)
  const segments: MathSegment[] = []
  let openAt = -1
  const pushText = (t: string) => {
    if (!t) return
    // A LaTeX "\%" that landed outside $…$ ("…0.7 and 70\%.") is just a percent sign (#355).
    const value = t.split('\\' + MATH_DOLLAR_SENTINEL).join('$').split(MATH_DOLLAR_SENTINEL).join('$').replace(/\\%/g, '%')
    const last = segments[segments.length - 1]
    if (last?.kind === 'text') last.value += value
    else segments.push({ kind: 'text', value })
  }
  let i = 0
  let textStart = 0
  while (i < s.length) {
    if (s[i] !== '$') { i++; continue }
    const display = s[i + 1] === '$'
    const open = display ? 2 : 1
    let close = -1
    for (let j = i + open; j < s.length; j++) {
      if (s[j] !== '$') continue
      if (display) {
        if (s[j + 1] === '$') { close = j; break }
        continue
      }
      // Inline: the FIRST `$` after the opener decides. It closes unless it's followed
      // by a digit, or preceded by whitespace when the opener isn't (padded "$ x $" is
      // fine); otherwise the opener was a literal dollar — so "costs $5 more … total
      // $23." never pairs across the prose.
      // A closer after whitespace is also fine when an operator precedes it — the
      // fill-in form "$k = $ ____" ends the expression at "=".
      const padded = /\s/.test(s[i + open] ?? '')
      const afterOperator = /[=+\-−<>≤≥×÷]\s+$/.test(s.slice(i + open, j))
      if ((padded || afterOperator || !/\s/.test(s[j - 1])) && !/\d/.test(s[j + 1] ?? '') && !looksLikeProse(s.slice(i + open, j))) close = j
      break
    }
    const body = close >= 0 ? s.slice(i + open, close) : ''
    if (close < 0 || !body.trim()) {
      // Unpaired (or empty) — a literal dollar, e.g. "$4 each".
      if (close < 0 && openAt < 0) openAt = i
      i += open
      continue
    }
    pushText(s.slice(textStart, i))
    // `$$…$$` is display math only when it stands on its own line; inside a sentence
    // ("Evaluate: $$x^2$$.") it renders inline so the sentence isn't broken apart.
    const ownLine = /(^|\n)[ \t]*$/.test(s.slice(0, i)) && /^[ \t]*(\n|$)/.test(s.slice(close + open))
    segments.push({ kind: 'math', value: normalizeLatex(body.split(MATH_DOLLAR_SENTINEL).join('$')), display: display && ownLine })
    i = close + open
    textStart = i
  }
  pushText(s.slice(textStart))
  return { segments, openAt }
}

/** Split a string into plain-text and math segments. Text segments carry literal `$`
 *  for currency / unpaired dollars (escapes resolved); math segments carry the raw
 *  LaTeX without delimiters. */
export function splitMath(input: string): MathSegment[] {
  if (!input) return []
  if (input.indexOf('$') === -1) return [{ kind: 'text', value: input }]
  return scanMath(input).segments
}

/** True when the string contains at least one math span. */
export function hasMath(input: string): boolean {
  return !!input && input.indexOf('$') !== -1 && splitMath(input).some((x) => x.kind === 'math')
}

/** Rewrite a Markdown string so remark-math sees exactly the spans `splitMath` found:
 *  every literal dollar is escaped (`\$`), inline spans are re-delimited as `$…$`, and
 *  own-line display spans become a fenced `$$` block. */
export function toMathMarkdown(input: string): string {
  if (!input || input.indexOf('$') === -1) return input
  return splitMath(input)
    .map((x) => (x.kind === 'text' ? x.value.replace(/\$/g, '\\$') : x.display ? `\n$$\n${x.value}\n$$\n` : `$${x.value}$`))
    .join('')
}

/** While text is still streaming in, cut it at the first `$` that opens a span whose
 *  closer hasn't arrived yet, so half-written LaTeX never flashes on screen. (A
 *  currency `$4` is held back too until the stream ends — harmless.) */
export function holdBackOpenMath(input: string): string {
  if (!input || input.indexOf('$') === -1) return input
  const { openAt } = scanMath(input)
  return openAt < 0 ? input : input.slice(0, openAt)
}


// ── Narration helpers (epic #318 Phase 4) ─────────────────────────────────────

/** The authored texts of a segment that are read aloud: the hook, each I-do text point, the recap. */
export function narratedTexts(seg: LessonSegment | LessonNode): { key: string; text: string }[] {
  if (seg.type === 'hook') return [{ key: 'hook', text: seg.prompt }]
  if (seg.type === 'recap') return [{ key: 'recap', text: seg.summary }]
  if (seg.type === 'instruct') {
    return seg.points.flatMap((p, i) => (isTextPracticeItem(p) && p.text.trim() ? [{ key: `p${i}`, text: p.text }] : []))
  }
  return []
}

/** Stable short hash (FNV-1a, 32-bit) of the words a clip speaks. */
export function narrationHash(text: string): string {
  let h = 0x811c9dc5
  const s = speakableText(text)
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return (h >>> 0).toString(16).padStart(8, '0')
}

/** The clip that reads `text` at `key`, if one exists and is not stale. */
export function freshNarration(seg: LessonSegment | LessonNode, key: string, text: string): NarrationClip | undefined {
  const hash = narrationHash(text)
  return seg.narration?.find((c) => c.key === key && c.hash === hash)
}

/** Narration coverage for the teacher: narrated parts with an up-to-date clip, out of all. */
export function narrationStatus(script: LessonScriptV1 | LessonScriptV2): { fresh: number; total: number } {
  let fresh = 0
  let total = 0
  for (const seg of scriptNodes(script)) {
    for (const { key, text } of narratedTexts(seg)) {
      total++
      if (freshNarration(seg, key, text)) fresh++
    }
  }
  return { fresh, total }
}

const SPOKEN_POWERS: Record<string, string> = { '2': ' squared', '3': ' cubed' }

/** Read a LaTeX math body aloud ("\frac{3}{4} - x^2" → "3/4 minus x squared"). */
export function speakableLatex(latex: string): string {
  let s = latex
  // Text-ish wrappers keep their content.
  s = s.replace(/\\(?:text|mathrm|mathbf|textbf|operatorname)\{([^{}]*)\}/g, '$1')
  s = s.replace(/\\left|\\right|\\,|\;|\\!|\\quad/g, ' ')
  // Innermost-first so nested fractions / roots read naturally.
  for (let i = 0; i < 6; i++) {
    const before = s
    s = s.replace(/\\[dt]?frac\{([^{}]*)\}\{([^{}]*)\}/g, (_, a: string, b: string) =>
      /^\s*-?\d+\s*$/.test(a) && /^\s*\d+\s*$/.test(b) ? ` ${a.trim()}/${b.trim()} ` : ` ${a} over ${b} `)
    s = s.replace(/\\sqrt\[([^\]]*)\]\{([^{}]*)\}/g, ' the $1 root of $2 ')
    s = s.replace(/\\sqrt\{([^{}]*)\}/g, ' the square root of $1 ')
    if (s === before) break
  }
  s = s.replace(/\^\{?\\circ\}?/g, ' degrees').replace(/\\degree/g, ' degrees')
  // A power of a parenthesized base is said as "the quantity …, squared" so (-2)^2 and
  // -2^2 don't sound alike.
  s = s.replace(/\(([^()]*)\)\s*(?:\^\{([^{}]*)\}|\^(\w))/g, (_, base: string, a?: string, b?: string) => {
    const p = (a ?? b ?? '').trim()
    return ` the quantity ${base}, ${(SPOKEN_POWERS[p] ?? ` to the power of ${p}`).trim()} `
  })
  s = s.replace(/\^\{([^{}]*)\}|\^(\w)/g, (_, a?: string, b?: string) => {
    const p = (a ?? b ?? '').trim()
    return SPOKEN_POWERS[p] ?? ` to the power of ${p}`
  })
  s = s.replace(/_\{([^{}]*)\}|_(\w)/g, (_, a?: string, b?: string) => ` sub ${a ?? b}`)
  s = s.replace(/\|([^|]+)\|/g, ' the absolute value of $1 ')
  const words: [RegExp, string][] = [
    [/\\(?:cdot|times)|×|·/g, ' times '],
    [/\\div|÷/g, ' divided by '],
    [/\\pm|±/g, ' plus or minus '],
    [/\\(?:leq|le)\b|≤/g, ' is less than or equal to '],
    [/\\(?:geq|ge)\b|≥/g, ' is greater than or equal to '],
    [/\\(?:neq|ne)\b|≠/g, ' is not equal to '],
    [/\\approx|≈/g, ' is about '],
    [/\\%|%/g, ' percent'],
    [/\\pi\b|π/g, ' pi '],
    [/\\theta\b/g, ' theta '],
    [/\\ldots|\\dots|\\cdots/g, ' and so on '],
  ]
  for (const [re, w] of words) s = s.replace(re, w)
  s = s.replace(/\\[a-zA-Z]+/g, ' ').replace(/[{}]/g, ' ')
  s = s.replace(/</g, ' is less than ').replace(/>/g, ' is greater than ').replace(/=/g, ' equals ')
  s = s.replace(/\+/g, ' plus ')
  // A minus sign after nothing, an operator word or an open paren is a negative sign.
  s = s.replace(/(^|\(|\[|\b(?:plus|minus|times|by|equals|than|to|of|or|over|quantity)\s*)\s*[-−]\s*/g, '$1 negative ')
  s = s.replace(/\s*[-−]\s*/g, ' minus ')
  return s.replace(/\s+/g, ' ').trim()
}

/** Plain, speakable text for TTS: math spans read aloud, Markdown marks removed. */
export function speakableText(text: string): string {
  return splitMath(text)
    .map((x) => (x.kind === 'math' ? ` ${speakableLatex(x.value)} ` : x.value))
    .join('')
    .replace(/\*\*|__|`|^#+\s*/gm, '')
    .replace(/(^|[\s(])[-−](?=\d)/g, '$1negative ')
    .replace(/[ \t]+/g, ' ')
    .replace(/ ([.,!?;:])/g, '$1')
    .trim()
}

/** Every illustration in a script (cap + teacher status). */
export function lessonImages(script: LessonScriptV1 | LessonScriptV2): LessonImage[] {
  return scriptNodes(script).flatMap((seg) => {
    if (seg.type === 'hook') return seg.image ? [seg.image] : []
    if (seg.type === 'instruct') return seg.points.flatMap((p) => (isImagePoint(p) ? [p.image] : []))
    return []
  })
}

// ─── Lesson script v2 graph helpers (epic #363) ──────────────────────────────
// Validation, v1 → v2 migration, and the traversal helpers the agent runtime, the
// teacher flow editor and the narration helpers share. Pure — no I/O. Kept in this file
// (not a sibling module): the agent's tsx runs load this package as CommonJS with only
// the ESM hook registered, so a relative import from here would not resolve.

/** Structural shape the graph helpers need (lets validation run before zod finishes). */
type GraphLike = Pick<LessonScriptV2, 'entryNodeId' | 'nodes' | 'edges'>

export interface LessonGraphIssue {
  path:    (string | number)[]
  message: string
}

// ─── Traversal ────────────────────────────────────────────────────────────────

export function nodeById(script: GraphLike, id: string): LessonNode | undefined {
  return script.nodes.find((n) => n.id === id)
}

export function outgoingEdges(script: GraphLike, id: string): LessonEdge[] {
  return script.edges.filter((e) => e.from === id)
}

/** Target of the first edge of `kind` leaving `id`, if any. */
export function edgeTarget(script: GraphLike, id: string, kind: LessonEdgeKind): string | undefined {
  return script.edges.find((e) => e.from === id && e.condition.kind === kind)?.to
}

/**
 * The route a student who gets everything right takes: from the entry node follow
 * `pass` (checks) else `default`, until no edge or a node repeats. Nodes off this path
 * (re-teach parts) are reached only from a failed check.
 */
export function mainPath(script: GraphLike): LessonNode[] {
  const path: LessonNode[] = []
  const seen = new Set<string>()
  let id: string | undefined = script.entryNodeId
  while (id && !seen.has(id)) {
    const node = nodeById(script, id)
    if (!node) break
    path.push(node)
    seen.add(id)
    id = (node.type === 'check' ? edgeTarget(script, id, 'pass') : undefined) ?? edgeTarget(script, id, 'default')
  }
  return path
}

export function isMainPathNode(script: GraphLike, id: string): boolean {
  return mainPath(script).some((n) => n.id === id)
}

/** The parts of either script version, for helpers that only read part content. */
export function scriptNodes(script: LessonScriptV1 | LessonScriptV2): (LessonSegment | LessonNode)[] {
  return script.version === 1 ? script.segments : script.nodes
}

/**
 * How a node is named on screen: the learning target it serves (headline), what the
 * tutor is doing (action chip), and the teacher's optional title (sub-label).
 */
export function nodeDisplayName(node: LessonNode, script: GraphLike): { headline: string; action: string; title?: string } {
  return {
    headline: node.objective ?? node.concept,
    action:   isMainPathNode(script, node.id) ? LESSON_SEGMENT_LABELS[node.type] : 'Re-teach',
    ...(node.title ? { title: node.title } : {}),
  }
}

// ─── Validation ───────────────────────────────────────────────────────────────

const CHECK_ONLY: LessonEdgeKind[] = ['pass', 'fail', 'exhausted']
const SINGLETON: LessonEdgeKind[] = ['default', 'pass', 'exhausted', 'return']

/** Gated edges (Phase F): taken on a finished node when a signal is below a threshold. */
export const GATED_EDGE_KINDS: LessonEdgeKind[] = ['score_below', 'mastery_below']
export function isGatedEdge(e: LessonEdge): boolean {
  return GATED_EDGE_KINDS.includes(e.condition.kind)
}

/** How many times a budgeted edge (`fail` or gated) may be taken. */
export function edgeBudget(e: LessonEdge): number {
  const c = e.condition
  return c.kind === 'fail' || c.kind === 'score_below' || c.kind === 'mastery_below' ? c.maxTimes ?? 1 : 0
}

/**
 * Structural rules for a v2 graph. Every issue carries a `['nodes', i, …]` /
 * `['edges', i, …]` path (or `['entryNodeId']`) so the editor can map it to a node.
 */
export function validateLessonGraph(script: GraphLike): LessonGraphIssue[] {
  const issues: LessonGraphIssue[] = []
  const add = (path: (string | number)[], message: string) => issues.push({ path, message })

  const nodeIndex = new Map<string, number>()
  script.nodes.forEach((n, i) => {
    if (nodeIndex.has(n.id)) add(['nodes', i, 'id'], `duplicate node id "${n.id}"`)
    else nodeIndex.set(n.id, i)
  })
  const edgeIds = new Set<string>()
  script.edges.forEach((e, i) => {
    if (edgeIds.has(e.id)) add(['edges', i, 'id'], `duplicate edge id "${e.id}"`)
    edgeIds.add(e.id)
  })

  if (!nodeIndex.has(script.entryNodeId)) add(['entryNodeId'], `entry node "${script.entryNodeId}" does not exist`)

  // Edges whose endpoints both exist; the rest are reported once and ignored below.
  const live: { edge: LessonEdge; i: number; from: LessonNode; to: LessonNode }[] = []
  script.edges.forEach((edge, i) => {
    const fi = nodeIndex.get(edge.from)
    const ti = nodeIndex.get(edge.to)
    if (fi === undefined) add(['edges', i, 'from'], `edge "${edge.id}" starts at a missing node "${edge.from}"`)
    if (ti === undefined) add(['edges', i, 'to'], `edge "${edge.id}" points at a missing node "${edge.to}"`)
    if (fi !== undefined && ti !== undefined) live.push({ edge, i, from: script.nodes[fi], to: script.nodes[ti] })
  })

  for (const type of ['practice', 'exit_ticket', 'recap'] as const) {
    const at = script.nodes.flatMap((n, i) => (n.type === type ? [i] : []))
    if (at.length > 1) add(['nodes', at[1]], `at most one ${type} part`)
  }

  const kindCount = new Map<string, number>()
  for (const { edge, i, from, to } of live) {
    const kind = edge.condition.kind
    if (CHECK_ONLY.includes(kind) && from.type !== 'check') add(['edges', i, 'condition'], `a "${kind}" edge can only leave a check`)
    if (kind === 'score_below' && from.type !== 'exit_ticket') add(['edges', i, 'condition'], 'a "score below" edge can only leave the exit ticket')
    // A check moves on by pass / fail and practice hands straight to the exit ticket, so a
    // gate there would never be evaluated.
    if (kind === 'mastery_below' && (from.type === 'check' || from.type === 'practice')) add(['edges', i, 'condition'], `a "mastery below" edge can't leave a ${from.type === 'check' ? 'check' : 'practice'} part`)
    if (edge.from === edge.to && kind !== 'fail') add(['edges', i, 'to'], `only a "fail" edge may point back at its own part`)
    if (from.type === 'recap') add(['edges', i, 'from'], 'the wrap-up ends the lesson and has no next part')
    if (kind === 'return') {
      const sentBy = live.filter((x) => x.edge.condition.kind === 'fail' && x.edge.to === edge.from && x.edge.from !== edge.from)
      if (from.type !== 'instruct' || sentBy.length === 0) {
        add(['edges', i, 'condition'], 'a "return" edge can only leave a teach part that a missed check sends students to')
      } else if (!sentBy.some((x) => x.edge.from === edge.to)) {
        add(['edges', i, 'to'], `"return" must point back at a check that sends students here`)
      }
      if (to.type !== 'check') add(['edges', i, 'to'], '"return" must point at a check')
    }
    if (SINGLETON.includes(kind)) {
      const key = `${edge.from}\u0000${kind}`
      const n = (kindCount.get(key) ?? 0) + 1
      kindCount.set(key, n)
      if (n === 2) add(['edges', i, 'condition'], `part "${edge.from}" has more than one "${kind}" edge`)
    }
  }

  // The exit ticket continues the practice block's cursor: practice hands straight to it.
  const practice = script.nodes.find((n) => n.type === 'practice')
  const exit = script.nodes.find((n) => n.type === 'exit_ticket')
  if (practice && exit) {
    const pIdx = nodeIndex.get(practice.id)!
    if (edgeTarget(script, practice.id, 'default') !== exit.id) add(['nodes', pIdx], 'practice must go straight to the exit ticket')
    for (const { edge, i } of live) {
      if (edge.to === exit.id && !(edge.from === practice.id && edge.condition.kind === 'default')) {
        add(['edges', i, 'to'], 'only practice may lead into the exit ticket')
      }
    }
  }

  // Everything reachable from the entry node.
  if (nodeIndex.has(script.entryNodeId)) {
    const reached = new Set([script.entryNodeId])
    const queue = [script.entryNodeId]
    while (queue.length) {
      const id = queue.shift()!
      for (const { edge } of live) {
        if (edge.from === id && !reached.has(edge.to)) {
          reached.add(edge.to)
          queue.push(edge.to)
        }
      }
    }
    script.nodes.forEach((n, i) => {
      if (!reached.has(n.id)) add(['nodes', i], `part "${n.id}" can never be reached`)
    })
  }

  // The forward subgraph (default / pass / exhausted) must be acyclic so every lesson ends.
  const forward = live.filter((x) => ['default', 'pass', 'exhausted'].includes(x.edge.condition.kind))
  const state = new Map<string, 1 | 2>() // 1 = on stack, 2 = done
  const cycleAt = new Set<number>()
  const visit = (id: string) => {
    state.set(id, 1)
    for (const x of forward) {
      if (x.edge.from !== id) continue
      const s = state.get(x.edge.to)
      if (s === 1) cycleAt.add(x.i)
      else if (s === undefined) visit(x.edge.to)
    }
    state.set(id, 2)
  }
  for (const n of script.nodes) if (!state.has(n.id)) visit(n.id)
  for (const i of cycleAt) add(['edges', i, 'to'], 'this edge loops back — only "fail" and "return" edges may revisit a part')

  // Every main-path part but the last says where to go next.
  if (nodeIndex.has(script.entryNodeId)) {
    const path = mainPath(script)
    path.slice(0, -1).forEach((n) => {
      if (!edgeTarget(script, n.id, 'default')) add(['nodes', nodeIndex.get(n.id)!], `part "${n.id}" has no "then" edge`)
    })
  }

  // Objective groups: keyed by an existing teach part, members connected to each other.
  const groups = new Map<string, string[]>()
  script.nodes.forEach((n, i) => {
    if (!n.objectiveId) return
    const owner = nodeById(script, n.objectiveId)
    if (!owner || owner.type !== 'instruct') {
      add(['nodes', i, 'objectiveId'], `objectiveId "${n.objectiveId}" must name a teach part`)
      return
    }
    groups.set(n.objectiveId, [...(groups.get(n.objectiveId) ?? []), n.id])
  })
  for (const [, members] of groups) {
    if (members.length < 2) continue
    const inGroup = new Set(members)
    const reached = new Set([members[0]])
    const queue = [members[0]]
    while (queue.length) {
      const id = queue.shift()!
      for (const { edge } of live) {
        const other = edge.from === id ? edge.to : edge.to === id ? edge.from : undefined
        if (other && inGroup.has(other) && !reached.has(other)) {
          reached.add(other)
          queue.push(other)
        }
      }
    }
    for (const id of members) {
      if (!reached.has(id)) add(['nodes', nodeIndex.get(id)!, 'objectiveId'], `part "${id}" is not connected to the rest of its learning-target group`)
    }
  }

  return issues
}

// ─── v1 → v2 ──────────────────────────────────────────────────────────────────

/**
 * Rewrite a v1 script (ordered segments + `reteachSegmentId`) as a v2 graph. Node ids are
 * preserved (narration clips and whiteboard entries key on them). The main path becomes
 * a chain of `default` edges ending at the recap (moved last if a teacher reordered it); a check's authored re-teach becomes
 * `fail{maxTimes: maxReteach}` + `return`; a check without one becomes a live re-teach
 * (self-loop `fail`) unless `maxReteach` is 0. Re-teach parts nothing points at are dropped.
 */
export function migrateLessonScriptV1toV2(v1: LessonScriptV1): LessonScriptV2 {
  const isMain = (s: LessonSegment) => !(s.type === 'instruct' && s.reteachOnly)
  const byId = new Map(v1.segments.map((s) => [s.id, s]))

  // The recap always ends the graph. v1 let a teacher reorder it mid-lesson (the old
  // cursor then ended the lesson there); moving it last keeps every authored part instead
  // of dropping the ones after it on the next save.
  const ordered = v1.segments.filter(isMain)
  const main = [...ordered.filter((s) => s.type !== 'recap'), ...ordered.filter((s) => s.type === 'recap')]
  const mainIds = new Set(main.map((s) => s.id))

  const edges: LessonEdge[] = []
  const edge = (from: string, to: string, condition: LessonEdge['condition']) =>
    edges.push({ id: `e-${edges.length + 1}`, from, to, condition })

  for (let i = 0; i + 1 < main.length; i++) edge(main[i].id, main[i + 1].id, { kind: 'default' })

  const reteachTargets = new Set<string>()
  const returned = new Set<string>()
  for (const s of main) {
    if (s.type !== 'check' || s.maxReteach === 0) continue
    const target = s.reteachSegmentId ? byId.get(s.reteachSegmentId) : undefined
    if (target?.type === 'instruct') {
      edge(s.id, target.id, { kind: 'fail', maxTimes: s.maxReteach ?? 1 })
      reteachTargets.add(target.id)
      // One `return` per re-teach part (the runtime returns via cursor.returnTo anyway);
      // a main-path target already has its own `default`.
      if (!mainIds.has(target.id) && !returned.has(target.id)) {
        edge(target.id, s.id, { kind: 'return' })
        returned.add(target.id)
      }
    } else {
      edge(s.id, s.id, { kind: 'fail', ...(s.maxReteach ? { maxTimes: s.maxReteach } : {}) })
    }
  }

  // Concept trios: a main-path teach part, the check right after it, and that check's
  // off-path re-teach share objectiveId = the teach part's id.
  const objectiveOf = new Map<string, string>()
  main.forEach((s, i) => {
    const next = main[i + 1]
    if (s.type !== 'instruct' || next?.type !== 'check') return
    objectiveOf.set(s.id, s.id)
    objectiveOf.set(next.id, s.id)
    const target = edges.find((e) => e.from === next.id && e.condition.kind === 'fail' && e.to !== next.id)?.to
    if (target && !mainIds.has(target) && !objectiveOf.has(target)) objectiveOf.set(target, s.id)
  })

  const nodes: LessonNode[] = v1.segments
    .filter((s) => mainIds.has(s.id) || reteachTargets.has(s.id))
    .map((s) => {
      const objectiveId = objectiveOf.get(s.id)
      const withGroup = objectiveId ? { objectiveId } : {}
      if (s.type === 'instruct') {
        const { reteachOnly: _r, ...rest } = s
        return { ...rest, ...withGroup }
      }
      if (s.type === 'check') {
        const { reteachSegmentId: _t, maxReteach: _m, ...rest } = s
        return { ...rest, ...withGroup }
      }
      return { ...s, ...withGroup }
    })

  return {
    version:     2,
    objective:   v1.objective,
    standardIds: v1.standardIds,
    ...(v1.estimatedMinutes !== undefined ? { estimatedMinutes: v1.estimatedMinutes } : {}),
    entryNodeId: main[0]?.id ?? v1.segments[0].id,
    nodes,
    edges,
  }
}

/**
 * Read a stored `lessonScript` of either version as v2 (v1 migrated). Null when absent or
 * invalid — callers treat that as "no script", same as the old `safeParse` checks.
 */
export function parseLessonScript(raw: unknown): LessonScriptV2 | null {
  if (raw == null) return null
  const v2 = lessonScriptV2Schema.safeParse(raw)
  if (v2.success) return v2.data
  const v1 = lessonScriptV1Schema.safeParse(raw)
  if (!v1.success) return null
  const migrated = lessonScriptV2Schema.safeParse(migrateLessonScriptV1toV2(v1.data))
  return migrated.success ? migrated.data : null
}

// ─── Standards coverage (epic #453) ───────────────────────────────────────────
// How well a course's lesson plans cover each standard, computed server-side by the
// agent's `standards.coverage` route. Only STANDARD lessons WITH content count (a
// PLANNED stub makes a standard `light` at most); Unit Pre/Post items are reported
// separately; free-tutoring answers never affect the level.

export type CoverageLevel = 'none' | 'light' | 'covered'
export type CoverageItemSource = 'practice' | 'exitTicket' | 'scriptCheck' | 'scriptGuided' | 'interactive'
/**
 * Class view only, from the class's "Mark taught" (epic #647 — student answers never count):
 * `taught` = marked Taught or Partly taught; `behind` = the date passed and it is marked Not
 * taught or not marked yet; `before_marking` = dated before the school year's marking start
 * (no mark state); `upcoming` = today or later with no mark.
 */
export type LessonClassState = 'taught' | 'behind' | 'upcoming' | 'skipped' | 'unscheduled' | 'before_marking'

/** light ⇐ at most `lightMaxLessons` lessons with content AND fewer than `lightMinItems` tagged items. */
export const COVERAGE_THRESHOLDS = { lightMaxLessons: 1, lightMinItems: 4 } as const

export interface CoverageEvidence {
  answers: number
  students: number
  /** ISO timestamp of the latest answer. */
  lastAt: string | null
}

export interface CoverageLesson {
  id: string
  title: string
  unitId: string
  sequence: number
  status: string
  kind: string
  estimatedMinutes: number | null
  /** Has a body, tutor block, lesson script or interactive resource. */
  hasContent: boolean
  /** Text items with no standard tag that couldn't be attributed. */
  untaggedItems: number
  /** Class view: the class's pacing date (ISO), else null. */
  date: string | null
  classState?: LessonClassState
}

export interface StandardCoverage {
  standardId: string
  standardCode: string
  standardName: string
  standardShortName: string | null
  description: string | null
  status: string
  /** Lowest-sequence unit holding the standard; null ⇒ in no unit. */
  primaryUnitId: string | null
  unitIds: string[]
  /** Every linked STANDARD lesson, in unit/sequence order (with or without content). */
  lessonIds: string[]
  /** Lessons with content (class view: minus skipped). Drives the level. */
  lessonCount: number
  publishedLessonCount: number
  /** Linked lessons with no content yet — listed, never counted. */
  emptyLessonCount: number
  minutes: number
  items: { total: number; bySource: Record<CoverageItemSource, number> }
  untaggedItems: number
  /** Items on Unit Pre/Post assessments — not counted toward the level. */
  assessmentItems: number
  evidence: { lessons: CoverageEvidence; freeTutoring: CoverageEvidence }
  /** Class view only: counts of the standard's lessons by class state. */
  class?: Record<LessonClassState, number>
  level: CoverageLevel
}

export interface StandardsCoverageOutput {
  scope: 'course' | 'class'
  courseId: string
  classId: string | null
  today: string
  generatedAt: string
  summary: {
    total: number
    covered: number
    light: number
    none: number
    /** Covered/light but no published lesson. */
    unpublishedOnly: number
    /** No lesson with content, yet students practiced it in free tutoring. */
    practicedNotTaught: number
    /** Class view: lessons past their date with no student work. */
    behindLessons: number
    /** Class view: standards with ≥ 1 taught lesson. */
    taughtStandards: number
  }
  units: { unitId: string; title: string; sequence: number; periodName: string | null; standardIds: string[] }[]
  unassignedStandardIds: string[]
  lessons: Record<string, CoverageLesson>
  /** Class view: roster size, the "students reached" denominator. */
  enrolled: number | null
  thresholds: typeof COVERAGE_THRESHOLDS
  standards: StandardCoverage[]
}

// ─── Ask AI proposed actions (epic #391) ──────────────────────────────────────
// The assistant never writes anything itself: `propose_action` stores a proposal
// server-side (Firestore `assistantActions/{actionId}`) and returns this card; the
// teacher approves it (`classAssistant.executeAction`) or opens the editor instead.

export type AssistantActionType =
  | 'create_practice_block'
  | 'create_exit_quiz'
  | 'draft_lesson_script'
  | 'generate_unit_assessment'
  // Student-level actions from a "Clear with Beacon" plan (epic #575): built by
  // classOverview.proposePlan, never by the chat model.
  | 'grade_review'
  | 'acknowledge_alert'
  | 'assign_makeup'
  | 'extend_makeup'
  | 'assign_focus_practice'
  | 'extend_practice'

export interface AssistantActionCard {
  actionId: string
  type: AssistantActionType
  /** Button-sized verb phrase, e.g. "Create AI Practice Block". */
  label: string
  /** The lesson / unit the action targets. */
  targetTitle: string
  /** The target's page in the teacher app. */
  link: string
  /** The page plus `?open=…`, which opens the matching editor there. */
  editorLink: string
  /** Why the assistant suggested it (one sentence). */
  reason: string
  /** False ⇒ "Open in editor" only (e.g. an Exit Ticket quiz, until server save lands). */
  runnable: boolean
  /** The target already has this content — running asks to replace it (if `canReplace`). */
  existing: boolean
  canReplace: boolean
  /** Shown before running, e.g. a Unit Assessment that won't fit the unit's pacing window. */
  warning?: string
}

export interface ExecuteAssistantActionResult {
  /** `exists` ⇒ nothing changed (ask before replacing); `skipped` ⇒ nothing changed for another
   *  reason, e.g. no quiz question's answer key could be verified (Phase G). */
  status: 'created' | 'exists' | 'skipped'
  message: string
  link: string
  /** The change can be undone (`classAssistant.revertAction`) while the lesson is unedited. */
  revertible?: boolean
}

/**
 * Ask AI streaming (#405): one frame of `POST /api/assistant/stream`. Text is PROVISIONAL
 * until `done` — a `retract` drops everything streamed so far for this answer (the round
 * turned out to be a tool round, or the review step sent the model a correction), and
 * `done.answer` is the canonical reply, identical to `classAssistant.ask`'s.
 */
export type AssistantStreamEvent =
  /** A tool started: a human-readable status line ("Checking 5 lessons' readiness…"). */
  | { type: 'tool'; name: string; status: string }
  /** The model is composing (no tool running). */
  | { type: 'thinking' }
  /** A delta of the answer being written. */
  | { type: 'text'; text: string }
  /** Discard all provisional text for this answer. */
  | { type: 'retract' }
  | {
      type: 'done'
      answer: string
      rounds: number
      toolCalls: { name: string; ok: boolean }[]
      actions?: AssistantActionCard[]
    }
  | { type: 'error'; message: string }

/** A run of one or more approved actions (Phase G): executed in the background, polled. */
export interface AssistantJob {
  jobId: string
  status: 'queued' | 'running' | 'done' | 'failed'
  items: {
    actionId: string
    status: 'queued' | 'running' | 'done' | 'failed'
    result?: ExecuteAssistantActionResult
    error?: string
  }[]
}

// ─── Lesson Plans board (epic #474) ───────────────────────────────────────────
// The teacher Lesson Plans page: every unit of a course with each lesson's readiness
// (the Ask AI `get_lesson_readiness` rules), its date on a class's pacing, and exit
// ticket results — for the active class in the row, and per section in the details
// strip. Computed by the agent's `lessonPlan.board`; never aggregated client-side.

/** ready = teachable now; fix = written but something is missing; empty = only planned / no plan text. */
export type LessonReadinessLevel = 'ready' | 'fix' | 'empty'

export interface LessonReadinessChecks {
  /** The lesson plan body has text; 'na' for unit assessments (their content is the items). */
  plan: 'ok' | 'missing' | 'na'
  /** AI Practice Block (or, for a unit assessment, its items). */
  practice: 'ok' | 'missing' | 'invalid'
  /** An Exit Ticket quiz; 'na' for unit assessments. */
  exitTicket: 'ok' | 'missing' | 'na'
  /** Quiz answer keys (epic #283); 'na' when there is no quiz. */
  answerKeys: { status: 'ok' | 'review' | 'na'; needsReview: number }
  /** Teach with AI script — optional, never lowers the level. 'na' for unit assessments. */
  teachWithAi: 'ok' | 'review' | 'none' | 'invalid' | 'na'
}

// ─── Lesson delivery — "Mark taught" (epic #647) ──────────────────────────────
// Whether a class's lesson was taught is an explicit mark by the class teacher or a
// Department Head, never inferred from student answers. Stored per (class, lesson) and
// written only through the agent's lessonDelivery.* routes.

export type LessonDeliveryStatus = 'TAUGHT' | 'PARTLY_TAUGHT' | 'NOT_TAUGHT'

/**
 * A class's lesson as every page shows it: its mark, or why it has none. UPCOMING covers
 * today too — a lesson is only flagged UNMARKED once its day is over. BEFORE_MARKING:
 * dated before the school year's `taughtMarksFrom`, so never asked for.
 */
export type LessonDeliveryState = LessonDeliveryStatus | 'UNMARKED' | 'UPCOMING' | 'BEFORE_MARKING' | 'UNSCHEDULED'

/** Quick tags offered in the Mark taught dialog (free tags are allowed too). */
export const LESSON_DELIVERY_TAGS = ['Ran short', 'Needs re-teach', 'Great discussion'] as const

export const LESSON_DELIVERY_LABEL: Record<LessonDeliveryState, string> = {
  TAUGHT: 'Taught',
  PARTLY_TAUGHT: 'Partly taught',
  NOT_TAUGHT: 'Not taught',
  UNMARKED: 'Not marked',
  UPCOMING: 'Upcoming',
  BEFORE_MARKING: 'Before marking started',
  UNSCHEDULED: 'Not scheduled',
}

/** TAUGHT and PARTLY_TAUGHT count as taught (pacing, coverage); NOT_TAUGHT doesn't. */
export const isTaughtStatus = (s: LessonDeliveryStatus | LessonDeliveryState | null | undefined): boolean =>
  s === 'TAUGHT' || s === 'PARTLY_TAUGHT'

/** One stored mark, as `lessonDelivery.*` returns it. */
export interface LessonDeliveryMark {
  classId: string
  lessonPlanId: string
  status: LessonDeliveryStatus
  /** YYYY-MM-DD; null for NOT_TAUGHT. */
  taughtOn: string | null
  /** The lesson's date in the class when it was marked. */
  scheduledOn: string | null
  carryOver: boolean
  markedBy: { uid: string; name: string | null }
  /** Marked by a Department Head in a colleague's class. */
  markedByLead: boolean
  markedAt: string
  hasNotes: boolean
  /** Null when the caller may not read notes (only the class teacher, its Department Heads and ED360 staff may). */
  notes: string | null
  tags: string[] | null
}

/** A section's delivery for one lesson on the Lesson Plans board (never carries the notes). */
export interface SectionDelivery {
  state: LessonDeliveryState
  taughtOn: string | null
  markedByName: string | null
  markedByLead: boolean
  hasNotes: boolean
  carryOver: boolean
}

/**
 * The one rule for a lesson's delivery state in a class. A NOT_TAUGHT mark lapses once the
 * lesson is moved to another day (`scheduledOn` ≠ its date): the new day asks again.
 */
export function deliveryState(o: {
  mark: Pick<LessonDeliveryMark, 'status' | 'scheduledOn'> | null
  date: string | null
  today: string
  marksFrom: string | null
}): LessonDeliveryState {
  const { mark, date, today, marksFrom } = o
  if (mark && !(mark.status === 'NOT_TAUGHT' && mark.scheduledOn && date && mark.scheduledOn !== date)) return mark.status
  if (!date) return 'UNSCHEDULED'
  if (date >= today) return 'UPCOMING'
  if (marksFrom && date < marksFrom) return 'BEFORE_MARKING'
  return 'UNMARKED'
}

export interface LessonSectionResult {
  classId: string
  /** Exit ticket (or, for a unit assessment, that phase's) average 0–1; null when no answers. */
  avg: number | null
  /** Scored answers behind `avg`. */
  answered: number
  /** Students with at least one scored answer. */
  finished: number
  enrolled: number
  /** Marked Taught or Partly taught (epic #647) — `delivery.state`; never inferred from answers. */
  taught: boolean
  /** The explicit mark: state, taught date, who marked it (epic #647). */
  delivery: SectionDelivery
  /** Students have answered this lesson in the class — a hint ("mark taught?") only, never taught. */
  hasAnswers: boolean
}

export interface LessonBoardLesson {
  lessonId: string
  unitId: string
  title: string
  sequence: number
  kind: 'STANDARD' | 'UNIT_ASSESSMENT_PRE' | 'UNIT_ASSESSMENT_POST'
  status: LessonPlanStatus
  standardIds: string[]
  readiness: { level: LessonReadinessLevel; checks: LessonReadinessChecks; gaps: string[] }
  /** Date on the schedule scope's pacing (a class, or the course baseline); null if unplaced/excluded. */
  date: string | null
  /** Order within its unit for the schedule scope: a class's override, else the course sequence (epic #484). */
  order: number
  /** The schedule scope's marks for this lesson (epic #484); a baseline has no pins, overrides or exclusions. */
  schedule: { pinned: boolean; moved: boolean; excluded: boolean; unplaced: boolean }
  isToday: boolean
  /** The active class's tutoring window is open on this lesson. */
  practiceOpen: boolean
  /**
   * The row's result: the active class's, or with no active class the mean of the
   * sections that have answers (`sectionsAveraged`). `growth` (POST only) = post − pre.
   */
  result: { avg: number | null; finished: number; enrolled: number | null; sectionsAveraged: number; growth: number | null }
  /** A STANDARD lesson taught (active class, or any section) with no Exit Ticket quiz. */
  taughtWithoutExitTicket: boolean
  /** Every section of the course, in `sections` order. */
  bySection: LessonSectionResult[]
}

export interface LessonBoardUnit {
  unitId: string
  title: string
  sequence: number
  periodName: string | null
  standardIds: string[]
  /** Non-archived lessons in the schedule scope's order (excluded ones included). */
  lessonIds: string[]
  startDate: string | null
  endDate: string | null
  contentDays: number | null
  bufferDays: number | null
  /** Lessons that don't fit the unit's content days on this schedule. */
  unplacedLessonIds: string[]
  isCurrent: boolean
  counts: { lessons: number; ready: number; fix: number; empty: number }
  /** Mean of the row results of the unit's taught STANDARD lessons. */
  avg: number | null
  taughtLessons: number
}

export type LessonAttentionKind =
  | 'today_no_exit_ticket'
  | 'taught_no_exit_ticket'
  | 'answer_keys_review'
  | 'missing_practice_or_exit_ticket'
  | 'teach_with_ai_review'
  | 'planned_not_written'
  | 'unit_no_lessons'

export interface LessonAttentionItem {
  unitId: string
  lessonId: string | null
  date: string | null
  /** e.g. "3 keys", "no practice". */
  detail: string | null
  /** Due within 7 days, or already taught without an exit ticket. */
  urgent: boolean
  /** In the default "current unit onward" scope. */
  currentOnward: boolean
}

export interface LessonPlansBoardOutput {
  courseId: string
  classId: string | null
  today: string
  generatedAt: string
  /** Whose pacing the dates, marks and order follow (epic #484): a class, or the course baseline. */
  schedule: {
    scope: 'class' | 'baseline'
    classId: string | null
    label: string
    /** Days a lesson can be placed on for this scope (meeting days minus no-lesson days) — pin targets. */
    lessonDays: string[]
    /** Meeting days with no lessons allowed (e.g. Half Days) — shaded on the calendar. */
    noLessonDays: string[]
  } | null
  sections: { classId: string; label: string; teacherName: string | null; mine: boolean }[]
  standards: Record<string, { code: string; name: string }>
  units: LessonBoardUnit[]
  lessons: Record<string, LessonBoardLesson>
  attention: { groups: { kind: LessonAttentionKind; items: LessonAttentionItem[] }[] }
}

const sameBoardId = (a: string | null | undefined, b: string | null | undefined) =>
  !!a && !!b && a.replace(/-/g, '').toLowerCase() === b.replace(/-/g, '').toLowerCase()

/**
 * The one read of a lesson's per-section exit ticket spread (board details strip, My Week,
 * Ask AI's `get_lesson_plans_board`): `lesson` = low in every scored section (≥ 2, all
 * below 60%) — revise the lesson; `class` = only the active class is low (below the
 * developing band while another section is at 70%+) — re-teach there. Null otherwise.
 */
export function sectionVerdict(
  lesson: Pick<LessonBoardLesson, 'kind' | 'bySection'>,
  activeClassId: string | null,
  activeLabel: string | null,
): { tone: 'lesson' | 'class'; lead: string; rest: string } | null {
  if (lesson.kind !== 'STANDARD') return null
  const scored = lesson.bySection.filter((s) => s.avg != null)
  if (scored.length >= 2 && scored.every((s) => s.avg! < 0.6)) {
    return { tone: 'lesson', lead: 'Low in every section.', rest: 'Worth revising the lesson or its practice.' }
  }
  const mine = activeClassId ? scored.find((s) => sameBoardId(s.classId, activeClassId)) : undefined
  if (mine && mine.avg! < MASTERY_BANDS.developing && scored.some((s) => s !== mine && s.avg! >= 0.7)) {
    return { tone: 'class', lead: `Only ${activeLabel ?? 'this class'} is low.`, rest: 'The lesson works elsewhere; re-teach or assign make-ups here.' }
  }
  return null
}

// ─── Class overview → Needs attention (epic #575) ─────────────────────────────
// One ranking of the students who need their teacher, shared by the teacher app's
// Class overview card and Beacon's "Clear with Beacon" planner (agent), so the plan
// is built from exactly the list the teacher sees. Pure: callers fetch the inputs
// (live sessions + alerts from the Firestore mirrors, reviews, session history,
// mastery rows, open work) and pass them in.

/** Teacher-facing names for anomaly (alert) types. */
export const ANOMALY_LABEL: Record<AnomalyType, string> = {
  STALLED: 'Student Stalled',
  LOW_MASTERY_DROP: 'Mastery Dropping',
  RAPID_PROGRESSION: 'Rapid Progress',
  LOW_CONFIDENCE_SCORE: 'Low Confidence Score',
  SENSITIVE_TOPIC: 'Sensitive Topic',
  LONG_IDLE: 'Long Idle',
  RETEACH_EXHAUSTED: 'Check Still Missed After Re-teach',
}

export type AttentionReasonKind =
  | 'escalated_live'
  | 'alert'
  | 'rapid_progress'
  | 'makeup_overdue'
  | 'practice_overdue'
  | 'review_pending'
  | 'mastery_declining'
  | 'below_cutoff'
  | 'inactive'

/** Reasons worked out from scores or activity: assigned work can put them "in hand",
 *  but only new answers make them go away. */
export const MASTERY_REASON_KINDS: readonly AttentionReasonKind[] = ['mastery_declining', 'below_cutoff', 'inactive']

export type AttentionTone = 'error' | 'warning' | 'info' | 'secondary' | 'success' | 'default'

export type AttentionReason = {
  kind: AttentionReasonKind
  tone: AttentionTone
  text: string
  /** The records the reason is about, so Beacon can act on exactly them. */
  refs: {
    threadIds?: string[]
    alertIds?: string[]
    alertTypes?: AnomalyType[]
    reviewEventIds?: string[]
    lessonPlanIds?: string[]
    standardIds?: string[]
  }
  /** Set when open work covers this reason (mastery reasons only): what it is. */
  inHand?: string
}

export type AttentionAction =
  | { kind: 'session'; threadId: string; live: boolean; alert?: string }
  | { kind: 'reviews' }
  | { kind: 'student' }

export type AttentionItem = {
  uid: string
  name: string
  score: number
  reasons: AttentionReason[]
  detail: string | null
  action: AttentionAction
  actionLabel: string
  /** Every reason is covered by open work: the card folds the row into "In hand". */
  inHand: boolean
}

export type AttentionLiveSession = { threadId: string; studentUid: string; escalated: boolean; currentTopic: string }
export type AttentionAlert = { id: string; threadId: string; studentUid: string; type: string; triggeredAt: string }
export type AttentionReview = { eventId: string; studentUid: string; createdAt: string }
export type AttentionSessionSummary = { lastStartedAt: string | null; sessionCount: number }

/** Open (or recently finished) work assigned to a student: a make-up lesson or focus practice. */
export type AttentionWork = {
  kind: 'makeup' | 'practice'
  studentUid: string
  /** The make-up's lesson, or null for focus practice. */
  lessonPlanId: string | null
  title: string
  /** Standards the work practises (a lesson's aligned standards; the practice's skill). */
  standardIds: string[]
  status: 'ASSIGNED' | 'IN_PROGRESS' | 'COMPLETED'
  assignedAt: string | null
  dueDate: string | null
}

/** One student's mastery row for a standard, as the attention model needs it. */
export type AttentionMasteryRow = {
  standardId: string
  masteryScore: number
  trend: string
  evidenceCount: number
  lastAssessedAt?: string | null
}

export type MasterySignals = {
  /** standardId → score (0–1). */
  scores: Map<string, number>
  /** Standards trending DECLINING. */
  declining: string[]
  /** Standards below the developing cut-off with settled evidence (never early evidence). */
  below: string[]
  lastAssessedAt: Map<string, string>
}

const attnId = (id: string) => id.replace(/-/g, '').toLowerCase()

/** One student's signals from their mastery rows — the teacher heatmap and Beacon share it. */
export function masterySignals(rows: AttentionMasteryRow[]): MasterySignals {
  const lastAssessedAt = new Map<string, string>()
  for (const r of rows) if (r.lastAssessedAt) lastAssessedAt.set(r.standardId, r.lastAssessedAt)
  return {
    scores: new Map(rows.map((r) => [r.standardId, r.masteryScore])),
    declining: rows.filter((r) => r.trend === 'DECLINING').map((r) => r.standardId),
    // Early-evidence standards never count as "below": one wrong first answer is not a gap.
    below: rows.filter((r) => r.masteryScore < MASTERY_BANDS.developing && !isEarlyEvidence(r.evidenceCount)).map((r) => r.standardId),
    lastAssessedAt,
  }
}

/** Days of no session before a student reads as inactive. */
export const ATTENTION_STALE_DAYS = 7
/** Work assigned longer ago than this no longer keeps a student "in hand". */
export const IN_HAND_DAYS = 14

const daysBetween = (iso: string, now: number) => Math.floor((now - new Date(iso).getTime()) / 86_400_000)

/** The open work that covers a mastery reason, if any. */
export function workCovering(
  reason: Pick<AttentionReason, 'kind' | 'refs'>,
  work: AttentionWork[],
  signals: MasterySignals | undefined,
  today: string,
  now: number,
): AttentionWork | null {
  if (!MASTERY_REASON_KINDS.includes(reason.kind)) return null
  const open = work.filter((w) =>
    w.status !== 'COMPLETED' &&
    !(w.dueDate && w.dueDate.slice(0, 10) < today) &&
    !(w.assignedAt && daysBetween(w.assignedAt, now) > IN_HAND_DAYS))
  if (reason.kind === 'inactive') return open[0] ?? null
  const wanted = new Set((reason.refs.standardIds ?? []).map(attnId))
  for (const w of open) {
    const covered = w.standardIds.filter((id) => wanted.has(attnId(id)))
    if (!covered.length) continue
    // The score kept falling after the work was assigned: new answers since then and the
    // standard still declining / below — the teacher needs to look again.
    const stillFalling = covered.some((id) => {
      const at = [...(signals?.lastAssessedAt ?? new Map<string, string>()).entries()].find(([k]) => attnId(k) === attnId(id))?.[1]
      return !!at && !!w.assignedAt && at > w.assignedAt && reason.kind === 'mastery_declining'
    })
    if (!stillFalling) return w
  }
  return null
}

function workLabel(w: AttentionWork): string {
  const due = w.dueDate ? `, due ${w.dueDate.slice(0, 10)}` : ''
  return `${w.kind === 'makeup' ? 'Make-up' : 'Practice'}: ${w.title}${due}`
}

/**
 * Rank students who need the teacher, most urgent first. Each signal adds to a student's
 * score; the strongest one also picks the row's action link. Rows whose every reason is
 * covered by open work come back with `inHand: true` (sorted after the rest).
 */
export function buildNeedsAttention(input: {
  roster: { uid: string; name: string }[]
  /** uid → the student's mastery signals. */
  mastery: Map<string, MasterySignals>
  live: AttentionLiveSession[]
  alerts: AttentionAlert[]
  reviews: AttentionReview[]
  /** Per-class session history; inactivity is only flagged once it has loaded. */
  sessionSummary: Map<string, AttentionSessionSummary> | undefined
  work: AttentionWork[]
  standardLabelById: Map<string, string>
  today: string
  now?: number
}): AttentionItem[] {
  const now = input.now ?? Date.now()
  const pctOf = (v: number) => `${Math.round(v * 100)}%`
  const items: AttentionItem[] = []

  for (const s of input.roster) {
    const reasons: AttentionReason[] = []
    let score = 0
    let detail: string | null = null
    let action: AttentionAction = { kind: 'student' }
    let actionLabel = 'View student'
    let actionWeight = 0
    const take = (weight: number, a: AttentionAction, label: string) => {
      score += weight
      if (weight > actionWeight) {
        actionWeight = weight
        action = a
        actionLabel = label
      }
    }

    const liveEscalated = input.live.find((l) => l.studentUid === s.uid && l.escalated)
    if (liveEscalated) {
      reasons.push({ kind: 'escalated_live', tone: 'error', text: 'Escalated · live', refs: { threadIds: [liveEscalated.threadId] } })
      detail = liveEscalated.currentTopic || null
      take(100, { kind: 'session', threadId: liveEscalated.threadId, live: true }, 'Open session')
    }

    // Rapid progress is an alert but good news: list it as a low-priority "ready for more"
    // nudge, never as a problem that outranks students who are struggling.
    const mine = input.alerts.filter((a) => a.studentUid === s.uid)
    const problems = mine.filter((a) => a.type !== 'RAPID_PROGRESSION')
    const rapid = mine.filter((a) => a.type === 'RAPID_PROGRESSION')
    if (problems.length && !liveEscalated) {
      const first = problems[0]
      const label = ANOMALY_LABEL[first.type as AnomalyType] ?? 'Open alert'
      reasons.push({
        kind: 'alert',
        tone: 'error',
        text: problems.length > 1 ? `${label} +${problems.length - 1}` : label,
        refs: { alertIds: problems.map((a) => a.id), threadIds: problems.map((a) => a.threadId), alertTypes: problems.map((a) => a.type as AnomalyType) },
      })
      take(80, { kind: 'session', threadId: first.threadId, live: false }, 'Open session')
    }
    if (rapid.length) {
      reasons.push({
        kind: 'rapid_progress',
        tone: 'success',
        text: 'Rapid progress · ready for more',
        refs: { alertIds: rapid.map((a) => a.id), threadIds: rapid.map((a) => a.threadId), alertTypes: rapid.map(() => 'RAPID_PROGRESSION' as const) },
      })
      take(10, { kind: 'session', threadId: rapid[0].threadId, live: false }, 'Open session')
    }

    const myWork = input.work.filter((w) => w.studentUid === s.uid)
    const overdue = myWork.filter((w) => w.kind === 'makeup' && w.status !== 'COMPLETED' && w.dueDate && w.dueDate.slice(0, 10) < input.today)
    if (overdue.length) {
      reasons.push({
        kind: 'makeup_overdue',
        tone: 'info',
        text: overdue.length > 1 ? `${overdue.length} make-ups overdue` : 'Make-up overdue',
        refs: { lessonPlanIds: overdue.map((w) => w.lessonPlanId!).filter(Boolean) },
      })
      detail ??= overdue[0].title
      take(50, { kind: 'student' }, 'View student')
    }

    // Overdue focus practice no longer keeps the student in hand, so say so instead of
    // letting it drop out of sight; Beacon can move its due date (extend_practice).
    const practiceOverdue = myWork.filter((w) => w.kind === 'practice' && w.status !== 'COMPLETED' && w.dueDate && w.dueDate.slice(0, 10) < input.today)
    if (practiceOverdue.length) {
      reasons.push({
        kind: 'practice_overdue',
        tone: 'info',
        text: practiceOverdue.length > 1 ? `${practiceOverdue.length} practices overdue` : 'Practice overdue',
        refs: { standardIds: practiceOverdue.flatMap((w) => w.standardIds) },
      })
      detail ??= `Practice: ${practiceOverdue[0].title}`
      take(40, { kind: 'student' }, 'View student')
    }

    const reviews = input.reviews.filter((r) => r.studentUid === s.uid)
    if (reviews.length) {
      reasons.push({
        kind: 'review_pending',
        tone: 'secondary',
        text: reviews.length > 1 ? `${reviews.length} reviews pending` : 'Review pending',
        refs: { reviewEventIds: reviews.map((r) => r.eventId) },
      })
      take(45, { kind: 'reviews' }, 'Review')
    }

    const m = input.mastery.get(s.uid)
    if (m && m.declining.length > 0) {
      reasons.push({
        kind: 'mastery_declining',
        tone: 'warning',
        text: m.declining.length > 1 ? `Declining on ${m.declining.length} skills` : 'Mastery declining',
        refs: { standardIds: m.declining },
      })
      score += 30 + m.declining.length * 2
    }
    if (m && m.below.length >= 2) {
      // Weakest first, so a planner acting on the first standard picks the biggest gap.
      const below = [...m.below].sort((a, b) => (m.scores.get(a) ?? 0) - (m.scores.get(b) ?? 0))
      reasons.push({
        kind: 'below_cutoff',
        tone: 'warning',
        text: `Below ${Math.round(MASTERY_BANDS.developing * 100)}% on ${m.below.length} skills`,
        refs: { standardIds: below },
      })
      score += 25 + m.below.length
      if (!detail) {
        const weakest = [...m.scores.entries()].sort((a, b) => a[1] - b[1]).slice(0, 2)
        detail = weakest.map(([id, v]) => `${input.standardLabelById.get(id) ?? 'Skill'} ${pctOf(v)}`).join(', ')
      }
    }

    // Only flag inactivity once the summary has loaded; it's per-class session history.
    if (input.sessionSummary) {
      const summary = input.sessionSummary.get(s.uid)
      const isLive = input.live.some((l) => l.studentUid === s.uid)
      if (!isLive) {
        if (!summary?.lastStartedAt) {
          reasons.push({ kind: 'inactive', tone: 'default', text: 'No sessions yet', refs: {} })
          score += 15
        } else {
          const d = daysBetween(summary.lastStartedAt, now)
          if (d >= ATTENTION_STALE_DAYS) {
            reasons.push({ kind: 'inactive', tone: 'default', text: `No session ${d} days`, refs: {} })
            score += 15 + Math.min(d, 30) / 3
          }
        }
      }
    }

    for (const r of reasons) {
      const w = workCovering(r, myWork, m, input.today, now)
      if (w) r.inHand = workLabel(w)
    }

    if (reasons.length) {
      items.push({ uid: s.uid, name: s.name, score, reasons, detail, action, actionLabel, inHand: reasons.every((r) => !!r.inHand) })
    }
  }

  // A class-wide lull (a break, a unit with no AI practice) isn't a per-student signal:
  // when most of the class would be flagged as inactive, drop that reason for everyone.
  const isStale = (r: AttentionReason) => r.kind === 'inactive'
  const staleCount = items.filter((i) => i.reasons.some(isStale)).length
  const result =
    input.roster.length > 0 && staleCount / input.roster.length >= 0.5
      ? items
          .map((i) => {
            const reasons = i.reasons.filter((r) => !isStale(r))
            return { ...i, reasons, inHand: reasons.length > 0 && reasons.every((r) => !!r.inHand) }
          })
          .filter((i) => i.reasons.length > 0)
      : items

  return result.sort((a, b) => Number(a.inHand) - Number(b.inHand) || b.score - a.score || a.name.localeCompare(b.name))
}

// ─── "Clear with Beacon" plans (epic #575) ────────────────────────────────────
// `classOverview.proposePlan` turns a class's Needs attention list into steps grouped by
// action. Every step is an ordinary stored assistant action (approve → job → undo); the
// teacher ticks groups / steps and runs them with `classOverview.executePlan`.

export type BeaconGroupKind = 'grade' | 'makeup' | 'focus' | 'starter' | 'extend' | 'rapid' | 'ack'

/** A Needs attention reason a step takes care of. */
export type BeaconStepCover = { uid: string; kind: AttentionReasonKind }

export interface BeaconPlanStep {
  actionId: string
  type: AssistantActionType
  studentUid: string
  studentName: string
  /** One line: what the step does or why ("Add & subtract integers 31%"). */
  what: string
  /** Ticked when the dialog opens (unsure grade drafts and enrichment start unticked). */
  defaultOn: boolean
  covers: BeaconStepCover[]
  /** grade_review only: Beacon's draft next to the tutor's score. */
  review?: { question: string; answer: string; standard: string; tutorScore: number; score: number; rationale: string; sure: boolean }
}

export interface BeaconPlanGroup {
  id: string
  kind: BeaconGroupKind
  title: string
  why: string
  /** Short label for the group's pill ("Reviews", "Make-up", "Practice"…). */
  tag: string
  /** Groups that set a due date: the date the steps carry, and the class meetings the teacher can pick instead. */
  dueDate?: string | null
  dueOptions?: string[]
  note?: string
  steps: BeaconPlanStep[]
}

export interface BeaconNeedsYou {
  uid: string
  name: string
  what: string
  link: { label: string; kind: 'live' | 'session' | 'student'; threadId?: string }
}

export interface BeaconPlan {
  classId: string
  createdAt: string
  today: string
  /** The list the plan was built from (same model as the card). */
  items: AttentionItem[]
  groups: BeaconPlanGroup[]
  needsYou: BeaconNeedsYou[]
}

/**
 * What the Needs attention list looks like once `active` steps have run: a student is
 * cleared when every reason is covered (alerts, reviews, overdue work), in hand when what's
 * left is mastery reasons covered by work, and still listed otherwise. A reason that
 * several steps share (two reviews) needs all of them. Shared by the plan dialog's
 * live preview and its summary.
 */
export function projectBeaconPlan(
  items: AttentionItem[],
  steps: BeaconPlanStep[],
  active: (step: BeaconPlanStep) => boolean,
): { cleared: string[]; inHand: string[]; left: AttentionItem[] } {
  const covered = new Set<string>()
  const blocked = new Set<string>()
  for (const s of steps) for (const c of s.covers) (active(s) ? covered : blocked).add(`${c.uid}:${c.kind}`)
  const out = { cleared: [] as string[], inHand: [] as string[], left: [] as AttentionItem[] }
  for (const item of items) {
    const open = item.reasons.filter((r) => {
      const key = `${item.uid}:${r.kind}`
      return !r.inHand && (!covered.has(key) || blocked.has(key))
    })
    if (open.length === 0) {
      const masteryLeft = item.reasons.some((r) => MASTERY_REASON_KINDS.includes(r.kind))
      ;(masteryLeft ? out.inHand : out.cleared).push(item.uid)
    } else out.left.push({ ...item, reasons: open })
  }
  return out
}

// ─── Student records (epic #595) ──────────────────────────────────────────────
// A student's profile, supports, demographics and guardians, plus the audit trail, shaped
// so a Phase 6 SIS sync can own the same rows. Mirrors dataconnect/schema/schema.gql
// (§ Student records). Every read/write goes through the agent `roster.*` routes.

/** Who last wrote a student-record row. MANUAL and CSV are ed360's own; the rest are SIS feeds. */
export type RecordSource = 'MANUAL' | 'CSV' | 'ONEROSTER' | 'CLEVER' | 'CLASSLINK'
export type FrlStatus = 'FREE' | 'REDUCED' | 'PAID' | 'OTHER'
export type GuardianRelationship =
  | 'MOTHER' | 'FATHER' | 'PARENT' | 'STEPPARENT' | 'GRANDPARENT' | 'LEGAL_GUARDIAN'
  | 'FOSTER_PARENT' | 'RELATIVE' | 'SIBLING' | 'OTHER'
export type GuardianContactType = 'PRIMARY' | 'SECONDARY' | 'EMERGENCY'
export type StudentRecordEntity = 'PROFILE' | 'SUPPORT' | 'DEMOGRAPHICS' | 'GUARDIAN' | 'STUDENT_GUARDIAN'
export type StudentRecordAction = 'CREATE' | 'UPDATE' | 'DELETE' | 'VIEW_DEMOGRAPHICS' | 'IMPORT'

export const RECORD_SOURCES: readonly RecordSource[] = ['MANUAL', 'CSV', 'ONEROSTER', 'CLEVER', 'CLASSLINK']
export const GUARDIAN_RELATIONSHIPS: readonly GuardianRelationship[] = [
  'MOTHER', 'FATHER', 'PARENT', 'STEPPARENT', 'GRANDPARENT', 'LEGAL_GUARDIAN', 'FOSTER_PARENT', 'RELATIVE', 'SIBLING', 'OTHER',
]

/** A SIS feed (sync re-writes SIS-owned fields; the UI locks them). MANUAL / CSV are not. */
export const isExternalSource = (source: RecordSource): boolean => source !== 'MANUAL' && source !== 'CSV'

export const RECORD_SOURCE_LABELS: Record<RecordSource, string> = {
  MANUAL: 'Entered in ed360', CSV: 'ed360 CSV import', ONEROSTER: 'OneRoster', CLEVER: 'Clever', CLASSLINK: 'ClassLink',
}
export const GUARDIAN_RELATIONSHIP_LABELS: Record<GuardianRelationship, string> = {
  MOTHER: 'Mother', FATHER: 'Father', PARENT: 'Parent', STEPPARENT: 'Stepparent', GRANDPARENT: 'Grandparent',
  LEGAL_GUARDIAN: 'Legal guardian', FOSTER_PARENT: 'Foster parent', RELATIVE: 'Relative', SIBLING: 'Sibling', OTHER: 'Other',
}
export const GUARDIAN_CONTACT_TYPE_LABELS: Record<GuardianContactType, string> = {
  PRIMARY: 'Primary', SECONDARY: 'Secondary', EMERGENCY: 'Emergency',
}
export const FRL_STATUS_LABELS: Record<FrlStatus, string> = { FREE: 'Free', REDUCED: 'Reduced', PAID: 'Paid', OTHER: 'Other' }

/** The provenance block every student-record table carries. Timestamps are ISO strings. */
export interface RecordProvenance {
  source: RecordSource
  sourcedId: string | null
  sourceUpdatedAt: string | null
  syncedAt: string | null
  updatedAt: string
  updatedByUid: string | null
}

export interface StudentProfileRow extends RecordProvenance {
  studentUid: string
  givenName: string | null
  familyName: string | null
  middleName: string | null
  preferredName: string | null
  pronouns: string | null
  studentNumber: string | null
  stateId: string | null
  gradeLevel: string | null
  /** ISO date (YYYY-MM-DD). */
  birthDate: string | null
  /** Storage object path (see `studentPhotoPath`); never SIS-owned. */
  photoPath: string | null
  photoUpdatedAt: string | null
}

export interface StudentSupportRow extends RecordProvenance {
  studentUid: string
  /** Tri-state flags: null = unknown. */
  iepStatus: boolean | null
  section504Status: boolean | null
  ellStatus: boolean | null
  giftedStatus: boolean | null
  homeLanguageCode: string | null
  homeLanguage: string | null
  tutorAccommodations: string | null
  notes: string | null
}

export interface StudentDemographicsRow extends RecordProvenance {
  studentUid: string
  sex: string | null
  americanIndianOrAlaskaNative: boolean | null
  asian: boolean | null
  blackOrAfricanAmerican: boolean | null
  nativeHawaiianOrOtherPacificIslander: boolean | null
  white: boolean | null
  demographicRaceTwoOrMoreRaces: boolean | null
  hispanicOrLatinoEthnicity: boolean | null
  countryOfBirthCode: string | null
  stateOfBirthAbbreviation: string | null
  cityOfBirth: string | null
  publicSchoolResidenceStatus: string | null
  frlStatus: FrlStatus | null
}

export interface GuardianRow extends RecordProvenance {
  id: string
  schoolId: string
  givenName: string
  familyName: string
  email: string | null
  /** E.164. */
  phone: string | null
  /** E.164; the number that may receive texts. */
  sms: string | null
  preferredLanguageCode: string | null
  userUid: string | null
}

export interface StudentGuardianRow extends RecordProvenance {
  studentUid: string
  guardianId: string
  relationship: GuardianRelationship | null
  relationshipLabel: string | null
  contactType: GuardianContactType
  contactPriority: number
  isPrimaryContact: boolean
  livesWith: boolean | null
  smsConsent: boolean
  smsConsentAt: string | null
  smsConsentSource: string | null
  canReceiveCalls: boolean
  emailOk: boolean
}

/** A guardian as the apps see it: the person plus this student's link to them. */
export interface StudentGuardianView {
  guardian: GuardianRow
  link: StudentGuardianRow
}

export type StudentRecordDiff = Record<string, { from: unknown; to: unknown }>

export interface StudentRecordChangeRow {
  id: string
  studentUid: string
  actorUid: string
  at: string
  entity: StudentRecordEntity
  entityId: string
  action: StudentRecordAction
  diff: StudentRecordDiff | null
  source: RecordSource
  importId: string | null
}

// ─── Field ownership ──────────────────────────────────────────────────────────
// Declared once. When a row's `source` is external, these fields lock in the UI and the
// agent rejects them for non-ED360 actors (an ED360 override lands, is audited and keeps
// `source`; the next sync re-writes it). Everything else is ed360-owned and always editable.
// The import reconciler overwrites SIS-owned fields and fills ed360-owned fields only when
// they are null.
export const SIS_OWNED_FIELDS = {
  StudentProfile: ['givenName', 'familyName', 'middleName', 'studentNumber', 'stateId', 'gradeLevel', 'birthDate'],
  StudentSupport: ['iepStatus', 'section504Status', 'ellStatus', 'giftedStatus', 'homeLanguageCode', 'homeLanguage'],
  StudentDemographics: [
    'sex', 'americanIndianOrAlaskaNative', 'asian', 'blackOrAfricanAmerican', 'nativeHawaiianOrOtherPacificIslander',
    'white', 'demographicRaceTwoOrMoreRaces', 'hispanicOrLatinoEthnicity', 'countryOfBirthCode',
    'stateOfBirthAbbreviation', 'cityOfBirth', 'publicSchoolResidenceStatus', 'frlStatus',
  ],
  Guardian: ['givenName', 'familyName', 'email', 'phone', 'sms', 'preferredLanguageCode'],
  StudentGuardian: ['relationship', 'relationshipLabel'],
} as const
export type SisOwnedEntity = keyof typeof SIS_OWNED_FIELDS

export function isSisOwned(entity: SisOwnedEntity, field: string): boolean {
  return (SIS_OWNED_FIELDS[entity] as readonly string[]).includes(field)
}

/** Fields of `patch` the SIS owns — the ones a non-ED360 actor may not change on an external row. */
export function sisOwnedFieldsIn(entity: SisOwnedEntity, patch: Record<string, unknown>): string[] {
  return Object.keys(patch).filter((f) => isSisOwned(entity, f))
}

/**
 * How a student is named everywhere staff see them: preferred name first, then the profile's
 * legal name, then the auth display name, then the email. Pass `{ user }` alone for a student
 * with no profile row yet.
 */
export function studentDisplayName(input: {
  profile?: Pick<StudentProfileRow, 'givenName' | 'familyName' | 'preferredName'> | null
  user: { displayName?: string | null; email?: string | null }
}): string {
  const p = input.profile
  const family = p?.familyName?.trim() ?? ''
  const first = p?.preferredName?.trim() || p?.givenName?.trim() || ''
  const fromProfile = [first, family].filter(Boolean).join(' ')
  return fromProfile || input.user.displayName?.trim() || input.user.email || 'Student'
}

/** The legal name from the profile ("Sofia Nguyen"), or null when the profile has no name. */
export function studentLegalName(profile: Pick<StudentProfileRow, 'givenName' | 'familyName'> | null | undefined): string | null {
  const s = [profile?.givenName?.trim(), profile?.familyName?.trim()].filter(Boolean).join(' ')
  return s || null
}

// ─── Photos ───────────────────────────────────────────────────────────────────
/** Square edge the browser resizes a student photo to before upload. */
export const STUDENT_PHOTO_SIZE = 512
/** Upper bound enforced by storage.rules for `studentPhotos/**`. */
export const STUDENT_PHOTO_MAX_BYTES = 1024 * 1024
export const STUDENT_PHOTO_PREFIX = 'studentPhotos'
export function studentPhotoPath(schoolId: string, studentUid: string, photoId: string): string {
  return `${STUDENT_PHOTO_PREFIX}/${schoolId}/${studentUid}/${photoId}.jpg`
}
/** Parses a photo path back into its parts, or null when it is not under the expected prefix. */
export function parseStudentPhotoPath(path: string): { schoolId: string; studentUid: string; photoId: string } | null {
  const m = /^studentPhotos\/([^/]+)\/([^/]+)\/([^/]+)\.jpg$/.exec(path)
  return m ? { schoolId: m[1], studentUid: m[2], photoId: m[3] } : null
}

// ─── Patch schemas (shared by the agent routes and the forms) ─────────────────
const nullableStr = (max: number) => z.string().trim().max(max).nullable()
const triState = z.boolean().nullable()
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD')

export const studentProfilePatchSchema = z.object({
  givenName: nullableStr(100),
  familyName: nullableStr(100),
  middleName: nullableStr(100),
  preferredName: nullableStr(100),
  pronouns: nullableStr(40),
  studentNumber: nullableStr(64),
  stateId: nullableStr(64),
  gradeLevel: nullableStr(16),
  birthDate: isoDate.nullable(),
}).partial().strict()
export type StudentProfilePatch = z.infer<typeof studentProfilePatchSchema>

export const studentSupportPatchSchema = z.object({
  iepStatus: triState,
  section504Status: triState,
  ellStatus: triState,
  giftedStatus: triState,
  homeLanguageCode: nullableStr(8),
  homeLanguage: nullableStr(80),
  tutorAccommodations: nullableStr(2000),
  notes: nullableStr(4000),
}).partial().strict()
export type StudentSupportPatch = z.infer<typeof studentSupportPatchSchema>

export const studentDemographicsPatchSchema = z.object({
  sex: nullableStr(40),
  americanIndianOrAlaskaNative: triState,
  asian: triState,
  blackOrAfricanAmerican: triState,
  nativeHawaiianOrOtherPacificIslander: triState,
  white: triState,
  demographicRaceTwoOrMoreRaces: triState,
  hispanicOrLatinoEthnicity: triState,
  countryOfBirthCode: nullableStr(8),
  stateOfBirthAbbreviation: nullableStr(8),
  cityOfBirth: nullableStr(120),
  publicSchoolResidenceStatus: nullableStr(40),
  frlStatus: z.enum(['FREE', 'REDUCED', 'PAID', 'OTHER']).nullable(),
}).partial().strict()
export type StudentDemographicsPatch = z.infer<typeof studentDemographicsPatchSchema>

/** A guardian's own fields (phone / sms are normalised to E.164 by the agent). */
export const guardianInputSchema = z.object({
  givenName: z.string().trim().min(1).max(100),
  familyName: z.string().trim().min(1).max(100),
  email: z.string().trim().email().max(254).nullable().optional(),
  phone: z.string().trim().max(40).nullable().optional(),
  sms: z.string().trim().max(40).nullable().optional(),
  preferredLanguageCode: nullableStr(8).optional(),
})
export type GuardianInput = z.infer<typeof guardianInputSchema>

/** The student ↔ guardian link's own fields. */
export const studentGuardianLinkSchema = z.object({
  relationship: z.enum(GUARDIAN_RELATIONSHIPS as [GuardianRelationship, ...GuardianRelationship[]]).nullable(),
  relationshipLabel: nullableStr(80),
  contactType: z.enum(['PRIMARY', 'SECONDARY', 'EMERGENCY']),
  contactPriority: z.number().int().min(1).max(20),
  isPrimaryContact: z.boolean(),
  livesWith: triState,
  smsConsent: z.boolean(),
  canReceiveCalls: z.boolean(),
  emailOk: z.boolean(),
}).partial().strict()
export type StudentGuardianLinkPatch = z.infer<typeof studentGuardianLinkSchema>

// ─── Import (enrich-only; normalized record → reconciler → apply) ─────────────
// Every adapter (ed360 CSV, OneRoster 1.2 CSV, later Clever / ClassLink / OneRoster REST)
// produces these records; one pure reconciler turns them into a plan; the plan is binding.

export interface NormalizedGuardianRecord {
  sourcedId: string | null
  givenName: string
  familyName: string
  email: string | null
  phone: string | null
  sms: string | null
  preferredLanguageCode: string | null
  relationship: GuardianRelationship | null
  relationshipLabel: string | null
  contactType: GuardianContactType | null
  contactPriority: number | null
  isPrimaryContact: boolean | null
  livesWith: boolean | null
}

export interface NormalizedStudentRecord {
  /** Which input row produced this record (for the preview / errors). */
  sourceRow: number
  source: RecordSource
  sourcedId: string | null
  sourceUpdatedAt: string | null
  /** Match keys, tried in this order. */
  email: string | null
  studentNumber: string | null
  profile: Partial<Omit<StudentProfileRow, keyof RecordProvenance | 'studentUid' | 'photoPath' | 'photoUpdatedAt'>>
  support: Partial<Omit<StudentSupportRow, keyof RecordProvenance | 'studentUid'>>
  demographics: Partial<Omit<StudentDemographicsRow, keyof RecordProvenance | 'studentUid'>> | null
  guardians: NormalizedGuardianRecord[]
  /** Values the adapter had to drop (an invalid guardian phone, an unknown grade code). */
  warnings?: string[]
}

export type RosterImportRowAction = 'create' | 'update' | 'unchanged' | 'unmatched' | 'error'
export type RosterImportMatchedBy = 'sourcedId' | 'studentNumber' | 'email' | null

export type RosterImportFormat = 'ed360-csv' | 'oneroster-1.2-csv'
export const ROSTER_IMPORT_FORMATS: readonly RosterImportFormat[] = ['ed360-csv', 'oneroster-1.2-csv']

/** One guardian operation inside a plan row (what `apply` runs). */
export interface RosterImportGuardianOp {
  action: 'create' | 'update' | 'link' | 'unchanged'
  /** "Elena Martinez" — for the preview. */
  label: string
  /** Existing Guardian id when found (by sourcedId → email → phone); null when created. */
  guardianId: string | null
  /** "sid:…" / "email:…" / "phone:…" — lets apply reuse a guardian created earlier in the same run (siblings). */
  dedupeKey: string | null
  /** Person fields to write (create: all; update: the changed ones). */
  person: Record<string, unknown>
  /** Link fields to upsert for (student, guardian). */
  link: Record<string, unknown>
}

export interface RosterImportPlanRow {
  sourceRow: number
  action: RosterImportRowAction
  /** The matched year student (null when unmatched / error). */
  studentUid: string | null
  /** The matched student's name on file (so the preview can show "file name → student"). */
  studentName: string | null
  matchedBy: RosterImportMatchedBy
  /** Display only — the file row's name or match key, for the preview grid. */
  label: string
  /** What would change, per entity, as diffs; empty when unchanged. */
  changes: Partial<Record<StudentRecordEntity, StudentRecordDiff>>
  /** Guardians to create / merge / link (apply runs these in order). */
  guardians: RosterImportGuardianOp[]
  /** Provenance columns to stamp per entity when its diff is applied (source, sourcedId, sourceUpdatedAt, syncedAt). */
  provenance: Partial<Record<StudentRecordEntity, Record<string, unknown>>>
  /** Things the import could not do on this row (a locked field on a synced row, a dropped value). */
  warnings: string[]
  /** Machine-readable error code (never free text with PII — the job doc republishes it). */
  errorCode: string | null
  errorDetail: string | null
}

export interface RosterImportPlan {
  importId: string
  schoolYearId: string
  schoolId: string
  source: RecordSource
  format: RosterImportFormat
  createdAt: string
  createdByUid: string
  counts: Record<RosterImportRowAction, number>
  rows: RosterImportPlanRow[]
}

/** What `roster.importDryRun` returns to the admin: the plan minus the write payloads, capped per action. */
export interface RosterImportPreview {
  importId: string
  schoolYearId: string
  format: RosterImportFormat
  source: RecordSource
  counts: Record<RosterImportRowAction, number>
  rows: Array<Omit<RosterImportPlanRow, 'guardians'> & { guardians: Array<Pick<RosterImportGuardianOp, 'action' | 'label' | 'guardianId'>> }>
  /** True when more rows exist than were returned. */
  truncated: boolean
}

export type RosterImportStatus = 'planned' | 'queued' | 'running' | 'done' | 'failed'

/** The import's record (Firestore `rosterImports/{importId}`, admin-only). */
export interface RosterImportMeta {
  importId: string
  schoolYearId: string
  schoolId: string
  format: RosterImportFormat
  source: RecordSource
  status: RosterImportStatus
  createdByUid: string
  createdAt: string
  committedAt: string | null
  finishedAt: string | null
  counts: Record<RosterImportRowAction, number>
  applied: number
  failed: number
}

/** The `ingestion_jobs/roster-import-{importId}` doc: counts and codes only — the collection is readable by every signed-in user, so no PII. */
export interface RosterImportProgress {
  jobType: 'roster_import'
  importId: string
  schoolYearId: string
  status: 'processing' | 'completed' | 'failed'
  /** 0–100. */
  progress: number
  total: number
  applied: number
  failed: number
  errorCodes: Record<string, number>
  startedAt: string | null
  finishedAt: string | null
}

/** The ed360 student CSV template, in column order. Supports come only from this template (not OneRoster core). */
export const ED360_STUDENT_CSV_COLUMNS = [
  'email', 'student_number', 'state_id', 'sis_id',
  'given_name', 'family_name', 'middle_name', 'preferred_name', 'pronouns', 'grade_level', 'birth_date',
  'iep', 'section_504', 'ell', 'gifted', 'home_language_code', 'home_language', 'tutor_accommodations',
  'guardian1_given_name', 'guardian1_family_name', 'guardian1_relationship', 'guardian1_email', 'guardian1_phone', 'guardian1_sms', 'guardian1_language', 'guardian1_primary',
  'guardian2_given_name', 'guardian2_family_name', 'guardian2_relationship', 'guardian2_email', 'guardian2_phone', 'guardian2_sms', 'guardian2_language', 'guardian2_primary',
] as const
export type Ed360StudentCsvColumn = (typeof ED360_STUDENT_CSV_COLUMNS)[number]

// ─── roster.* route shapes (epic #595), shared by the teacher and admin agent clients ──
export type RosterScope = { classId: string } | { schoolYearId: string }

export interface RosterGuardianCard {
  guardianId: string
  name: string
  relationship: GuardianRelationship | null
  relationshipLabel: string | null
  phone: string | null
  sms: string | null
  email: string | null
  smsConsent: boolean
  canReceiveCalls: boolean
  emailOk: boolean
  preferredLanguageCode: string | null
}

export interface RosterSupportFlags {
  iep: boolean | null
  section504: boolean | null
  ell: boolean | null
  gifted: boolean | null
  homeLanguage: string | null
  hasAccommodations: boolean
}

/** One Class Roster row from `roster.classSummary`. */
export interface RosterStudentRow {
  uid: string
  email: string
  displayName: string
  legalName: string | null
  preferredName: string | null
  pronouns: string | null
  photoPath: string | null
  studentNumber: string | null
  gradeLevel: string | null
  source: RecordSource | null
  supports: RosterSupportFlags | null
  primaryGuardian: RosterGuardianCard | null
  guardianCount: number
}

export interface StudentRecordAccess {
  level: 'staff' | 'teacher' | 'lead'
  schoolYearId: string
  schoolId: string
  classId: string | null
  canEditProfile: boolean
  canManageGuardians: boolean
  canViewDemographics: boolean
  canEditDemographics: boolean
  canViewHistory: boolean
}

/** `roster.getStudent`. `demographics` is present only when asked for and allowed. */
export interface StudentRecord {
  student: { uid: string; email: string; displayName: string; legalName: string | null; authDisplayName: string | null }
  profile: StudentProfileRow | null
  support: StudentSupportRow | null
  guardians: StudentGuardianView[]
  demographics?: StudentDemographicsRow | null
  access: StudentRecordAccess
}

export type StudentRecordChangeEntry = StudentRecordChangeRow & { actor: { displayName: string | null; email: string } | null }

/** The supports flags of a student's row, in the roster's shape. */
export function supportFlagsFromRow(s: StudentSupportRow | null | undefined): RosterSupportFlags | null {
  if (!s) return null
  return {
    iep: s.iepStatus, section504: s.section504Status, ell: s.ellStatus, gifted: s.giftedStatus,
    homeLanguage: s.homeLanguage, hasAccommodations: !!s.tutorAccommodations,
  }
}

// ─── Scratch pad: hand-drawn work (epic #312) ────────────────────────────────
// On a typed practice item the teacher marked `showWork`, the student can draw their steps
// on a pad. A vision model reads the drawing WITHOUT the problem or answer key (so it
// can't "fix" a mistake), the student confirms or corrects the reading, and the tutor gives
// feedback on the confirmed steps. The drawing never changes the score: mastery comes from
// the typed final answer only. Strokes are kept (replay), plus a PNG for the model.

/** Written by the vision model right after a symbol it can't read for sure (e.g. `6[?]`). */
export const UNCERTAIN_MARK = '[?]'

export type WorkInputType = 'pen' | 'touch' | 'mouse'

/** One stroke: points are `[x, y, pressure 0–1, t ms since the pad opened]`. An erased
 *  stroke keeps its points and gets `erasedAt`, so a replay can show the erase. */
export const workStrokeSchema = z.object({
  id:       z.string().min(1).max(40),
  input:    z.enum(['pen', 'touch', 'mouse']),
  width:    z.number().positive().max(40),
  points:   z.array(z.tuple([z.number(), z.number(), z.number().min(0).max(1), z.number().min(0)])).min(1).max(5000),
  erasedAt: z.number().min(0).optional(),
})
export const workStrokesSchema = z.object({
  v:       z.literal(1),
  width:   z.number().int().positive().max(4000),
  height:  z.number().int().positive().max(8000),
  strokes: z.array(workStrokeSchema).max(3000),
})
export type WorkStroke  = z.infer<typeof workStrokeSchema>
export type WorkStrokes = z.infer<typeof workStrokesSchema>

/** Strokes still on the pad (not erased). */
export function visibleStrokes(strokes: WorkStrokes): WorkStroke[] {
  return strokes.strokes.filter((s) => s.erasedAt === undefined)
}

/** Summary numbers the teacher's review shows (input type = what drew most points). */
export function workStrokeStats(strokes: WorkStrokes): { inputType: WorkInputType; drawMs: number; eraseCount: number; strokeCount: number } {
  const counts: Record<WorkInputType, number> = { pen: 0, touch: 0, mouse: 0 }
  let first = Infinity
  let last = 0
  for (const s of strokes.strokes) {
    counts[s.input] += s.points.length
    first = Math.min(first, s.points[0][3])
    last = Math.max(last, s.points[s.points.length - 1][3], s.erasedAt ?? 0)
  }
  const inputType = (Object.keys(counts) as WorkInputType[]).reduce((a, b) => (counts[b] > counts[a] ? b : a), 'pen')
  return {
    inputType,
    drawMs: Number.isFinite(first) ? Math.max(0, Math.round(last - first)) : 0,
    eraseCount: strokes.strokes.filter((s) => s.erasedAt !== undefined).length,
    strokeCount: visibleStrokes(strokes).length,
  }
}

/**
 * - `transcribed`: the model read it; waiting for the student to check the reading.
 * - `confirmed`:   the student confirmed (or fixed) the reading.
 * - `skipped`:     the student skipped the check; the tutor doesn't use the drawing.
 * - `pending`:     stored, not read yet (or the reading failed).
 */
export type WorkDrawingStatus = 'pending' | 'transcribed' | 'confirmed' | 'skipped'

/**
 * The model's reading: one LaTeX string per handwritten line, `[?]` marks kept. `circled` is
 * what the student circled or boxed (epic #632), already tidied by `tidyCircledReading`;
 * absent / null when nothing was marked.
 */
export interface WorkReading { lines: string[]; uncertain: boolean; model: string; circled?: string | null }

/**
 * Where the answer that gets graded came from (epic #632): read off the drawing (`circled`,
 * `last_line`), typed in the box (`typed`), or changed by the student while checking (`fixed`).
 */
export type WorkAnswerSource = 'circled' | 'last_line' | 'typed' | 'fixed'

/** The final answer read off a drawing: LaTeX without `[?]` marks. */
export interface WorkReadingAnswer {
  latex:     string
  source:    'circled' | 'last_line'
  /** A `[?]` was in it, or the circled text matches no line: make the student look before confirming. */
  uncertain: boolean
}

export interface WorkDrawing {
  drawingId:       string
  threadId:        string
  sessionId:       string
  classId:         string
  studentUid:      string
  lessonPlanId?:   string
  storagePath:     string   // PNG the model read
  strokesPath:     string   // WorkStrokes JSON (replay)
  blockSection:    'PRACTICE' | 'EXIT_TICKET'
  blockItemIndex:  number   // position within its section, like AssessmentEvent.blockItemIndex
  itemText:        string
  status:          WorkDrawingStatus
  transcription?:  WorkReading
  /** What the student confirmed, one LaTeX line each. Equals the reading when nothing was fixed. */
  confirmedLines?: string[]
  /** The final answer that is graded, as plain math: typed at submit, then settled by `work.confirm`. */
  finalAnswer?:    string
  /** Where `finalAnswer` came from, once confirmed (epic #632). */
  answerSource?:   WorkAnswerSource
  /** The answer read off the drawing (circled, else the last line), derived from `transcription`. */
  readingAnswer?:  WorkReadingAnswer
  inputType:       WorkInputType
  drawMs:          number
  eraseCount:      number
  strokeCount:     number
  createdAt:       string
  confirmedAt?:    string
}

/** The pad's state for the item the student is on, sent after every turn and on resume. */
export interface ShowWorkState {
  enabled:  boolean
  /** Which item (`PRACTICE:1`): a new key means a new problem, so the pad starts clean. */
  itemKey?: string
  /** A drawing waiting for the student to check its reading (the composer stays locked). */
  pending?: WorkDrawing
}

/** `finalAnswer` is optional (epic #632): empty = take it from the drawing at confirm. */
export interface WorkSubmitInput  { threadId: string; png: string; strokes: WorkStrokes; finalAnswer?: string }
export interface WorkSubmitOutput { drawing: WorkDrawing }
/** `lines` are plain math as the student typed them (`3x - 5 = 10`); the server converts them. */
/** `accept` = the reading is right as read; `lines` = the student's fix; `skip` = don't use the reading. */
/** `answer` (plain math) = the final answer as the student set it while checking; it wins over the reading's. */
export interface WorkConfirmInput  { threadId: string; drawingId: string; accept?: boolean; lines?: string[]; skip?: boolean; answer?: string }
/** `finalAnswer` = plain math to send as the student's message (`session.sendMessage({ message, drawingId })`). */
export interface WorkConfirmOutput { drawing: WorkDrawing; finalAnswer: string }
/** A drawing as the teacher reviews it (epic #312): what it was sent with and how that went. */
export interface WorkReviewItem {
  drawing:      WorkDrawing
  studentName?: string
  /** Went out with a typed answer (false = abandoned or still being checked). */
  sent:         boolean
  /** The typed answer's grade — the drawing never changes it. `right` = score ≥ 0.5 (a teacher override wins). */
  result?:      { score: number; right: boolean }
  /** The tutor's reply to that answer (session reviews only). */
  feedback?:    string
  /** Thumbnail data URL (gallery only: the newest try per student). */
  png?:         string
}
export interface WorkListSessionInput  { threadId: string }
export interface WorkListLessonInput   { classId: string; lessonPlanId: string }
export interface WorkListOutput        { items: WorkReviewItem[] }

/** One drawing with its files: the PNG as a data URL and the strokes (for replay). */
export interface WorkGetInput  { drawingId: string }
export interface WorkGetOutput { drawing: WorkDrawing; png: string; strokes: WorkStrokes }

/** The reading's lines: the model separates handwritten lines with `\\`. `(blank)` = nothing written. */
export function readingLines(latex: string): string[] {
  const t = latex.trim()
  if (!t || /^\(?blank\)?$/i.test(t) || /^\\text\{\(?blank\)?\}$/i.test(t)) return []
  return t.split(/\\\\/).map((l) => l.trim()).filter(Boolean)
}

export function hasUncertainMark(line: string): boolean {
  return line.includes(UNCERTAIN_MARK)
}

export function stripUncertainMarks(line: string): string {
  return line.split(UNCERTAIN_MARK).join('').replace(/\s{2,}/g, ' ').trim()
}

/** KaTeX source for a reading line: the symbol before each `[?]` is highlighted. */
export function uncertainToKatex(line: string): string {
  return line.replace(/(\\[a-zA-Z]+|\{[^{}]*\}|[^\s\\{}])\s*\[\?\]/g, (_m, sym: string) => `\\colorbox{#FFF3C4}{$${sym}$}`)
}

// Plain math ↔ LaTeX (S3 "Fix it" edits plain math, never LaTeX). Both directions are pure
// and shared: the student app previews with them, the agent converts on confirm.

const LATEX_SYMBOLS: Record<string, string> = {
  times: '×', cdot: '·', div: '÷', le: '≤', leq: '≤', leqslant: '≤', ge: '≥', geq: '≥', geqslant: '≥',
  neq: '≠', ne: '≠', pm: '±', mp: '∓', approx: '≈', to: '→', rightarrow: '→', Rightarrow: '⇒',
  pi: 'π', theta: 'θ', alpha: 'α', beta: 'β', infty: '∞', circ: '°', degree: '°', lt: '<', gt: '>',
}
const UNWRAP_COMMANDS = new Set(['text', 'mathrm', 'textrm', 'operatorname', 'mathbf', 'mathit', 'boxed', 'underline', 'overline', 'cancel'])

/** The brace group starting at `i` (which must be `{`): its inner text and the index after it. */
function braceGroup(s: string, i: number): { inner: string; end: number } {
  if (s[i] !== '{') {
    // A bare single-token argument: `\frac12`, `x^2`.
    const m = /^(\\[a-zA-Z]+|.)/.exec(s.slice(i))
    return { inner: m ? m[1] : '', end: i + (m ? m[1].length : 0) }
  }
  let depth = 0
  for (let j = i; j < s.length; j++) {
    if (s[j] === '{') depth++
    else if (s[j] === '}' && --depth === 0) return { inner: s.slice(i + 1, j), end: j + 1 }
  }
  return { inner: s.slice(i + 1), end: s.length }
}

const isSimpleAtom = (x: string) => /^[0-9A-Za-zα-ωπθ.]+$/u.test(x) || /^-?\d+(\.\d+)?$/.test(x)
const wrapAtom = (x: string) => (isSimpleAtom(x) ? x : `(${x})`)

/** A LaTeX reading line → the plain math a student would type (`\frac{5}{3}` → `5/3`). */
export function latexToPlainMath(tex: string): string {
  let out = ''
  let i = 0
  const s = tex.replace(/\\left|\\right/g, '').replace(/\\[,;:!]|\\quad|\\qquad|~/g, ' ').replace(/\\ /g, ' ')
  while (i < s.length) {
    const ch = s[i]
    if (ch === '\\') {
      const m = /^\\([a-zA-Z]+|.)/.exec(s.slice(i))!
      const cmd = m[1]
      i += m[0].length
      if (cmd === 'frac' || cmd === 'dfrac' || cmd === 'tfrac') {
        while (s[i] === ' ') i++
        const a = braceGroup(s, i); i = a.end
        while (s[i] === ' ') i++
        const b = braceGroup(s, i); i = b.end
        out += `${wrapAtom(latexToPlainMath(a.inner))}/${wrapAtom(latexToPlainMath(b.inner))}`
      } else if (cmd === 'sqrt') {
        let index = ''
        if (s[i] === '[') { const close = s.indexOf(']', i); index = s.slice(i + 1, close); i = close + 1 }
        const a = braceGroup(s, i); i = a.end
        out += `${index}√${wrapAtom(latexToPlainMath(a.inner))}`
      } else if (UNWRAP_COMMANDS.has(cmd)) {
        const a = braceGroup(s, i); i = a.end
        out += latexToPlainMath(a.inner)
      } else if (cmd in LATEX_SYMBOLS) {
        out += LATEX_SYMBOLS[cmd]
      } else if (/^[a-zA-Z]+$/.test(cmd)) {
        out += cmd // \sin, \log, an unknown command: its name
      } else {
        out += cmd // \%, \{, \$: the character
      }
    } else if (ch === '^' || ch === '_') {
      i++
      const a = braceGroup(s, i); i = a.end
      const inner = latexToPlainMath(a.inner)
      out += inner === '°' ? '°' : `${ch}${isSimpleAtom(inner) ? inner : `(${inner})`}`
    } else if (ch === '{' || ch === '}') {
      i++
    } else {
      out += ch
      i++
    }
  }
  return out.replace(/\s+/g, ' ').trim()
}

type PlainTok = { t: 'num' | 'id' | 'op' | 'lp' | 'rp' | 'sp' | 'sqrt' | 'pow' | 'sub' | 'slash' | 'unc'; v: string }

const PLAIN_OPS: [string, string][] = [
  ['<=', '\\le '], ['>=', '\\ge '], ['!=', '\\neq '], ['−', '-'], ['-', '-'], ['+', '+'], ['×', '\\times '], ['*', '\\times '],
  ['·', '\\cdot '], ['÷', '\\div '], ['=', '='], ['≠', '\\neq '], ['≤', '\\le '], ['≥', '\\ge '], ['<', '<'], ['>', '>'],
  ['±', '\\pm '], ['≈', '\\approx '], ['→', '\\to '], ['⇒', '\\Rightarrow '], ['%', '\\%'], ['°', '^{\\circ}'],
  [',', ','], [':', ':'], [';', ';'], ['!', '!'], ['|', '|'], ['∞', '\\infty '],
]
const PLAIN_LETTERS: Record<string, string> = { 'π': '\\pi ', 'θ': '\\theta ', 'α': '\\alpha ', 'β': '\\beta ' }
const PLAIN_FUNCTIONS = new Set(['sin', 'cos', 'tan', 'log', 'ln', 'exp'])
const SUPERSCRIPT_DIGITS: Record<string, string> = { '²': '2', '³': '3', '¹': '1', '⁰': '0', '⁴': '4', '⁵': '5', '⁶': '6', '⁷': '7', '⁸': '8', '⁹': '9' }

function tokenizePlain(src: string): PlainTok[] {
  const toks: PlainTok[] = []
  let i = 0
  const s = src.split(UNCERTAIN_MARK).join('⁇')
  outer: while (i < s.length) {
    const ch = s[i]
    if (ch === '⁇') { toks.push({ t: 'unc', v: UNCERTAIN_MARK }); i++; continue }
    if (/\s/.test(ch)) { toks.push({ t: 'sp', v: ' ' }); while (/\s/.test(s[i] ?? '')) i++; continue }
    const num = /^\d+(?:\.\d+)?/.exec(s.slice(i))
    if (num) { toks.push({ t: 'num', v: num[0] }); i += num[0].length; continue }
    if (ch in SUPERSCRIPT_DIGITS) { toks.push({ t: 'pow', v: '^' }, { t: 'num', v: SUPERSCRIPT_DIGITS[ch] }); i++; continue }
    if (ch in PLAIN_LETTERS) { toks.push({ t: 'id', v: PLAIN_LETTERS[ch] }); i++; continue }
    const word = /^[A-Za-z]+/.exec(s.slice(i))
    if (word) {
      const w = word[0]
      toks.push({ t: 'id', v: PLAIN_FUNCTIONS.has(w) ? `\\${w} ` : w.length >= 3 ? `\\text{${w}}` : w })
      i += w.length
      continue
    }
    if (ch === '(' || ch === '[' || ch === '{') { toks.push({ t: 'lp', v: ch }); i++; continue }
    if (ch === ')' || ch === ']' || ch === '}') { toks.push({ t: 'rp', v: ch }); i++; continue }
    if (ch === '√') { toks.push({ t: 'sqrt', v: ch }); i++; continue }
    if (ch === '^') { toks.push({ t: 'pow', v: ch }); i++; continue }
    if (ch === '_') { toks.push({ t: 'sub', v: ch }); i++; continue }
    if (ch === '/') { toks.push({ t: 'slash', v: ch }); i++; continue }
    for (const [p, tex] of PLAIN_OPS) {
      if (s.startsWith(p, i)) { toks.push({ t: 'op', v: tex }); i += p.length; continue outer }
    }
    toks.push({ t: 'op', v: ch === '\\' ? '\\backslash ' : ch === '$' ? '\\$' : ch === '#' ? '\\#' : ch === '&' ? '\\&' : ch })
    i++
  }
  return toks
}

/** Plain math as a student types it (`x = 5/3`, `√(x+1)`, `x^2`, `3x − 5 ≤ 10`) → LaTeX. */
export function plainMathToLatex(src: string): string {
  const toks = tokenizePlain(src)
  let p = 0
  const peek = () => toks[p]

  // A factor: a number, a letter run, a bracketed group, √factor; with ^ / _ postfixes.
  // `group` is the inner LaTeX when the factor is one bracketed group (its brackets drop
  // when it becomes a fraction part, an exponent or a root).
  const factor = (): { tex: string; group?: string } | null => {
    const t = peek()
    if (!t) return null
    let f: { tex: string; group?: string }
    if (t.t === 'num' || t.t === 'id') { p++; f = { tex: t.v } }
    else if (t.t === 'lp') {
      p++
      const inner = expr(true)
      const close = peek()?.t === 'rp' ? toks[p++].v : ''
      const open = t.v === '{' ? '\\{' : t.v
      const shut = close === '}' ? '\\}' : close
      f = { tex: `${open === '(' ? '\\left(' : open}${inner}${shut === ')' ? '\\right)' : shut}`, group: inner }
    } else if (t.t === 'sqrt') {
      p++
      const a = factor()
      f = { tex: `\\sqrt{${a ? a.group ?? a.tex : ''}}` }
    } else return null
    for (;;) {
      const n = peek()
      if (n?.t === 'pow' || n?.t === 'sub') {
        p++
        let sign = ''
        if (peek()?.t === 'op' && peek()!.v === '-') { sign = '-'; p++ }
        const a = factor()
        f = { tex: `${f.tex}${n.t === 'pow' ? '^' : '_'}{${sign}${a ? a.group ?? a.tex : ''}}` }
      } else if (n?.t === 'unc') {
        p++
        f = { tex: `${f.tex}${UNCERTAIN_MARK}` }
      } else break
    }
    return f
  }
  // A term: factors written next to each other (`3x`, `2(x+1)`), as one fraction part.
  const term = (): { tex: string; group?: string } | null => {
    const parts: { tex: string; group?: string }[] = []
    for (;;) {
      const t = peek()
      if (!t || !(t.t === 'num' || t.t === 'id' || t.t === 'lp' || t.t === 'sqrt')) break
      const f = factor()
      if (!f) break
      parts.push(f)
    }
    if (!parts.length) return null
    if (parts.length === 1) return parts[0]
    return { tex: parts.map((x) => x.tex).join('') }
  }
  const expr = (inGroup: boolean): string => {
    const items: { kind: 'term' | 'text'; tex: string; group?: string }[] = []
    while (p < toks.length) {
      const t = peek()!
      if (t.t === 'rp') { if (inGroup) break; items.push({ kind: 'text', tex: t.v === '}' ? '\\}' : t.v }); p++; continue }
      if (t.t === 'sp') { items.push({ kind: 'text', tex: ' ' }); p++; continue }
      if (t.t === 'op' || t.t === 'unc' || t.t === 'pow' || t.t === 'sub') { items.push({ kind: 'text', tex: t.v }); p++; continue }
      if (t.t === 'slash') {
        p++
        while (items.length && items[items.length - 1].tex === ' ') items.pop()
        const num = items[items.length - 1]?.kind === 'term' ? items.pop()! : null
        while (peek()?.t === 'sp') p++
        const den = term()
        const part = (x: { tex: string; group?: string } | null) => (x ? x.group ?? x.tex : '')
        items.push({ kind: 'term', tex: `\\frac{${part(num)}}{${part(den)}}` })
        continue
      }
      const tm = term()
      if (tm) items.push({ kind: 'term', ...tm })
      else { items.push({ kind: 'text', tex: t.v }); p++ }
    }
    return items.map((x) => x.tex).join('').replace(/ {2,}/g, ' ')
  }
  return expr(false).trim()
}

/** Compare a model line with a confirmed line, ignoring spacing and `[?]` marks. */
export function sameWorkLine(a: string, b: string): boolean {
  // Through plain math and back, so `\frac{5}{3}` and `5/3` typed as a fix compare equal.
  const norm = (x: string) => plainMathToLatex(latexToPlainMath(stripUncertainMarks(x))).replace(/\\left|\\right/g, '').replace(/\s+/g, '')
  return norm(a) === norm(b)
}

// ── The final answer from the drawing (epic #632) ──────────────────────────────────────
// A circle's tail often crosses the `=` beside what it marks, and the model reads `\neq`
// or `\in` there (5 of 48 real samples; a prompt rule didn't help). These rules undo it.

/** Relations a circle can turn `=` into, or `=` itself: the answer is what comes after them. */
const EQ_REL = /=|\\neq(?![a-zA-Z])|\\ne(?![a-zA-Z])|\\in(?![a-zA-Z])/g
/** Inequalities: the whole `x > 3` is the answer, not just `3`. */
const INEQ_REL = /<|>|≤|≥|\\(?:le|leq|ge|geq|lt|gt|leqslant|geqslant)(?![a-zA-Z])/

/** Text after the last top-level match of `re` (outside braces), trimmed; the whole text if none. */
function afterLastRelation(tex: string, re: RegExp): string {
  const global = new RegExp(re.source, 'g')
  let cut = -1
  let depth = 0
  const depthAt: number[] = []
  for (let i = 0; i < tex.length; i++) {
    depthAt[i] = depth
    if (tex[i] === '{') depth++
    else if (tex[i] === '}') depth = Math.max(0, depth - 1)
  }
  for (const m of tex.matchAll(global)) if (!depthAt[m.index ?? 0]) cut = (m.index ?? 0) + m[0].length
  return (cut < 0 ? tex : tex.slice(cut)).trim()
}

const ANY_REL = new RegExp(`${EQ_REL.source}|${INEQ_REL.source}`)
const answerKey = (tex: string) => stripUncertainMarks(tex).replace(/\\left|\\right|\s|\{|\}/g, '')
const escapeRe = (x: string) => x.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * Clean a reading with a circled part: drop a leading `=` / `\neq` / `\in` from the circled
 * text, and turn a `\neq` / `\in` just before the circled part of a line back into `=`.
 */
export function tidyCircledReading(lines: string[], circled: string | null | undefined): { lines: string[]; circled: string | null } {
  const c = (circled ?? '').replace(new RegExp(`^\\s*(?:${EQ_REL.source})\\s*`), '').trim()
  if (!c) return { lines, circled: null }
  const tail = afterLastRelation(c, EQ_REL)
  const before = new RegExp(`\\\\(?:neq|ne|in)(?![a-zA-Z])(\\s*)(?=${escapeRe(tail)})`)
  return { lines: tail ? lines.map((l) => l.replace(before, '=$1')) : lines, circled: c }
}

/** The answer part of a line: an inequality whole, else the part after its last `=`. */
function lineAnswer(line: string): string {
  return INEQ_REL.test(line) ? line.trim() : afterLastRelation(line, EQ_REL)
}

/**
 * The final answer on a drawing (epic #632): what the student circled or boxed, else the last
 * line — after its last `=`, or the whole line for an inequality. A circled number that is
 * the right side of an inequality line becomes that whole line (`3` → `x > 3`). A circled
 * answer that matches no line, or any `[?]`, makes it `uncertain`. Null on a blank reading.
 */
export function answerFromReading(lines: string[], circled: string | null | undefined): WorkReadingAnswer | null {
  const c = (circled ?? '').trim()
  if (c) {
    let latex = INEQ_REL.test(c) ? c : afterLastRelation(c, EQ_REL)
    const key = answerKey(latex)
    let matches = false
    for (const line of lines) {
      const whole = answerKey(line)
      const right = answerKey(afterLastRelation(line, ANY_REL))
      if (key === whole || key === right || key === answerKey(lineAnswer(line))) matches = true
      if (!ANY_REL.test(c) && INEQ_REL.test(line) && key === right) latex = stripUncertainMarks(line)
    }
    if (latex) {
      return { latex: stripUncertainMarks(latex), source: 'circled', uncertain: hasUncertainMark(c) || !matches }
    }
  }
  const last = lines[lines.length - 1]
  if (!last) return null
  const latex = lineAnswer(last)
  return latex ? { latex: stripUncertainMarks(latex), source: 'last_line', uncertain: hasUncertainMark(latex) } : null
}

/** Per line of a confirmed reading: what the model read there, and whether the student changed it. */
export function workReadingDiff(model: string[], confirmed: string[]): { line: string; modelLine: string | null; changed: boolean }[] {
  return confirmed.map((line, i) => {
    const modelLine = model[i] ?? null
    return { line, modelLine, changed: modelLine === null || !sameWorkLine(modelLine, line) }
  })
}
